import { applySiteStudyArea } from '../../../lib/siteStudyArea';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, Copy, Factory, FolderOpen, LayoutGrid, Loader2, Mail, Maximize2, Save, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { openConfirmDialog, type ConfirmDialogOptions } from '../../ui/confirmDialog';
import { useGenerationScreeningAdmissions } from './useGenerationScreeningAdmissions';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { SiteTabSectionHeader } from '../SiteTabSectionHeader';
import { GridLedStandbyPlanningWorkbench } from './GridLedStandbyPlanningWorkbench';
import { GenerationProjectInputsPanel } from './GenerationProjectInputsPanel';
import { useGenerationProjectInputSync } from './useGenerationProjectInputSync';
import { declaredSharedProjectPue, generationProjectInputHash, overlayIntentOnGenerationDraft } from '../../../lib/generationImpact/generationProjectInputSync';
import { intentDecisionStateHash } from '../../../lib/intent/undecidedProjectIntent';
import { isIntentFieldExplicitlyUnknown } from '../../../lib/intent/explicitUnknown';
import { LandScreeningBasis } from '../LandScreeningBasis';

import { callUserFunction, UserFunctionError } from '../../../lib/callUserFunction';
import {
  buildPlanningScenario,
  setCcsEnabledWithPlanningDefaults,
} from '../../../lib/generationImpact/planningCatalog';
import {
  buildGenerationParcelTestFit,
  DEFAULT_GENERATION_LAYOUT,
} from '../../../lib/generationImpact/generationLayoutGeometry';
import {
  buildGenerationAlternativesMatrix,
  type GenerationAlternativeRow,
} from '../../../lib/generationImpact/generationAlternatives';
import { sizeUniformGenerationBlocks } from '../../../lib/generationImpact/sizeUniformGenerationBlocks';
import { GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE, isGridImportOnlyScenario } from '../../../lib/generationImpact/generationSizingValidation';
import {
  deriveReliabilityPerspective,
  resolveCapacityScreenVerdict,
} from '../../../lib/generationImpact/screeningIntegrity';
import { solveGenerationImpact } from '../../../lib/generationImpact/solveGenerationImpact';
import {
  DEFAULT_PUE,
  formatPowerBandLayer,
  isValidPue,
  resolveIntentPowerStack,
  resolveIntentPowerStackBand,
  resolvePhasePowerStack,
  resolvePowerStack,
} from '../../../lib/powerStack';
import { effectivePhaseCapacity, effectivePhases } from '../../../lib/projectIntent';
import {
  selectGranularityLadderRows,
  sweepGenerationBlockGranularity,
} from '../../../lib/generationImpact/sweepGenerationBlockGranularity';
import {
  evaluateWorkbenchTestFitWorkflow,
  generationImpactWorkflowInternals,
} from '../../../lib/generationImpact/testFitWorkflow';
import {
  GENERATION_EQUIPMENT_CATALOG,
  applyGenerationEquipmentEnvelope,
  recordEquipmentRatingConfirmation,
  buildGenerationEquipmentScreen,
  projectGenerationEquipmentExhibitBasis,
} from '../../../lib/generationImpact/generationEquipmentCatalog';
import {
  applyGenerationUserEquipmentEnvelope,
  buildGenerationUserEquipmentScreen,
} from '../../../lib/generationImpact/userEquipmentEnvelope';
import {
  activeGenerationEquipmentSelection,
  resizeGenerationEquipmentFleet,
  MAX_GENERATION_BLOCK_COUNT,
  MIN_GENERATION_BLOCK_COUNT,
} from '../../../lib/generationImpact/equipmentSelectionState';
import { sizeGenerationEquipmentBlockCount } from '../../../lib/generationImpact/sizeGenerationEquipmentBlockCount';
import { GenerationScreeningSourceBadge } from './GenerationScreeningSourceBadge';
import { summarizeScreeningEvidenceRejections } from '../../../lib/generationImpact/screeningEvidenceNotices';
import { findResourceLimitDocumentCandidates } from '../../../lib/generationImpact/siteEvidenceSuggestions';
import {
  applyDataCenterLoad,
  dataCenterPresetsForIntent,
  DATA_CENTER_COOLING_WATER_DEFAULTS,
  deriveDataCenterCoolingWaterBasis,
  sizeDataCenter,
  type DataCenterCoolingType,
  recommendDataCenterCoolingType,
} from '../../../lib/generationImpact/dataCenterLibrary';
import { effectiveWorkloadProfile, type WorkloadProfile } from '../../../lib/workloadProfile';
import {
  resolveBomItemImport,
  summarizeBomEquipmentCoverage,
  type GenerationEquipmentImportResolution,
  type GenerationUserEquipmentPrefill,
} from '../../../lib/generationImpact/equipmentImportSources';
import { expectedGridFrequencyHzFromAddress } from '../../../lib/generationImpact/gridFrequency';
import type { FinderSource } from '../../../lib/generationImpact/equipmentFinderRanking';
import { GenerationEquipmentImportDialog } from './GenerationEquipmentImportDialog';
import {
  applyGenerationProjectDutyBasis,
  type GenerationProjectDutyBasisDraft,
} from '../../../lib/generationImpact/generationProjectDuty';
import { materializeSiteBoundaries } from '../../../lib/siteBoundaryGeometry';
import { downloadSiteLayersGeoJson } from '../../../services/geoJsonExporter';
import { downloadSiteLayersKml } from '../../../services/kmlExporter';
import type { SiteEntity } from '../../../types/site';
import type { DatacrateModel } from '../../../types/datacrateModel';
import type { SiteEvidenceDocument } from '../../../types/siteEvidence';
import type { ProjectIntent, ProjectPhase, SiteDocument } from '../../../types/siteRag';
import type { GenerationProjectInputChanges } from '../../../types/generationProjectInputSync';
import type {
  GenerationAssumption,
  GenerationEquipmentWorkspace,
  GenerationImpactLayoutWorkspaceState,
  GenerationImpactScenarioInput,
  GenerationImpactScenarioSnapshot,
  GenerationLayoutConfiguration,
  GenerationTechnology,
  GenerationUserEquipmentEntry,
  ListGenerationImpactScenariosResult,
  PublishGenerationImpactLayoutOutput,
} from '../../../types/generationImpact';
import { GenerationAlternativesMatrix } from './GenerationAlternativesMatrix';
import { GenerationMaxPowerPanel } from './GenerationMaxPowerPanel';
import { GenerationBomBasisBoard } from './GenerationBomBasisBoard';
import { evaluateGenerationBomBasisAlignment } from '../../../lib/generationImpact/generationBomBasis';
import { GenerationEquipmentEnvelopeBoard } from './GenerationEquipmentEnvelopeBoard';
import { GenerationSiteWaterEvidence } from './GenerationSiteWaterEvidence';
import { GenerationScenarioBasis } from './GenerationScenarioBasis';
import { resolvePromotedGenerationScenario } from '../../../lib/generationImpact/promotedScenario';
import { GenerationOemConfirmationDialog } from './GenerationOemConfirmationDialog';
import { GenerationAddEquipmentDialog } from './GenerationAddEquipmentDialog';
import { TestFitStudio } from './studio/TestFitStudio';
import { buildGenerationMaxPowerStudySnapshot } from '../../../lib/generationImpact/maxPowerStudy';
import { GenerationNoiseSourcesEditor } from './GenerationNoiseSourcesEditor';
import { buildNoiseField, deriveNoiseGeometryFromTestFit } from '../../../lib/generationImpact/noiseField';
import { rankAlternativeParcelFits } from '../../../lib/generationImpact/generationLayoutSearch';
import { GenerationProjectDutyBoard } from './GenerationProjectDutyBoard';
import {
  deriveGenerationEquipmentConstraintFits,
  deriveSiteEnvelopeLedger,
  deriveSiteLimitDocumentation,
  suggestGenerationEquipmentBasis,
  type SiteLimitDocumentationState,
} from '../../../lib/generationImpact/workflowGuidance';
import {
  buildUtilityDataRequestEmail,
  DEFAULT_NOMINATION_MARGIN_PCT,
  deriveUtilityAsk,
  type UtilityResourceKind,
} from '../../../lib/generationImpact/utilityDataRequestEmail';
import {
  GENERATION_WORKFLOW_STAGE_IDS,
  GenerationWorkflowNavigator,
  GenerationNextActionCard,
  type GenerationWorkflowNextAction,
  type GenerationWorkflowStageId,
  type GenerationWorkflowStages,
} from './GenerationWorkflowNavigator';
import { GenerationFieldHelp, GenerationFieldLabel } from './GenerationFieldHelp';
import {
  GenerationImpactLayoutPublishing,
  GenerationScenarioStatusBadges,
} from './GenerationImpactLayoutPublishing';
import { GenerationImpactTestFitWorkflow } from './GenerationImpactTestFitWorkflow';
import {
  generationScenarioInputMatches,
  newLayoutPublishRequestId,
  readLayoutWorkspace,
} from './layoutPublishUi';
import { isParcelStudioV2Enabled } from '../../../lib/runtimeFlags';
import { hashGenerationImpactValue } from '../../../lib/generationImpact/integrity';
import {
  clearScenarioDraft,
  readScenarioDraft,
  scenarioDraftKey,
  writeScenarioDraft,
} from './generationScenarioDraftMemory';
import {
  resolveGenerationScreeningPrefill,
  type GenerationScreeningPrefillField,
  type GenerationScreeningPrefillResolution,
} from '../../../lib/generationImpact/screeningEvidencePrefill';

interface Props {
  site: SiteEntity;
  sed?: SiteEvidenceDocument | null;
  /** Canonical Documents-tab records available as project-duty evidence. */
  documents?: readonly SiteDocument[];
  /** Preloaded, unit-structured DAMs eligible for deterministic screening only. */
  dams?: readonly DatacrateModel[];
  readOnly?: boolean;
  /**
   * Path-to-GO deep-link target. When supplied, the workbench opens the
   * owning workflow panel and transfers focus to it instead of landing on a
   * generic first recommendation.
   */
  initialStage?: GenerationWorkflowStageId | null;
  /** @deprecated Use onSedRebuildRequested. */
  onLayoutPublished?: () => Promise<void>;
  /** Refresh the host's canonical site after promotion/publication commits. */
  onWorkflowAuthorityChanged?: () => Promise<void>;
  /** Start the one SED rebuild that follows a successful new publication. */
  onSedRebuildRequested?: () => Promise<void>;
  /** Open report generation after the frozen SED authority becomes ready. */
  onGenerateTestFit?: () => void;
  /** Open the Documents tab, optionally focused on one linked record. */
  onOpenDocuments?: (documentId?: string) => void;
  /** Open the canonical Equipment (BOM) workspace. */
  onOpenBom?: () => void;
  /** Open the Site Map tab, where published layout layers render. */
  onOpenSiteMap?: () => void;
  /**
   * Full-page Parcel Test-Fit Studio mode. When true, the Step-5 studio is
   * lifted into a full-viewport overlay (mirrors Simulation's `/editor`); the
   * workbench data pipeline is shared, so this is view state only. Driven by
   * the `/sites/:id/studio` route in SiteLandingPage.
   */
  fullStudio?: boolean;
  /** Internal view-only workspace selector rendered in the workbench toolbar. */
  workspaceControl?: React.ReactNode;
  /** Enter full-page studio (navigates to `/sites/:id/studio`). */
  onOpenFullStudio?: () => void;
  /** Leave full-page studio (navigates back to the generation tab). */
  onExitFullStudio?: () => void;
}

type WorkbenchOperation = 'saving' | 'publishing' | 'promoting' | 'deleting' | null;

/** Quiet-time before an edit to a selected scenario is written back (see the
 *  autosave effect). Every keystroke re-arms it, so a save only ever carries a
 *  value the user has stopped changing. */
export const AUTOSAVE_DEBOUNCE_MS = 2500;

interface PendingPublishAttempt {
  requestId: string;
  scenarioId: string;
  scenarioUpdatedAt: string;
  inputHash: string;
  resultHash: string;
  expectedLayoutVersion: number;
}

const technologyLabels: Record<GenerationTechnology, string> = {
  'reciprocating-engine': 'Reciprocating engines',
  'simple-cycle-gas-turbine': 'Simple-cycle turbines',
  'combined-cycle-block': 'Combined-cycle blocks',
  'grid-import': 'Grid import',
  'battery-storage': 'Battery storage (BESS)',
  'solar-bess': 'Solar + BESS',
  'wind-bess': 'Wind + BESS',
  'nuclear-smr': 'Small modular reactor (SMR)',
  'large-nuclear': 'Large nuclear',
};

const intentComponentTechnology: Record<string, GenerationTechnology> = {
  recip: 'reciprocating-engine',
  'gas-turbine': 'simple-cycle-gas-turbine',
  grid: 'grid-import',
  battery: 'battery-storage',
  'solar-bess': 'solar-bess',
  'wind-bess': 'wind-bess',
  smr: 'nuclear-smr',
  'large-nuclear': 'large-nuclear',
};

const intentTechnologyPriority = ['recip', 'gas-turbine', 'smr', 'large-nuclear', 'solar-bess', 'wind-bess', 'battery', 'grid'] as const;
const gasTechnologies = new Set<GenerationTechnology>([
  'reciprocating-engine',
  'simple-cycle-gas-turbine',
  'combined-cycle-block',
]);

const intentComponentLabels: Record<string, string> = {
  recip: 'Reciprocating engines',
  'gas-turbine': 'Gas turbines',
  grid: 'Grid import',
  battery: 'Battery storage',
  'solar-bess': 'Solar + BESS',
  'wind-bess': 'Wind + BESS',
  smr: 'SMR',
  'large-nuclear': 'Large nuclear',
};

const METRES_PER_FOOT = 0.3048;

function technologyFromPhase(phase: ProjectPhase | undefined): GenerationTechnology | undefined {
  if (!phase) return undefined;
  const components = phase.powerSourceConfig?.components ?? [];
  const preferred = intentTechnologyPriority.find((component) => components.includes(component));
  if (preferred) return intentComponentTechnology[preferred];
  switch (phase.powerSource) {
    case 'gas-cc': return 'combined-cycle-block';
    case 'gas-sc': return 'simple-cycle-gas-turbine';
    case 'grid': return 'grid-import';
    case 'solar-bess': return 'solar-bess';
    case 'wind-bess': return 'wind-bess';
    case 'smr': return 'nuclear-smr';
    case 'large-nuclear': return 'large-nuclear';
    default: return undefined;
  }
}

function latestIntentPhase(site: SiteEntity): ProjectPhase | undefined {
  return [...(site.projectIntent?.phases ?? [])].sort((left, right) => right.sequence - left.sequence)[0];
}

/**
 * Generation contingency criterion implied by the declared redundancy class.
 *
 * `redundancyClass` is a data-centre DISTRIBUTION spec (Uptime Institute
 * tiering), not a generation contingency count, so only the two classes that
 * translate without ambiguity are carried:
 *
 *   'N'    -> 0   no spare capacity; the fleet is sized to serve, not to survive
 *   'N+1'  -> 1   one spare beyond need — the same statement as N-1, from the
 *                 other side
 *
 * '2N' and '2N+1' describe full path duplication. How many GENERATION groups
 * that implies depends on how the plant is split, which the redundancy class
 * does not say — so they are deliberately NOT mapped. The workbench surfaces
 * the declared class beside the criterion instead, and the user aligns them.
 */
function contingencyOutagesFromRedundancy(
  redundancyClass: string | undefined,
): number | undefined {
  if (redundancyClass === 'N') return 0;
  if (redundancyClass === 'N+1') return 1;
  return undefined;
}

/**
 * Grid relationship declared for the owning phase in Project Intent.
 * `GridConnectionMode` is the same union as the scenario's `gridConnection`, so
 * no mapping or guesswork is involved — the scenario just stops ignoring a
 * decision the user already recorded. Returns undefined when the phase declares
 * nothing, which keeps the planning default (and its assumption row) in place.
 */
function gridConnectionFromPhase(
  phase: ProjectPhase | undefined,
): GenerationImpactScenarioInput['gridConnection'] | undefined {
  return phase?.powerSourceConfig?.gridConnection;
}

function riskTitle(riskId: string, criterionLabel: string): string {
  return riskId === 'n-1-shortfall' ? `${criterionLabel} shortfall` : riskId.replaceAll('-', ' ');
}

function layoutParcels(site: SiteEntity) {
  return materializeSiteBoundaries({
    siteBoundaries: site.siteBoundaries,
    siteBoundary: site.siteBoundary,
    siteArea: site.siteArea,
    sitePerimeter: site.sitePerimeter,
    siteCentroid: site.siteCentroid,
  });
}

function defaultLayout(site: SiteEntity): GenerationLayoutConfiguration {
  const parcels = layoutParcels(site);
  const preferred = parcels.find(parcel => parcel.intendedUse === 'datacenter')
    ?? [...parcels].sort((left, right) => right.area - left.area)[0];
  return {
    ...DEFAULT_GENERATION_LAYOUT,
    ...(preferred ? { targetParcelId: preferred.id } : {}),
  };
}

function hydrateLayout(
  input: GenerationImpactScenarioInput,
  site: SiteEntity,
): GenerationImpactScenarioInput {
  const layout = input.layout ?? defaultLayout(site);
  const parcel = layoutParcels(site).find(candidate => candidate.id === layout.targetParcelId);
  return {
    ...input,
    ...(site.studyAreaScope ? { studyAreaSelectionHash: site.studyAreaScope.selectionHash } : {}),
    layout,
    layoutAnchor: input.layoutAnchor ?? parcel?.centroid ?? site.siteCentroid ?? site.location,
  };
}

/**
 * The largest single contingency block the shipped screening catalog offers
 * (Wärtsilä 24 × 18.875 MW = 453 MW; GE 7HA.01 normalises to ~456 MW ISO gross).
 * Used ONLY to keep the opening planning proposal inside equipment reality —
 * every value stays editable, and an applied vendor envelope replaces it.
 */
const MAX_PLANNING_BLOCK_ISO_GROSS_MW = 460;

/** Convert a declared load at its recorded boundary once. A new IT-to-facility
 * planning input needs a valid declared or explicitly adopted PUE; modeled
 * config/default estimates cannot silently supply that missing input. */
function planningFacilityLoadMW(intent: ProjectIntent | undefined, phase?: ProjectPhase): number | null {
  if (!intent || isIntentFieldExplicitlyUnknown(intent, 'powerBasis')) return null;
  const stack = phase ? resolvePhasePowerStack(phase, intent) : resolveIntentPowerStack(intent);
  if (stack.basis === 'it-load') {
    const explicitPue = phase?.adoptSimulatedPue && stack.pueSource === 'simulation'
      ? phase.simulatedPueValue : declaredSharedProjectPue(intent);
    if (!isValidPue(explicitPue)) return null;
  }
  if (phase) return stack.facilityMW;
  // Preserve this editor's existing explicit project-MW priority. Only its
  // electrical boundary changes; selecting a phase remains a separate action.
  return resolvePowerStack({
    enteredMW: intent.powerRequirementMW ?? null, basis: stack.basis, pue: stack.pue,
    pueSource: stack.pueSource, powerFactor: stack.powerFactor,
  }).facilityMW ?? resolveIntentPowerStackBand(intent)?.facilityMW.sizingBasis ?? null;
}


/** A facility-field edit is still entered in the project's recorded boundary.
 * Never reinterpret other phases by changing the global basis from this field. */
function canonicalFacilityLoadEdit(intent: ProjectIntent | undefined, facilityMW: number | null,
  phaseId?: string): GenerationProjectInputChanges {
  const phase = phaseId ? intent?.phases?.find(candidate => candidate.id === phaseId) : undefined;
  if (phaseId && !phase) throw new Error('The edited project phase no longer exists.');
  const scope = phaseId ? { phaseId } : {};
  // Clearing a quantity needs no conversion and leaves its basis untouched.
  if (facilityMW == null) return { ...scope, loadMW: null };
  if (!intent || isIntentFieldExplicitlyUnknown(intent, 'powerBasis')
    || (intent.powerBasis !== 'facility' && intent.powerBasis !== 'it-load')) {
    throw new Error('Declare the power basis in Shared project inputs before saving facility demand.');
  }
  if (intent.powerBasis === 'facility') return { ...scope, loadMW: facilityMW, loadBasis: 'facility' };
  const stack = phase ? resolvePhasePowerStack(phase, intent) : resolveIntentPowerStack(intent);
  const explicitPue = phase?.adoptSimulatedPue && stack.pueSource === 'simulation'
    ? phase.simulatedPueValue : declaredSharedProjectPue(intent);
  if (!isValidPue(explicitPue)) {
    throw new Error('Declare a valid PUE or adopt a phase simulation PUE before saving facility demand on an IT basis.');
  }
  const enteredITMW = resolvePowerStack({ enteredMW: facilityMW, basis: 'facility',
    pue: explicitPue, pueSource: stack.pueSource }).itLoadMW;
  if (enteredITMW == null) throw new Error('Enter a positive, finite facility demand.');
  return { ...scope, loadMW: enteredITMW, loadBasis: 'it-load' };
}

function planningBaselineInput(site: SiteEntity): GenerationImpactScenarioInput {
  // Fall back to the BUCKET's sizing basis before giving up. A bucket-only site
  // previously landed on 0 here and then took the hard-coded 100 MW/block guess
  // below, ignoring a stated 200-500 MW intent entirely.
  const facilityLoadMW = planningFacilityLoadMW(site.projectIntent) ?? 0;
  const layout = defaultLayout(site);
  const parcel = layoutParcels(site).find(candidate => candidate.id === layout.targetParcelId);
  const layoutAnchor = parcel?.centroid ?? site.siteCentroid ?? site.location;
  const grossPerBlock = facilityLoadMW > 0 ? Math.ceil((facilityLoadMW * 1.15) / 10) * 10 : 100;
  const intentPhase = latestIntentPhase(site);
  const technology = technologyFromPhase(intentPhase) ?? 'combined-cycle-block';
  const gridConnection = gridConnectionFromPhase(intentPhase);
  const contingencyOutages = contingencyOutagesFromRedundancy(site.projectIntent?.redundancyClass);
  const draft = hydrateLayout(buildPlanningScenario({
    siteId: site.id,
    name: `${site.name} generation concept`,
    facilityLoadMW,
    technology,
    blockCount: 2,
    grossMWPerBlock: grossPerBlock,
    layoutAnchor,
    ...(gridConnection ? { gridConnection } : {}),
    ...(contingencyOutages == null ? {} : { contingencyOutages }),
  }), site);
  // Reserve-size the initial planning fleet for BOTH aggregate-only and
  // structured-phase Project Intent: the phase supplies technology and load,
  // and the exact solver sizing replaces the crude 115% first proposal. This
  // is a sanctioned display default at scenario creation — every value still
  // lands as an editable planning-default assumption.
  if (facilityLoadMW <= 0) return draft;
  const sizing = sizeUniformGenerationBlocks(draft, solveGenerationImpact(draft));
  if (sizing.status !== 'available') return draft;
  // BLOCK COUNT — grounded in what machines exist, not fixed at two.
  //
  // Two blocks under N-1 means ONE block must carry the entire duty, so the
  // opening proposal for a large load was a block rating no machine on the
  // market can supply (a 1 GW site asked for ~1,150 MW per block). The
  // granularity relationship is per-block ≈ requirement / (n − 1), so the
  // fewest blocks that keep the rating inside the real catalog ceiling is a
  // closed-form answer — no search, and the same two solves as before.
  //
  // Deliberately the FEWEST such blocks: finer granularity lowers N-1
  // overbuild, but it also buys switchgear, transformers and collector system
  // that this screening does not price (the Step 4 granularity table says so
  // explicitly). Proposing more blocks than equipment reality requires would be
  // asserting an economic claim the platform does not make.
  const blockCount = Math.min(
    MAX_GENERATION_BLOCK_COUNT,
    Math.max(2, Math.ceil(1 + sizing.suggestedReserveGrossMWPerBlock / MAX_PLANNING_BLOCK_ISO_GROSS_MW)),
  );
  const resized = blockCount === 2
    ? draft
    : hydrateLayout(buildPlanningScenario({
        siteId: site.id,
        name: draft.name,
        facilityLoadMW,
        technology,
        blockCount,
        grossMWPerBlock: sizing.suggestedReserveGrossMWPerBlock,
        layoutAnchor,
        ...(gridConnection ? { gridConnection } : {}),
    ...(contingencyOutages == null ? {} : { contingencyOutages }),
      }), site);
  const finalSizing = blockCount === 2
    ? sizing
    : sizeUniformGenerationBlocks(resized, solveGenerationImpact(resized));
  const grossMWPerBlock = finalSizing.status === 'available'
    ? finalSizing.suggestedReserveGrossMWPerBlock
    : sizing.suggestedReserveGrossMWPerBlock;
  return hydrateLayout(buildPlanningScenario({
    siteId: site.id,
    name: draft.name,
    facilityLoadMW,
    technology,
    blockCount,
    grossMWPerBlock,
    layoutAnchor,
    ...(gridConnection ? { gridConnection } : {}),
    ...(contingencyOutages == null ? {} : { contingencyOutages }),
  }), site);
}

function initialScreeningResolution(
  site: SiteEntity,
  sed: SiteEvidenceDocument | null | undefined,
  documents: readonly SiteDocument[],
  dams: readonly DatacrateModel[],
): GenerationScreeningPrefillResolution {
  return resolveGenerationScreeningPrefill({
    input: planningBaselineInput(site),
    site,
    sed,
    documents,
    dams,
  });
}

function numberValue(value: string, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

type ScreeningInputUpdater = (
  current: GenerationImpactScenarioInput,
) => GenerationImpactScenarioInput;

function upsertGenerationAssumption(
  assumptions: readonly GenerationAssumption[],
  next: GenerationAssumption,
): GenerationAssumption[] {
  return [
    ...assumptions.filter(assumption => assumption.field !== next.field),
    next,
  ];
}

function userScreeningAssumption(
  field: string,
  value: number | string | boolean | undefined,
): GenerationAssumption {
  const cleared = value === undefined || value === '';
  return {
    field,
    value: value ?? '',
    source: 'user',
    label: cleared ? 'Cleared by user' : 'User-entered screening value',
    confidence: 'high',
    autoPopulated: false,
  };
}

function isProtectedScreeningOwner(
  input: GenerationImpactScenarioInput,
  field: string,
): boolean {
  const assumption = [...input.assumptions]
    .reverse()
    .find(candidate => candidate.field === field);
  return assumption?.source === 'user'
    || assumption?.source === 'project-intent'
    || assumption?.source === 'vendor';
}

function preserveProtectedTechnologyFields(
  previous: GenerationImpactScenarioInput,
  rebuilt: GenerationImpactScenarioInput,
): GenerationImpactScenarioInput {
  const previousUnit = previous.units[0];
  if (!previousUnit) return rebuilt;
  const unitFields = [
    ['units.ambientFactor', 'ambientFactor'],
    ['units.degradationFactor', 'degradationFactor'],
    ['units.auxiliaryLoadMWPerUnit', 'auxiliaryLoadMWPerUnit'],
    ['units.heatRateBtuPerKWhHhv', 'heatRateBtuPerKWhHhv'],
    ['units.forcedOutageRate', 'forcedOutageRate'],
    ['units.soundPowerDbA', 'soundPowerDbA'],
  ] as const;
  const protectedUnitPatch: Partial<GenerationImpactScenarioInput['units'][number]> = {};
  for (const [ownerField, unitField] of unitFields) {
    if (isProtectedScreeningOwner(previous, ownerField)) {
      Object.assign(protectedUnitPatch, { [unitField]: previousUnit[unitField] });
    }
  }
  const water = { ...rebuilt.water };
  if (isProtectedScreeningOwner(previous, 'water.generationWithdrawalGalPerMWhGross')) {
    water.generationWithdrawalGalPerMWhGross =
      previous.water.generationWithdrawalGalPerMWhGross;
  }
  if (isProtectedScreeningOwner(previous, 'water.generationConsumptionGalPerMWhGross')) {
    water.generationConsumptionGalPerMWhGross =
      previous.water.generationConsumptionGalPerMWhGross;
  }
  if (isProtectedScreeningOwner(previous, 'water.dataCenterWithdrawalGalPerMWhFacility')) {
    water.dataCenterWithdrawalGalPerMWhFacility =
      previous.water.dataCenterWithdrawalGalPerMWhFacility;
  }
  if (isProtectedScreeningOwner(previous, 'water.dataCenterConsumptionGalPerMWhFacility')) {
    water.dataCenterConsumptionGalPerMWhFacility =
      previous.water.dataCenterConsumptionGalPerMWhFacility;
  }
  const footprint = { ...rebuilt.footprint };
  if (isProtectedScreeningOwner(previous, 'footprint.plantAcresPerGrossMW')) {
    footprint.plantAcresPerGrossMW = previous.footprint.plantAcresPerGrossMW;
  }
  if (isProtectedScreeningOwner(previous, 'footprint.coolingAcres')) {
    footprint.coolingAcres = previous.footprint.coolingAcres;
  }
  if (isProtectedScreeningOwner(previous, 'footprint.switchyardAcres')) {
    footprint.switchyardAcres = previous.footprint.switchyardAcres;
  }
  const protectedAssumptions = previous.assumptions.filter(assumption =>
    assumption.source === 'user'
    || assumption.source === 'project-intent'
    || assumption.source === 'vendor');
  const protectedAssumptionFields = new Set(
    protectedAssumptions.map(assumption => assumption.field),
  );
  return {
    ...rebuilt,
    units: rebuilt.units.map(unit => ({ ...unit, ...protectedUnitPatch })),
    water,
    footprint,
    assumptions: [
      ...rebuilt.assumptions.filter(
        assumption => !protectedAssumptionFields.has(assumption.field),
      ),
      ...protectedAssumptions,
    ],
  };
}

function withShortlisted(
  workspace: GenerationEquipmentWorkspace | undefined,
  catalogId: string,
): GenerationEquipmentWorkspace {
  const existing = workspace?.shortlistedCatalogIds ?? [];
  return {
    shortlistedCatalogIds: existing.includes(catalogId) ? existing : [...existing, catalogId],
    userEquipment: workspace?.userEquipment ?? [],
  };
}

interface UniformFleetPatch {
  technology?: GenerationTechnology;
  count?: number;
  gross?: number;
}

function rebuildUniformGenerationInput(
  current: GenerationImpactScenarioInput,
  siteId: string,
  patch: UniformFleetPatch,
): GenerationImpactScenarioInput {
  const baseline = current.projectDutyBasis && current.equipmentBasis
    ? applyGenerationEquipmentEnvelope(
        current,
        current.equipmentBasis.catalogId,
        { allowUnconfirmed: true },
      )
    : current;
  const previousUnit = baseline.units[0];
  const technology = patch.technology ?? previousUnit?.technology ?? 'combined-cycle-block';
  const count = Math.max(1, Math.floor(patch.count ?? baseline.units.length));
  const grossMWPerBlock = Math.max(0, patch.gross ?? previousUnit?.isoGrossMWPerUnit ?? 0);
  const technologyChanged = technology !== previousUnit?.technology;
  const rebuilt = buildPlanningScenario({
    siteId,
    name: baseline.name,
    facilityLoadMW: baseline.facilityLoadMW,
    criticalLoadMW: baseline.criticalLoadMW,
    technology,
    blockCount: count,
    grossMWPerBlock,
    ccsEnabled: baseline.ccs.enabled && gasTechnologies.has(technology),
    targetPhaseId: baseline.targetPhaseId,
    layoutAnchor: baseline.layoutAnchor,
  });
  const previousAuxiliaryFraction = previousUnit && previousUnit.isoGrossMWPerUnit > 0
    ? previousUnit.auxiliaryLoadMWPerUnit / previousUnit.isoGrossMWPerUnit
    : null;
  const units = technologyChanged || !previousUnit
    ? rebuilt.units
    : rebuilt.units.map((unit) => ({
        ...unit,
        ambientFactor: previousUnit.ambientFactor,
        degradationFactor: previousUnit.degradationFactor,
        auxiliaryLoadMWPerUnit: previousAuxiliaryFraction == null
          ? unit.auxiliaryLoadMWPerUnit
          : grossMWPerBlock * previousAuxiliaryFraction,
        heatRateBtuPerKWhHhv: previousUnit.heatRateBtuPerKWhHhv,
        forcedOutageRate: previousUnit.forcedOutageRate,
        noise: previousUnit.noise,
        soundPowerDbA: previousUnit.soundPowerDbA,
        directivityCorrectionDb: previousUnit.directivityCorrectionDb,
        barrierLossDb: previousUnit.barrierLossDb,
        noxLbPerMWh: previousUnit.noxLbPerMWh,
        coLbPerMWh: previousUnit.coLbPerMWh,
      }));
  const technologyAssumption = rebuilt.assumptions.find(
    (assumption) => assumption.field === 'technologyArchetype',
  );
  const next: GenerationImpactScenarioInput = {
    ...baseline,
    units,
    water: technologyChanged ? rebuilt.water : baseline.water,
    footprint: technologyChanged ? rebuilt.footprint : baseline.footprint,
    assumptions: technologyChanged && technologyAssumption
      ? baseline.assumptions.map((assumption) => (
          assumption.field === 'technologyArchetype' ? technologyAssumption : assumption
        ))
      : baseline.assumptions,
  };
  if (technologyChanged || patch.count !== undefined || patch.gross !== undefined) {
    delete next.equipmentBasis;
    delete next.projectDutyBasis;
    // The generic rebuild also releases an applied user entry (workspace
    // metadata) — the fleet is a planning configuration again.
    if (next.equipmentWorkspace?.appliedUserEquipmentId != null) {
      next.equipmentWorkspace = { ...next.equipmentWorkspace };
      delete next.equipmentWorkspace.appliedUserEquipmentId;
    }
  }
  return next;
}

export function GenerationImpactWorkbench(props: Props): React.JSX.Element {
  const [assessmentMode, setAssessmentMode] = useState<'legacy' | 'planning'>(() => props.site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? 'planning' : 'legacy');
  if (props.fullStudio) return <LegacyGenerationImpactWorkbench {...props} />;
  const workspaceControl = <label className="flex min-w-0 flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-text-muted">
    Workspace
    <select aria-label="Generation assessment workspace" aria-description="Viewing a workspace does not select an assessment for reports. Use the saved revision's promotion action to select it."
      title="View a workspace; select its saved assessment for reports through the promotion action."
      className="w-full min-w-0 max-w-[18rem] rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-medium normal-case tracking-normal text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      value={assessmentMode} onChange={event => setAssessmentMode(event.target.value as typeof assessmentMode)}>
      <option value="legacy">Generation &amp; Impact engineering scenario</option><option value="planning">Grid-led standby static planning</option>
    </select>
  </label>;
  return assessmentMode === 'planning'
    ? <GridLedStandbyPlanningWorkbench site={props.site} documents={props.documents} readOnly={props.readOnly} onWorkflowAuthorityChanged={props.onWorkflowAuthorityChanged} workspaceControl={workspaceControl} />
    : <LegacyGenerationImpactWorkbench {...props} workspaceControl={workspaceControl} />;
}

function LegacyGenerationImpactWorkbench(props: Props): React.JSX.Element {
  try {
    const scoped = applySiteStudyArea(props.site);
    return <GenerationImpactWorkbenchBody {...props} site={scoped} key={`${scoped.id}:${scoped.studyAreaScope?.selectionHash ?? 'legacy'}`} />;
  } catch (error) {
    return <div className="p-5 space-y-3" role="alert">{props.workspaceControl}<p>{(error as Error).message}</p><Button onClick={props.onOpenSiteMap}>Review study area on map</Button></div>;
  }
}

function GenerationImpactWorkbenchBody({
  site,
  sed,
  documents = site.documents ?? [],
  dams = [],
  readOnly = false,
  initialStage = null,
  onLayoutPublished,
  onWorkflowAuthorityChanged,
  onSedRebuildRequested,
  onGenerateTestFit,
  onOpenDocuments,
  onOpenBom,
  onOpenSiteMap,
  fullStudio = false,
  onOpenFullStudio,
  onExitFullStudio,
  workspaceControl,
}: Props): React.JSX.Element {
  const initialScreeningResolutionRef = useRef<GenerationScreeningPrefillResolution | null>(null);
  if (initialScreeningResolutionRef.current == null) {
    initialScreeningResolutionRef.current = initialScreeningResolution(
      site,
      sed,
      documents,
      dams,
    );
  }
  // Unsaved edits are recovered on mount. `input` was plain useState, so a
  // reload silently discarded everything since the last explicit Save.
  // Restoring at initialiser time means the first paint shows the user's work
  // instead of flashing the derived scenario and then replacing it.
  const restoredDraftRef = useRef<string | null>(null);
  const recoveredWorkingDraftRef = useRef(false);
  const [input, setInput] = useState<GenerationImpactScenarioInput>(() => {
    const derived = initialScreeningResolutionRef.current!.input;
    try {
      // At mount no saved scenario is selected yet, so the context is always
      // the working draft. Loading or saving a scenario clears this, so a
      // stale draft can never leak across scenarios.
      const key = scenarioDraftKey(site.id, 'draft');
      const draft = readScenarioDraft(key);
      if (draft) {
        restoredDraftRef.current = draft.savedAt;
        recoveredWorkingDraftRef.current = true;
        return draft.input;
      }
    } catch {
      /* a bad draft must never block opening the workbench */
    }
    return derived;
  });
  // Keep an intentionally blank editor value until its explicit clear commits;
  // the solver's numeric draft can remain zero without turning blank into '0'.
  const [facilityLoadText, setFacilityLoadText] = useState<string | null>(null);
  const facilityLoadEditing = useRef(false);
  useEffect(() => {
    if (!facilityLoadEditing.current) setFacilityLoadText(null);
  }, [input.facilityLoadMW]);
  const [screeningEvidenceResolution, setScreeningEvidenceResolution] = useState(
    () => initialScreeningResolutionRef.current!,
  );
  const [automaticEvidenceRefreshEnabled, setAutomaticEvidenceRefreshEnabled] = useState(true);
  /**
   * The flag as it stands NOW, readable from an in-flight save's continuation.
   * A ref rather than a dependency: `save` is the autosave effect's dep, so
   * re-creating it whenever prefill is toggled would re-arm the debounce from
   * zero on an event that is not a keystroke.
   */
  const automaticEvidenceRefreshEnabledRef = useRef(automaticEvidenceRefreshEnabled);
  automaticEvidenceRefreshEnabledRef.current = automaticEvidenceRefreshEnabled;
  const screeningEvidenceRevision = hashGenerationImpactValue({
    siteId: site.id,
    evidenceMode: site.evidenceMode,
    receptorLayers: (site.siteLayers ?? [])
      .filter(layer => layer.category === 'impact.receptor')
      .map(layer => ({
        id: layer.id,
        importedAt: layer.importedAt,
        geometry: layer.geometry,
      })),
    sed: sed ? {
      sedId: sed.id,
      sedVersion: sed.version,
      climate: sed.climate,
      waterResource: sed.waterResource,
      onSiteInfrastructure: sed.onSiteInfrastructure,
      generationDocumentEvidence: sed.generationDocumentEvidence,
      siteResourceDocumentEvidence: sed.siteResourceDocumentEvidence,
    } : null,
    documents: documents.map(document => ({
      id: document.id,
      contentHash: document.contentHash,
      includeInSed: document.includeInSed,
      archived: document.archived,
      deletedAt: document.deletedAt,
      extractionCacheKey: document.generationScreeningExtraction?.cacheKey,
      extractionStatus: document.generationScreeningExtraction?.status,
    })),
    dams: dams.map(dam => ({
      reportType: dam.reportType,
      version: dam.version,
      buildSeed: dam.buildSeed,
    })),
  });
  const setScreeningField = useCallback((
    field: string,
    value: number | string | boolean | undefined,
    update: ScreeningInputUpdater,
  ): void => {
    setInput((current) => {
      const updated = update(current);
      return {
        ...updated,
        assumptions: upsertGenerationAssumption(
          updated.assumptions,
          userScreeningAssumption(field, value),
        ),
      };
    });
  }, []);
  const screeningInputRevision = hashGenerationImpactValue(input);
  useEffect(() => {
    const resolution = input.siteId === site.id
      ? resolveGenerationScreeningPrefill({
          input,
          site,
          sed,
          documents,
          dams,
        })
      : initialScreeningResolution(site, sed, documents, dams);
    setScreeningEvidenceResolution(resolution);
    if (automaticEvidenceRefreshEnabled && hashGenerationImpactValue(resolution.input) !== screeningInputRevision) {
      setInput(resolution.input);
    }
    // A saved input stays frozen, but its evidence preview must still reflect
    // the selected scenario and current sources. Only a working draft applies
    // proposals automatically; manual fields retain their user assumption.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automaticEvidenceRefreshEnabled, screeningEvidenceRevision, screeningInputRevision]);
  // Utility data requests — the "I don't have the figure yet" path. The margin
  // is UI draft state only (never written to the scenario input); the pending
  // record lives on `input.resourceLimitRequests` and is workflow-only (the
  // solver never reads it, so stored scenarios replay byte-identically).
  const [utilityAskMarginPct, setUtilityAskMarginPct] = useState<Record<UtilityResourceKind, number>>({
    gas: DEFAULT_NOMINATION_MARGIN_PCT,
    water: DEFAULT_NOMINATION_MARGIN_PCT,
    co2: DEFAULT_NOMINATION_MARGIN_PCT,
  });
  const [copiedUtilityAskKind, setCopiedUtilityAskKind] = useState<UtilityResourceKind | null>(null);
  const setResourceLimitRequest = useCallback((kind: UtilityResourceKind, requested: boolean): void => {
    setInput((current) => {
      const { resourceLimitRequests: requests, ...withoutRequests } = current;
      if (!requested) {
        if (!requests?.[kind]) return current;
        const { [kind]: _cleared, ...rest } = requests;
        return Object.keys(rest).length > 0
          ? { ...withoutRequests, resourceLimitRequests: rest }
          : withoutRequests;
      }
      return {
        ...withoutRequests,
        resourceLimitRequests: {
          ...requests,
          [kind]: { requestedAt: new Date().toISOString().slice(0, 10) },
        },
      };
    });
  }, []);
  const copyUtilityAsk = useCallback(async (kind: UtilityResourceKind, to: string, subject: string, body: string): Promise<void> => {
    const text = `To: ${to || '(fill in counterparty contact)'}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard?.writeText(text);
      setCopiedUtilityAskKind(kind);
      window.setTimeout(() => {
        setCopiedUtilityAskKind((current) => (current === kind ? null : current));
      }, 2_000);
    } catch {
      // Clipboard unavailable (permissions/jsdom) — the mailto link still works.
    }
  }, []);
  // A manually entered limit supersedes its pending utility request.
  const withRequestCleared = (
    next: GenerationImpactScenarioInput,
    kind: UtilityResourceKind,
    value: number | undefined,
  ): GenerationImpactScenarioInput => {
    if (value == null || !next.resourceLimitRequests?.[kind]) return next;
    const { resourceLimitRequests: requests, ...withoutRequests } = next;
    const { [kind]: _cleared, ...rest } = requests!;
    return Object.keys(rest).length > 0
      ? { ...withoutRequests, resourceLimitRequests: rest }
      : withoutRequests;
  };
  const [saved, setSaved] = useState<GenerationImpactScenarioSnapshot[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedBase, setSelectedBase] = useState<GenerationImpactScenarioSnapshot | null>(null);
  const [mainProjectDraft, setMainProjectDraft] = useState(true);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [layoutWorkspace, setLayoutWorkspace] = useState<GenerationImpactLayoutWorkspaceState | null>(null);
  const [layoutWorkspaceLoaded, setLayoutWorkspaceLoaded] = useState(false);
  const [layoutReviewOpen, setLayoutReviewOpen] = useState(false);
  const [layoutConflict, setLayoutConflict] = useState<string | null>(null);
  const [layoutPublishError, setLayoutPublishError] = useState<string | null>(null);
  const [publishAttempt, setPublishAttempt] = useState<PendingPublishAttempt | null>(null);
  const [promotedScenarioId, setPromotedScenarioId] = useState<string | null>(
    site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? null : site.projectIntent?.promotedGenerationScenarioId ?? null,
  );
  const [promotedScenario, setPromotedScenario] = useState<GenerationImpactScenarioSnapshot | null>(
    site.generationAssessmentSelection?.kind === 'grid-led-standby-planning' ? null : site.promotedGenerationScenarioSnapshot ?? null,
  );
  const [sedRebuildPublicationId, setSedRebuildPublicationId] = useState<string | null>(null);
  const [operation, setOperation] = useState<WorkbenchOperation>(null);
  const [scenarioSaveFailed, setScenarioSaveFailed] = useState(false);
  const busy = operation !== null;
  const activeSelection = site.generationAssessmentSelection;
  const activeId = activeSelection?.kind === 'generation-impact-v1' ? activeSelection.assessmentId
    : activeSelection == null ? site.projectIntent?.promotedGenerationScenarioId : null;
  const isMainProjectScope = (selectedBase != null && selectedBase.id === activeId) || (activeSelection == null && !activeId && selectedBase == null && mainProjectDraft);
  const editingAssessment = selectedBase ? { kind: 'generation-impact-v1' as const, assessmentId: selectedBase.id,
    inputHash: selectedBase.inputHash, resultHash: selectedBase.resultHash } : null;
  const projectInputSync = useGenerationProjectInputSync({ site, editingAssessment, enabled: isMainProjectScope && !readOnly && !busy, onSaved: onWorkflowAuthorityChanged });
  const sharedFieldFocus = useRef<{ load: string | null; grid: string | null }>({ load: null, grid: null });
  // An equal numeric load still belongs to a different editor scope. A
  // completed clear in this scope stays blank; scenario/phase navigation
  // discards its local text and the old field's captured blur baseline.
  useEffect(() => {
    facilityLoadEditing.current = false;
    setFacilityLoadText(null);
    sharedFieldFocus.current.load = null;
  }, [site.id, selectedId, input.targetPhaseId]);
  const sharedInputHash = generationProjectInputHash(site.projectIntent);
  const observedInputHash = useRef(sharedInputHash);
  useEffect(() => {
    if (observedInputHash.current === sharedInputHash) return;
    observedInputHash.current = sharedInputHash;
    if (!isMainProjectScope) return;
    setInput(current => {
      const next = overlayIntentOnGenerationDraft(current, site.projectIntent);
      // This surface now accepts declared IT demand as a facility proposal.
      // Keep that same guarded conversion current in the active UNSAVED view;
      // no saved scenario/result or inactive what-if receives the overlay.
      if (site.projectIntent?.powerBasis !== 'it-load') return next;
      const phase = current.targetPhaseId
        ? site.projectIntent.phases?.find(candidate => candidate.id === current.targetPhaseId)
        : undefined;
      // A deleted phase cannot silently become the whole-project load.
      if (current.targetPhaseId && !phase) return next;
      const facilityMW = planningFacilityLoadMW(site.projectIntent, phase);
      return facilityMW == null || next.facilityLoadMW === facilityMW
        ? next : { ...next, facilityLoadMW: facilityMW };
    });
  }, [sharedInputHash, isMainProjectScope, site.projectIntent]);
  /** The scenario a delete request is currently in flight for, else null.
   *  Derived rather than stored: `actOnScenario` clears `selectedId` only
   *  after the call resolves, so these two already pin the exact row. */
  const deletingScenarioId = operation === 'deleting' ? selectedId : null;
  const scenarioLoadRequestRef = useRef(0);
  const scenarioSaveRequestRef = useRef(0);
  const [alternativeApplyStatus, setAlternativeApplyStatus] = useState<string | null>(null);
  const [equipmentApplyStatus, setEquipmentApplyStatus] = useState<string | null>(null);
  const [projectDutyApplyStatus, setProjectDutyApplyStatus] = useState<string | null>(null);
  const [projectDutyDraftDirty, setProjectDutyDraftDirty] = useState(false);
  /**
   * Every confirm this workbench opens, counted.
   *
   * `openConfirmDialog` mounts its own portal and carries no state this
   * component can observe, and the actions that open one (change the fleet,
   * load another scenario, promote) leave `operation` null while they await the
   * answer. Autosave must not commit — or race a promote against — edits the
   * user has not decided about yet, so it reads this count, and re-arms when
   * the answer lands.
   */
  const [openConfirmDecisions, setOpenConfirmDecisions] = useState(0);
  const confirmDecision = useCallback(async (options: ConfirmDialogOptions): Promise<boolean> => {
    setOpenConfirmDecisions((open) => open + 1);
    try {
      return await openConfirmDialog(options);
    } finally {
      setOpenConfirmDecisions((open) => open - 1);
    }
  }, []);
  // Hero scenario picker (replaces the left scenario rail): open state is
  // controlled so a successful load can close the panel while compare
  // toggles and corrupt-row selection keep it open.
  const [scenarioPickerOpen, setScenarioPickerOpen] = useState(false);
  // Staged-panel navigation: null until the mount effect pins the first
  // recommendation; afterwards the user owns which stage panel is shown.
  const [selectedStage, setSelectedStage] = useState<GenerationWorkflowStageId | null>(
    initialStage,
  );
  const selectStage = useCallback((stage: GenerationWorkflowStageId): void => {
    setSelectedStage(stage);
  }, []);
  const selectedScenario = useMemo(
    () => saved.find(row => row.id === selectedId) ?? null,
    [saved, selectedId],
  );
  const selectedIntegrityFailed = selectedScenario?.integrity?.valid === false;
  const screeningAssumptions = useMemo(
    () => new Map(input.assumptions.map(assumption => [assumption.field, assumption])),
    [input.assumptions],
  );
  const latestScreeningEvidence = useMemo(
    () => new Map(screeningEvidenceResolution.decisions.map(
      decision => [decision.field, decision.candidate],
    )),
    [screeningEvidenceResolution.decisions],
  );
  // One notice per document (not per fact) with the reason and next step —
  // replaces the six-line resolver dump under the site-limit inputs.
  const screeningEvidenceNotices = useMemo(
    () => summarizeScreeningEvidenceRejections(screeningEvidenceResolution.rejections),
    [screeningEvidenceResolution.rejections],
  );
  const useLatestScreeningEvidence = useCallback((
    field: GenerationScreeningPrefillField,
  ): void => {
    const resolution = resolveGenerationScreeningPrefill({
      input: {
        ...input,
        assumptions: input.assumptions.filter(assumption => assumption.field !== field),
      },
      site,
      sed,
      documents,
      dams,
      refreshableFields: new Set([field]),
    });
    setInput(resolution.input);
    setScreeningEvidenceResolution(resolution);
  }, [dams, documents, input, sed, site]);
  const latestEvidenceActionFor = useCallback((
    field: GenerationScreeningPrefillField,
  ): (() => void) | undefined => {
    if (screeningAssumptions.get(field)?.source !== 'user'
      || !latestScreeningEvidence.has(field)) return undefined;
    return () => useLatestScreeningEvidence(field);
  }, [latestScreeningEvidence, screeningAssumptions, useLatestScreeningEvidence]);
  const studyScopeChanged = (input.studyAreaSelectionHash ?? null) !== (site.studyAreaScope?.selectionHash ?? null);
  const result = useMemo(() => solveGenerationImpact(input), [input]);
  // Same bucket fallback for the load-basis selector, so "Project Intent total"
  // is selectable (and correctly detected as selected) on a bucket-only site.
  const projectIntentBand = useMemo(
    () => resolveIntentPowerStackBand(site.projectIntent),
    [site.projectIntent],
  );
  const projectLoadMW = planningFacilityLoadMW(site.projectIntent);
  const enteredProjectMW = site.projectIntent?.powerRequirementMW;
  const projectLoadUsesBand = projectLoadMW != null && projectIntentBand != null
    && !(typeof enteredProjectMW === 'number' && Number.isFinite(enteredProjectMW) && enteredProjectMW > 0);
  // The design-basis strip is LABELLED "Facility basis" but printed the raw
  // `powerRequirementMW`, which is the IT load whenever the intent is on an
  // it-load basis. Resolve the actual facility layer instead — and render the
  // band rather than "Missing" when only a bucket is declared.
  // PUE / PF for the max-power inverse stack — resolved through the SAME
  // precedence chain the intent breakdown uses, so the two never disagree about
  // how big a campus a given generation figure supports.
  const intentPowerContext = useMemo(() => {
    const stack = resolveIntentPowerStack(site.projectIntent);
    return { pue: stack.pue, pueSource: stack.pueSource, powerFactor: stack.powerFactor };
  }, [site.projectIntent]);
  /**
   * DATA-CENTRE LOAD BASIS — size the load from what is actually being built.
   *
   * Step 1 otherwise asks for facility MW as a bare number, which a developer
   * who knows their rack count and workload but not their PUE-adjusted facility
   * draw has to compute by hand. `sizeDataCenter` already owns that arithmetic
   * (racks × kW/rack → IT MW → facility MW through `resolvePowerStack`, the one
   * platform owner of the PUE ladder), and ranks the presets against the
   * workload Project Intent already declares.
   *
   * It is a BASIS, not an authority: it writes `facilityLoadMW` /
   * `criticalLoadMW` and nothing else, exactly like picking a phase.
   */
  const dataCenterPresets = useMemo(
    () => dataCenterPresetsForIntent(effectiveWorkloadProfile(site.projectIntent)),
    [site.projectIntent],
  );
  const [dataCenterDraft, setDataCenterDraft] = useState<{ presetId: WorkloadProfile; rackCount: string }>(
    () => ({ presetId: dataCenterPresets[0]?.id ?? 'mixed', rackCount: '' }),
  );
  const dataCenterSizing = useMemo(() => {
    const rackCount = Number(dataCenterDraft.rackCount);
    if (!Number.isFinite(rackCount) || rackCount <= 0) return null;
    try {
      return sizeDataCenter({
        presetId: dataCenterDraft.presetId,
        rackCount,
        pue: intentPowerContext.pue ?? undefined,
        powerFactor: intentPowerContext.powerFactor ?? undefined,
      });
    } catch {
      // Out-of-range rack counts simply produce no preview rather than an error.
      return null;
    }
  }, [dataCenterDraft, intentPowerContext.powerFactor, intentPowerContext.pue]);
  const applyDataCenterSizing = useCallback((): void => {
    if (!dataCenterSizing || readOnly) return;
    setInput(current => applyDataCenterLoad(current, dataCenterSizing));
    toast.success(
      `Facility load set to ${dataCenterSizing.facilityMW.toFixed(1)} MW from ${dataCenterSizing.rackCount.toLocaleString('en-US')} racks.`,
    );
  }, [dataCenterSizing, readOnly]);
  // FACILITY COOLING WATER BASIS — a demand parameter of the data centre, so
  // it lives with the load definition (Step 1), not among the Step 2 supply
  // limits. The cooling-type default enters as `source: 'planning-default'`
  // with its citation, so provenance is visible and any user- or
  // evidence-supplied basis supersedes it.
  const [coolingTypeDraft, setCoolingTypeDraft] = useState<DataCenterCoolingType | ''>('');
  // P5 — recommend a cooling FAMILY from the documented water limit (the
  // `water-capacity` constraint fit) and the declared heat-rejection sink;
  // no recommendation without a documented limit (honesty over helpfulness).
  const coolingRecommendation = useMemo(() => {
    const waterFit = deriveGenerationEquipmentConstraintFits(result, input)
      .find((fit) => fit.id === 'water-capacity')?.status ?? null;
    const recommendation = recommendDataCenterCoolingType({
      declaredHeatRejection: site.projectIntent?.bopHeatRejection ?? null,
      waterFit,
    });
    if (!recommendation) return null;
    const label = DATA_CENTER_COOLING_WATER_DEFAULTS.find((entry) => entry.coolingType === recommendation.coolingType)?.label
      ?? recommendation.coolingType;
    return { ...recommendation, label };
  }, [result, input, site.projectIntent?.bopHeatRejection]);
  const applyCoolingWaterDefault = useCallback((): void => {
    if (!coolingTypeDraft || readOnly) return;
    const pue = dataCenterSizing?.pue ?? intentPowerContext.pue ?? DEFAULT_PUE;
    const basis = deriveDataCenterCoolingWaterBasis(coolingTypeDraft, pue);
    if (!basis) return;
    const defaultAssumption = (field: string, value: number): GenerationAssumption => ({
      field,
      value,
      source: 'planning-default',
      label: `${basis.label} cooling planning default (PUE ${pue.toFixed(2)})`,
      citation: basis.citation,
      confidence: 'low',
      autoPopulated: false,
    });
    setInput((current) => ({
      ...current,
      water: {
        ...current.water,
        dataCenterWithdrawalGalPerMWhFacility: basis.withdrawalGalPerMWhFacility,
        dataCenterConsumptionGalPerMWhFacility: basis.consumptionGalPerMWhFacility,
      },
      assumptions: upsertGenerationAssumption(
        upsertGenerationAssumption(
          current.assumptions,
          defaultAssumption('water.dataCenterWithdrawalGalPerMWhFacility', basis.withdrawalGalPerMWhFacility),
        ),
        defaultAssumption('water.dataCenterConsumptionGalPerMWhFacility', basis.consumptionGalPerMWhFacility),
      ),
    }));
    toast.success(`${basis.label} cooling basis applied — the campus water total now computes.`);
  }, [coolingTypeDraft, dataCenterSizing, intentPowerContext.pue, readOnly]);

  const designBasisFacilityLabel = useMemo(() => {
    if (projectLoadMW == null) return 'Unknown facility demand';
    if (projectLoadUsesBand && projectIntentBand) return formatPowerBandLayer(projectIntentBand.facilityMW);
    // Match the Project Intent source picker, including its explicit project
    // quantity priority; phase cumulative values have their own options.
    return `${Math.round(projectLoadMW)} MW`;
  }, [projectLoadMW, projectLoadUsesBand, projectIntentBand]);
  const projectPhases = useMemo(
    () => (site.projectIntent?.phases ?? []).filter(
      (phase) => phase.capacityMW != null && Number.isFinite(phase.capacityMW),
    ),
    [site.projectIntent?.phases],
  );
  // RANGE-mode phases cannot be a scenario load basis — a basis needs an exact
  // MW, and collapsing a range into one here would break the "a range never
  // collapses into a scalar" rule. Surface them as DISABLED picker options so
  // they stay discoverable (previously they silently vanished from the list).
  const rangeOnlyPhases = useMemo(
    () => (site.projectIntent?.phases ?? []).filter((phase) => {
      const cap = effectivePhaseCapacity(phase);
      return !cap.isExact && cap.range != null;
    }),
    [site.projectIntent?.phases],
  );
  const selectedLoadBasis = input.targetPhaseId
    && projectPhases.some((phase) => phase.id === input.targetPhaseId)
    ? input.targetPhaseId
    : projectLoadMW != null && input.facilityLoadMW === projectLoadMW
      ? 'project-total'
      : 'manual';
  const selectedIntentPhase = selectedLoadBasis === 'project-total'
    ? latestIntentPhase(site)
    : projectPhases.find((phase) => phase.id === selectedLoadBasis);
  const intentTechnology = technologyFromPhase(selectedIntentPhase);
  const intentComponents = selectedIntentPhase?.powerSourceConfig?.components ?? [];
  const isGasTechnology = gasTechnologies.has(input.units[0]?.technology ?? 'combined-cycle-block');
  const isGridOnly = isGridImportOnlyScenario(input);
  const latestSelected = selectedId ? saved.find((row) => row.id === selectedId) : undefined;
  const selectedRemoteChanged = Boolean(
    selectedBase
      && (!latestSelected
        || latestSelected.updatedAt !== selectedBase.updatedAt
        || latestSelected.inputHash !== selectedBase.inputHash
        || latestSelected.resultHash !== selectedBase.resultHash),
  );
  const inputIsSaved = generationScenarioInputMatches(input, selectedBase);
  /**
   * The live input differs from the selected snapshot ONLY in its name.
   *
   * A save re-solves the scenario server-side and rewrites the record, so a
   * pause between keystrokes in the name field must not commit a half-typed
   * identifier. Renaming is committed by the manual Save (or carried along by
   * the next real edit); autosave leaves it alone.
   */
  const inputDiffersOnlyByName = selectedBase != null
    && !inputIsSaved
    && generationScenarioInputMatches({ ...input, name: selectedBase.input.name }, selectedBase);
  const inputFingerprint = useMemo(
    () => hashGenerationImpactValue(input),
    [input],
  );
  /**
   * What the workbench is pointed at NOW, read by an in-flight save to place
   * its response. Deliberately NOT the input fingerprint, and the scenarioId is
   * a placement axis, not an applicability one: a response that arrives after
   * the user typed on, or after they moved to another scenario, still describes
   * a write that already LANDED. Its identity has to be adopted for the row it
   * was written for, or every later save of that row carries a stale
   * `expectedUpdatedAt` and is rejected as a conflict forever.
   */
  const renderedSaveScope = {
    siteId: site.id,
    scenarioId: selectedBase?.id ?? 'draft',
  };
  const saveScopeRef = useRef({ ...renderedSaveScope, revision: 0 });
  if (saveScopeRef.current.siteId !== renderedSaveScope.siteId
    || saveScopeRef.current.scenarioId !== renderedSaveScope.scenarioId) {
    saveScopeRef.current = {
      ...renderedSaveScope,
      revision: saveScopeRef.current.revision + 1,
    };
  }
  /**
   * The input as it stands NOW, readable from an in-flight save's continuation.
   * `input` captured in that closure is the shape that was SENT, so it always
   * matches the response and can never tell whether the user typed on. Read it
   * for reporting only — never to decide whether a landed write applies.
   */
  const liveInputRef = useRef(input);
  liveInputRef.current = input;
  useEffect(() => {
    setScenarioSaveFailed(false);
  }, [input]);

  // Tell the user their work came back — a silent restore is indistinguishable
  // from "my edits were never lost", which hides the fact that they are still
  // UNSAVED and still need an explicit save.
  useEffect(() => {
    const savedAt = restoredDraftRef.current;
    if (!savedAt) return;
    restoredDraftRef.current = null;
    const when = new Date(savedAt);
    toast.success(
      `Recovered unsaved scenario edits from ${Number.isNaN(when.getTime()) ? 'your last session' : when.toLocaleString()}. Save to keep them.`,
    );
  }, []);

  // Auto-save the working input so a reload/crash never costs the session's
  // work. This is CRASH PROTECTION, not a save: the Firestore scenario record
  // stays the only shared, immutable, publishable artifact, and a recovered
  // draft is still unsaved (it still blocks publishing until saved). Debounced
  // so typing does not thrash storage.
  useEffect(() => {
    const key = scenarioDraftKey(site.id, 'draft');
    if (inputIsSaved) {
      // The work is genuinely persisted — drop the local copy so a later
      // session cannot resurrect a stale draft over the saved scenario.
      clearScenarioDraft(key);
      return;
    }
    const timer = setTimeout(() => {
      writeScenarioDraft(key, input, selectedBase?.inputHash ?? 'draft');
    }, 500);
    return () => clearTimeout(timer);
  }, [input, inputIsSaved, site.id, selectedBase?.inputHash]);
  const layoutView = useMemo(() => readLayoutWorkspace(layoutWorkspace), [layoutWorkspace]);
  const currentProjectScenario = useMemo(() => {
    try {
      return resolvePromotedGenerationScenario(site.projectIntent,
        site.promotedGenerationScenarioSnapshot ?? promotedScenario,
        saved.find(row => row.id === activeId), site.generationAssessmentSelection,
        site.generationProjectInputBinding);
    } catch { return null; }
  }, [activeId, promotedScenario, saved, site.generationAssessmentSelection,
    site.generationProjectInputBinding, site.projectIntent, site.promotedGenerationScenarioSnapshot]);
  const currentPublishedVersion = currentProjectScenario && layoutWorkspace?.active
    && generationImpactWorkflowInternals.layoutMatchesScenario(layoutWorkspace.active, currentProjectScenario)
      ? layoutWorkspace.active.version : null;
  const selectedIsPromoted = generationImpactWorkflowInternals.sameScenarioRevision(
    promotedScenario,
    selectedBase,
  );
  const publishBlockReason = !selectedBase
    ? 'Save and select this scenario before publishing its layout.'
    : selectedRemoteChanged
      ? 'The selected saved scenario changed elsewhere. Reload it or clone this preserved draft before publishing.'
      : !inputIsSaved
        ? 'Save these scenario changes before publishing. The map can only use an immutable saved snapshot.'
        : !selectedIsPromoted
          ? 'Promote this exact saved scenario revision to Project Intent before publishing its layout.'
          : !selectedBase.input.layoutAnchor
            ? 'Set a site location or layout anchor before publishing geometry.'
            : null;
  /**
   * The one publish blocker whose remedy is NOT on this step. Save, reload and
   * promote all have their own control right here; the layout anchor is set in
   * the Step 5 parcel picker, so naming the blocker without a way to reach it
   * left the user to find it themselves.
   */
  const publishBlockRemedy = publishBlockReason != null
    && selectedBase != null
    && !selectedBase.input.layoutAnchor
    ? { label: 'Open Parcel fit', onClick: () => selectStage('parcel') }
    : null;
  const testFitReadiness = useMemo(
    () => evaluateWorkbenchTestFitWorkflow({
      site,
      sed,
      scenario: selectedBase,
      inputIsSaved,
      remoteScenarioChanged: selectedRemoteChanged,
      promotedScenario,
      layoutHead: layoutWorkspace?.active ?? null,
      sedRebuildRequested: sedRebuildPublicationId != null,
    }),
    [
      inputIsSaved,
      layoutWorkspace?.active,
      promotedScenario,
      sed,
      sedRebuildPublicationId,
      selectedBase,
      selectedRemoteChanged,
      site,
    ],
  );
  const sizing = useMemo(() => sizeUniformGenerationBlocks(input, result), [input, result]);
  const granularity = useMemo(() => sweepGenerationBlockGranularity(input, result), [input, result]);
  // Shared with the frozen report study so both surfaces show the same ladder.
  const granularityRows = useMemo(() => selectGranularityLadderRows(granularity), [granularity]);
  const alternatives = useMemo(() => buildGenerationAlternativesMatrix(input), [input]);
  // LIVE ENVELOPE AVAILABILITY — Firestore `equipment_catalog` governs WHICH
  // screening envelopes are offered (admins hide rows without a release); the
  // shipped versioned catalog stays the numeric authority, so an admitted
  // entry's solver duty can never be changed by a DB edit. Unseeded/offline →
  // full static catalog, byte-identical to the pre-Firestore behavior.
  const screeningAdmissions = useGenerationScreeningAdmissions();
  const equipmentRows = useMemo(
    () => buildGenerationEquipmentScreen(input, screeningAdmissions.admitted),
    [input, screeningAdmissions.admitted],
  );
  const equipmentSelection = useMemo(
    () => projectGenerationEquipmentExhibitBasis(input),
    [input],
  );
  const userEquipmentRows = useMemo(
    () => buildGenerationUserEquipmentScreen(input),
    [input],
  );
  const [addEquipmentOpen, setAddEquipmentOpen] = useState(false);
  // Equipment Library (BOM / OEM database / company catalog / manual → selection).
  const [importOpen, setImportOpen] = useState(false);
  const [importTab, setImportTab] = useState<FinderSource | 'all'>('all');
  const [importPrefill, setImportPrefill] = useState<GenerationUserEquipmentPrefill | null>(null);
  // Remount key for the add dialog so each import starts from fresh state and
  // a later blank open is truly blank.
  const [importNonce, setImportNonce] = useState(0);
  const [bomBridgeDismissed, setBomBridgeDismissed] = useState(false);
  // The equipment selection (catalog or verified user entry) whose duty the
  // fleet currently carries; null for generic planning fleets or hand edits.
  const activeEquipmentSelection = useMemo(
    () => activeGenerationEquipmentSelection(input),
    [input],
  );
  const parcels = useMemo(() => layoutParcels(site), [
    site.siteArea,
    site.siteBoundaries,
    site.siteBoundary,
    site.siteCentroid,
    site.sitePerimeter,
  ]);
  const fallbackLayout = useMemo(() => defaultLayout(site), [
    site.siteArea,
    site.siteBoundaries,
    site.siteBoundary,
    site.siteCentroid,
    site.sitePerimeter,
  ]);
  const activeLayout = input.layout ?? fallbackLayout;
  const testFit = useMemo(() => buildGenerationParcelTestFit({
    parcels,
    layout: activeLayout,
    fallbackAnchor: input.layoutAnchor ?? site.siteCentroid ?? site.location,
    fallbackEnvelopeAcres: site.siteBoundaryAggregate?.totalArea ?? site.siteArea,
    generationBlockCount: Math.max(1, input.units.reduce((sum, unit) => sum + unit.count, 0)),
    footprint: result.footprint,
    installedReservations: input.projectDutyBasis?.installedReservations,
  }), [
    activeLayout,
    input.layoutAnchor,
    input.projectDutyBasis?.installedReservations,
    input.units,
    parcels,
    result.footprint,
    site.location,
    site.siteArea,
    site.siteBoundaryAggregate?.totalArea,
    site.siteCentroid,
  ]);
  const totalCcsLoadMW = useMemo(
    () => result.unitResults.reduce((sum, unit) => sum + unit.ccsLoadMWPerUnit * unit.count, 0),
    [result.unitResults],
  );

  // grid-screening-v2 — live noise propagation field for the CURRENT inputs
  // (same pure engine the solver freezes at save; preview = frozen result by
  // construction). Failure (missing anchor, invalid barrier) renders nothing.
  const [noiseVisible, setNoiseVisible] = useState(false);
  const liveNoiseField = useMemo(() => {
    if (!input.noiseSources || !input.noiseGeometry) return null;
    try {
      return buildNoiseField({
        noiseSources: input.noiseSources,
        geometry: input.noiseGeometry,
        barriers: input.layout?.acousticBarriers ?? [],
        receptors: input.receptors,
      });
    } catch {
      return null;
    }
  }, [input.noiseSources, input.noiseGeometry, input.layout?.acousticBarriers, input.receptors]);

  // Keep the frozen emitter positions synced to the placed generation blocks
  // so save/publish replays exactly what the preview shows. `basis: 'manual'`
  // opts a scenario out of auto-sync. JSON compare guards the effect loop.
  useEffect(() => {
    if (!input.noiseSources || input.noiseGeometry?.basis === 'manual') return;
    const derived = deriveNoiseGeometryFromTestFit(input.noiseSources, testFit);
    if (JSON.stringify(derived) !== JSON.stringify(input.noiseGeometry)) {
      setInput((current) => ({ ...current, noiseGeometry: derived }));
    }
  }, [input.noiseSources, input.noiseGeometry, testFit]);
  const layoutSearchBase = useMemo(() => ({
    parcels,
    fallbackAnchor: input.layoutAnchor ?? site.siteCentroid ?? site.location,
    fallbackEnvelopeAcres: site.siteBoundaryAggregate?.totalArea ?? site.siteArea,
    generationBlockCount: Math.max(1, input.units.reduce((sum, unit) => sum + unit.count, 0)),
    footprint: result.footprint,
    installedReservations: input.projectDutyBasis?.installedReservations,
  }), [
    input.layoutAnchor,
    input.projectDutyBasis?.installedReservations,
    input.units,
    parcels,
    result.footprint,
    site.location,
    site.siteArea,
    site.siteBoundaryAggregate?.totalArea,
    site.siteCentroid,
  ]);
  const alternativeParcelFits = useMemo(() => (
    alternatives.status === 'available' && testFit.parcel
      ? rankAlternativeParcelFits({
          rows: alternatives.rows,
          base: {
            parcels,
            fallbackAnchor: layoutSearchBase.fallbackAnchor,
            fallbackEnvelopeAcres: layoutSearchBase.fallbackEnvelopeAcres,
          },
          layout: activeLayout,
        })
      : undefined
  ), [activeLayout, alternatives, layoutSearchBase.fallbackAnchor, layoutSearchBase.fallbackEnvelopeAcres, parcels, testFit.parcel]);

  /**
   * Saved-scenario comparison rows. Each row keeps its OWN frozen solver result
   * (capacity, gas, water, footprint are replayed, never recomputed), but the
   * parcel fit is re-derived against the CURRENT layout through the same ranker
   * the alternatives matrix uses — comparing candidates on one geometry is the
   * only way "which of these actually fits" is answerable.
   */
  const comparedScenarios = useMemo(() => {
    const rows = saved.filter(row => row.integrity?.valid !== false && compareIds.includes(row.id));
    const fits = testFit.parcel
      ? rankAlternativeParcelFits({
          rows: rows.map(row => ({
            id: row.id,
            blockCount: Math.max(1, row.input.units.reduce((sum, unit) => sum + unit.count, 0)),
            result: { footprint: row.result.footprint },
            input: { projectDutyBasis: row.input.projectDutyBasis },
          })),
          base: {
            parcels,
            fallbackAnchor: layoutSearchBase.fallbackAnchor,
            fallbackEnvelopeAcres: layoutSearchBase.fallbackEnvelopeAcres,
          },
          layout: activeLayout,
        })
      : undefined;
    return rows.map(row => {
      const capacity = row.result.capacity as typeof row.result.capacity & {
        contingencyOutages?: number;
        contingencyNetMW?: number;
        contingencyMarginMW?: number;
        contingencyReserveMarginMW?: number;
      };
      const outages = capacity.contingencyOutages ?? row.input.contingencyOutages ?? 1;
      const selection = activeGenerationEquipmentSelection(row.input);
      return {
        row,
        criterionLabel: outages === 0 ? 'N' : `N-${outages}`,
        capacity: {
          netMW: capacity.contingencyNetMW ?? capacity.nMinusOneNetMW,
          marginMW: capacity.contingencyMarginMW ?? capacity.nMinusOneMarginMW,
          reserveMarginMW: capacity.contingencyReserveMarginMW ?? capacity.nMinusOneReserveMarginMW,
        },
        equipmentLabel: selection
          ? `${selection.manufacturer} ${selection.model}${selection.kind === 'user' ? ' (unverified)' : ''}${row.input.projectDutyBasis ? ' + dossier' : ''}`
          : 'Planning technology',
        parcelFit: fits?.get(row.id) ?? null,
      };
    });
  }, [activeLayout, compareIds, layoutSearchBase.fallbackAnchor, layoutSearchBase.fallbackEnvelopeAcres, parcels, saved, testFit.parcel]);

  useEffect(() => {
    if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') {
      setPromotedScenario(null); setPromotedScenarioId(null); return;
    }
    const next = site.promotedGenerationScenarioSnapshot;
    if (!next || (site.generationAssessmentSelection == null && !site.projectIntent?.promotedGenerationScenarioId)) {
      // Missing result hydration cannot retire the canonical immutable row.
      // It remains unavailable as result authority, but autosave cannot fork it.
      setPromotedScenario(null); setPromotedScenarioId(activeId ?? null); return;
    }
    setPromotedScenario((current) => (
      generationImpactWorkflowInternals.sameScenarioRevision(current, next) ? current : next
    ));
    setPromotedScenarioId(next.id);
  }, [activeId, site.promotedGenerationScenarioSnapshot, site.generationAssessmentSelection, site.projectIntent?.promotedGenerationScenarioId]);

  useEffect(() => {
    if (!sedRebuildPublicationId) return;
    // 'failed' AND 'partial' are both TERMINAL — the rebuild stopped, it simply
    // stopped incompletely. Only 'bootstrapping' means still running.
    //
    // Clearing on 'failed' alone left a rebuild that landed on 'partial' with
    // this flag set forever, so `sedRebuildRequested` stayed true and Step 6
    // showed "the rebuild is running in the background" indefinitely — no
    // error, no retry, and nothing that would ever change it. Releasing the
    // flag lets the readiness chain judge the SED on its merits and offer
    // Rebuild SED again, which is an actionable state rather than a wedge.
    if (site.sedStatus === 'failed' || site.sedStatus === 'partial') {
      setSedRebuildPublicationId(null);
      return;
    }
    const frozenPublicationId = sed?.generationImpactAuthoritySnapshot?.provenance.layoutPublicationId;
    if (site.sedStatus === 'complete'
      && site.sedVersion === sed?.version
      && frozenPublicationId === sedRebuildPublicationId) {
      setSedRebuildPublicationId(null);
    }
  }, [sed, sedRebuildPublicationId, site.sedStatus, site.sedVersion]);

  const applyLoadBasis = useCallback(async (basis: string): Promise<void> => {
    if (basis === 'manual') {
      setInput((current) => ({ ...current, targetPhaseId: undefined }));
      return;
    }
    const phase = basis === 'project-total'
      ? latestIntentPhase(site)
      : projectPhases.find((candidate) => candidate.id === basis);
    const loadMW = basis === 'project-total'
      ? projectLoadMW
      : phase ? planningFacilityLoadMW(site.projectIntent, phase) : null;
    if (loadMW == null || !Number.isFinite(loadMW)) return;
    const matchedTechnology = technologyFromPhase(phase);
    if (matchedTechnology
      && matchedTechnology !== input.units[0]?.technology
      && (input.equipmentBasis || input.projectDutyBasis)
      && !(await confirmDecision({
        title: 'Change load basis?',
        body: 'Changing this load basis also changes the generation technology. The selected vendor envelope and project duty dossier will be cleared.',
        confirmLabel: 'Continue',
        tone: 'danger',
      }))) return;
    setInput((current) => {
      const rebuilt = matchedTechnology && matchedTechnology !== current.units[0]?.technology
        ? hydrateLayout(buildPlanningScenario({
            siteId: site.id,
            name: current.name,
            facilityLoadMW: loadMW,
            criticalLoadMW: loadMW,
            technology: matchedTechnology,
            blockCount: current.units.length,
            grossMWPerBlock: current.units[0]?.isoGrossMWPerUnit ?? 0,
            ccsEnabled: current.ccs.enabled && gasTechnologies.has(matchedTechnology),
            targetPhaseId: basis === 'project-total' ? undefined : basis,
            layoutAnchor: current.layoutAnchor,
          }), site)
        : current;
      const next = {
        ...rebuilt,
        facilityLoadMW: loadMW,
        criticalLoadMW: loadMW,
        targetPhaseId: basis === 'project-total' ? undefined : basis,
        gridConnection: current.gridConnection,
        reserveMarginPct: current.reserveMarginPct,
        contingencyOutages: current.contingencyOutages,
        receptors: current.receptors,
        lighting: current.lighting,
        availableGasCapacityDthPerDay: current.availableGasCapacityDthPerDay,
        availableWaterConsumptionGalPerDay: current.availableWaterConsumptionGalPerDay,
        ccsExportCapacityTonnesPerYear: current.ccsExportCapacityTonnesPerYear,
        layout: current.layout,
      };
      const protectedNext = preserveProtectedTechnologyFields(current, next);
      if (!automaticEvidenceRefreshEnabled) return protectedNext;
      return resolveGenerationScreeningPrefill({
        input: protectedNext,
        site,
        sed,
        documents,
        dams,
      }).input;
    });
  }, [automaticEvidenceRefreshEnabled, confirmDecision, dams, documents, input.equipmentBasis, input.projectDutyBasis, input.units, projectLoadMW, projectPhases, sed, site]);

  const loadScenarios = useCallback(async () => {
    const requestId = ++scenarioLoadRequestRef.current;
    const requestSiteId = site.id;
    try {
      const data = await callUserFunction<ListGenerationImpactScenariosResult & {
        layout?: GenerationImpactLayoutWorkspaceState;
      }>('listGenerationImpactScenarios', { siteId: site.id, ownerUid: site._ownerUid });
      if (scenarioLoadRequestRef.current !== requestId || requestSiteId !== site.id) return;
      setSaved(data.scenarios);
      setLayoutWorkspace(data.layout ?? {
        active: null,
        recent: [],
        activeLayers: [],
        legacyUnversioned: false,
      });
      setCompareIds((ids) => ids.filter((id) => data.scenarios.some(
        (scenario) => scenario.id === id && scenario.integrity?.valid !== false,
      )));
    } catch (error) {
      if (scenarioLoadRequestRef.current !== requestId || requestSiteId !== site.id) return;
      toast.error((error as Error).message || 'Could not load generation scenarios');
    } finally {
      if (scenarioLoadRequestRef.current === requestId && requestSiteId === site.id) {
        setLayoutWorkspaceLoaded(true);
      }
    }
  }, [site._ownerUid, site.id]);

  useEffect(() => {
    setLayoutWorkspaceLoaded(false);
    void loadScenarios();
    return () => {
      scenarioLoadRequestRef.current += 1;
    };
  }, [loadScenarios]);

  const rebuildUnits = useCallback(async (patch: UniformFleetPatch): Promise<void> => {
    const currentUnit = input.units[0];
    const changesAuthority = (patch.technology != null && patch.technology !== currentUnit?.technology)
      || (patch.count != null && Math.floor(patch.count) !== input.units.length)
      || (patch.gross != null && patch.gross !== currentUnit?.isoGrossMWPerUnit);
    if (changesAuthority
      && (input.equipmentBasis || input.projectDutyBasis)
      && !(await confirmDecision({
        title: 'Change the fleet?',
        body: 'Changing the fleet will clear the selected vendor envelope and project duty dossier because their ratings no longer match.',
        confirmLabel: 'Continue',
        tone: 'danger',
      }))) return;
    setInput((current) => {
      const rebuilt = preserveProtectedTechnologyFields(
        current,
        rebuildUniformGenerationInput(current, site.id, patch),
      );
      if (!automaticEvidenceRefreshEnabled) return rebuilt;
      return resolveGenerationScreeningPrefill({
        input: rebuilt,
        site,
        sed,
        documents,
        dams,
      }).input;
    });
    setProjectDutyApplyStatus(null);
  }, [automaticEvidenceRefreshEnabled, confirmDecision, dams, documents, input.equipmentBasis, input.projectDutyBasis, input.units, sed, site]);

  const patchUniformUnits = useCallback((
    field: string,
    value: number | undefined,
    patch: Partial<Pick<GenerationImpactScenarioInput['units'][number],
      'ambientFactor' | 'degradationFactor' | 'auxiliaryLoadMWPerUnit' | 'heatRateBtuPerKWhHhv' | 'forcedOutageRate'>>,
  ): void => {
    setScreeningField(field, value, current => ({
      ...current,
      units: current.units.map((unit) => ({ ...unit, ...patch })),
    }));
  }, [setScreeningField]);

  const toggleCcs = useCallback((enabled: boolean): void => {
    setScreeningField('ccs.enabled', enabled, current => ({
      ...current,
      ccs: setCcsEnabledWithPlanningDefaults(current.ccs, enabled),
      assumptions: current.assumptions.map((assumption) => assumption.field === 'ccsArchetype'
        ? { ...assumption, value: enabled }
        : assumption),
    }));
  }, [setScreeningField]);

  const patchLayout = useCallback((patch: Partial<GenerationLayoutConfiguration>): void => {
    if (readOnly || busy) return;
    setInput((current) => ({
      ...current,
      layout: {
        ...(current.layout ?? defaultLayout(site)),
        ...patch,
        // Legacy scenarios remain byte-identical until an operator makes a
        // deliberate Studio edit. From that point onward every mutation uses
        // the pin/custom/row-road geometry contract.
        schemaVersion: 2,
      },
    }));
  }, [busy, readOnly, site]);

  const replaceLayout = useCallback((layout: GenerationLayoutConfiguration): void => {
    if (readOnly || busy) return;
    setInput((current) => ({
      ...current,
      // Undo/redo restores the exact captured snapshot. In particular, undoing
      // the first Studio edit must return a legacy layout to legacy semantics.
      layout: structuredClone(layout),
    }));
  }, [busy, readOnly]);

  const commitLayout = useCallback((
    _label: string,
    patch: Partial<GenerationLayoutConfiguration>,
  ): void => {
    patchLayout(patch);
  }, [patchLayout]);

  const applyAlternative = useCallback(async (row: GenerationAlternativeRow): Promise<void> => {
    if (readOnly || busy || row.kind === 'current') return;
    // Equipment-count rows carry a REAL re-applied envelope (valid basis and
    // fingerprint) — only the project duty dossier is cleared by them.
    const isEquipmentCount = row.kind === 'equipment-count';
    const clearsAuthority = isEquipmentCount
      ? Boolean(input.projectDutyBasis)
      : Boolean(input.equipmentBasis || input.projectDutyBasis);
    if (clearsAuthority
      && !(await confirmDecision({
        title: isEquipmentCount ? 'Apply this block-count alternative?' : 'Apply this planning alternative?',
        body: isEquipmentCount
          ? 'Applying this block-count alternative keeps the vendor envelope but clears the project duty dossier, which is valid only for its documented block quantity.'
          : 'Applying this planning alternative will replace the fleet and clear the selected vendor envelope and project duty dossier. The BOM reference remains separate.',
        confirmLabel: 'Apply alternative',
        tone: 'danger',
      }))) return;
    const applied = structuredClone(row.input);
    applied.name = isEquipmentCount && row.equipmentModel
      ? `${site.name} · ${row.blockCount} × ${row.equipmentModel} · ${row.ccsEnabled ? 'CCS on' : 'CCS off'}`
      : `${site.name} · ${row.blockCount} × ${row.isoGrossMWPerUnit} MW · ${row.ccsEnabled ? 'CCS on' : 'CCS off'}`;
    setInput(applied);
    setSelectedId(null);
    setSelectedBase(null); setMainProjectDraft(false);
    setEquipmentApplyStatus(null);
    setProjectDutyApplyStatus(null);
    setAlternativeApplyStatus(isEquipmentCount && row.equipmentModel
      ? `Applied ${row.blockCount} × ${row.equipmentModel} · CCS ${row.ccsEnabled ? 'on' : 'off'} as the working basis.`
      : `Applied ${row.blockCount} × ${row.isoGrossMWPerUnit} MW ISO per block · CCS ${row.ccsEnabled ? 'on' : 'off'} as the working basis.`,
    );
  }, [busy, confirmDecision, input.equipmentBasis, input.projectDutyBasis, readOnly, site.name]);

  /**
   * Load a saved scenario into the working editor. ONE path shared by the
   * scenario picker and the comparison table, so the unsaved-work confirmation
   * and the automatic-evidence freeze can never differ between the two.
   *
   * `selectOnly` marks a scenario whose payload failed integrity validation:
   * it becomes the selected row (so it can be deleted) but its corrupt input is
   * never loaded into the editor.
   */
  const loadSavedScenario = useCallback(async (
    row: GenerationImpactScenarioSnapshot,
    options?: { selectOnly?: boolean },
  ): Promise<void> => {
    if (!options?.selectOnly) {
      const hasUnsavedWork = projectDutyDraftDirty || (!inputIsSaved && (selectedBase != null
        || recoveredWorkingDraftRef.current
        || inputFingerprint !== hashGenerationImpactValue(initialScreeningResolutionRef.current!.input)));
      if (hasUnsavedWork && !(await confirmDecision({
        title: 'Load this saved scenario?',
        body: 'The current unapplied or unsaved working changes will be discarded.',
        confirmLabel: 'Load scenario',
        tone: 'danger',
      }))) return;
      setInput(structuredClone(row.input));
      // A loaded scenario is a frozen record: never let automatic evidence
      // re-derivation mutate it out from under the user.
      setAutomaticEvidenceRefreshEnabled(false);
      setScenarioPickerOpen(false);
    }
    setSelectedId(row.id);
    setSelectedBase(row);
    setLayoutConflict(null);
    setLayoutPublishError(null);
    setPublishAttempt(null);
    setLayoutReviewOpen(false);
  }, [confirmDecision, inputFingerprint, inputIsSaved, projectDutyDraftDirty, selectedBase]);

  /**
   * Filename-screened document hints beside the three resource-limit inputs.
   *
   * A filename match is NEVER a number, so nothing is auto-filled — this only
   * answers "did I already upload the thing that would tell me this?", which is
   * the question a blank "Evidence input" field otherwise leaves the user to
   * answer from memory. Clicking opens the document in the Documents tab.
   */
  const resourceLimitDocuments = useMemo(
    () => findResourceLimitDocumentCandidates(documents),
    [documents],
  );
  const renderResourceLimitHints = (
    key: 'gas' | 'water' | 'co2',
    documented: boolean,
  ): React.JSX.Element | null => {
    const candidates = resourceLimitDocuments[key];
    if (documented || candidates.length === 0 || !onOpenDocuments) return null;
    return (
      <p className="mt-1 text-[9px] normal-case text-text-muted" data-testid={`generation-resource-limit-hint-${key}`}>
        Possibly relevant:{' '}
        {candidates.map((candidate, index) => (
          <React.Fragment key={candidate.documentId}>
            {index > 0 ? ', ' : ''}
            <button
              type="button"
              className="text-primary underline-offset-2 hover:underline"
              onClick={() => onOpenDocuments(candidate.documentId)}
            >
              {candidate.name}
            </button>
          </React.Fragment>
        ))}
      </p>
    );
  };

  /**
   * Re-arm automatic evidence refresh and pull the current evidence through
   * once.
   *
   * The flag is switched OFF whenever a scenario is loaded, saved or promoted
   * — correct, because a frozen record must not mutate under the user. But
   * nothing ever switched it back on, so from the first save onward a new SED
   * revision or a freshly extracted document simply never reached the
   * scenario, and no surface said so. This is the way back.
   *
   * `protected-assumption` fields (anything the user typed, and anything an
   * equipment or project-duty authority owns) are skipped by the resolver, so
   * this can never overwrite a deliberate entry.
   */
  const refreshFromEvidence = useCallback((): void => {
    if (readOnly) return;
    setAutomaticEvidenceRefreshEnabled(true);
    const resolution = input.siteId === site.id
      ? resolveGenerationScreeningPrefill({ input, site, sed, documents, dams })
      : initialScreeningResolution(site, sed, documents, dams);
    setScreeningEvidenceResolution(resolution);
    const changed = hashGenerationImpactValue(resolution.input) !== hashGenerationImpactValue(input);
    if (changed) setInput(resolution.input);
    toast.success(changed
      ? 'Refreshed the screening inputs from current site evidence.'
      : 'Screening inputs already match the current site evidence.');
  }, [dams, documents, input, readOnly, sed, site]);

  const applyEquipmentEnvelope = useCallback(async (catalogId: string): Promise<void> => {
    if (readOnly || busy) return;
    const replacesAuthority = Boolean(
      input.projectDutyBasis
      || (input.equipmentBasis && input.equipmentBasis.catalogId !== catalogId),
    );
    if (replacesAuthority
      && !(await confirmDecision({
        title: 'Apply a different vendor envelope?',
        body: 'Applying a different vendor envelope will replace the current equipment basis and clear the project duty dossier.',
        confirmLabel: 'Apply envelope',
        tone: 'danger',
      }))) return;
    try {
      const recorded = input.equipmentRatingConfirmations?.[catalogId];
      const applied = applyGenerationEquipmentEnvelope(
        input,
        catalogId,
        recorded ? { confirmation: recorded } : {},
      );
      // Applying a suggested envelope also picks it into the working set so
      // the selection survives with the scenario.
      applied.equipmentWorkspace = withShortlisted(applied.equipmentWorkspace, catalogId);
      setInput(applied);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setAlternativeApplyStatus(null);
      setProjectDutyApplyStatus(null);
      setEquipmentApplyStatus(
        `Applied ${applied.equipmentBasis?.manufacturer} ${applied.equipmentBasis?.model} as the sourced equipment screening basis.`,
      );
    } catch (error) {
      toast.error((error as Error).message || 'Equipment envelope could not be applied');
    }
  }, [busy, confirmDecision, input, readOnly]);

  // A `confirmationRequired` entry is reconciled, not overridden: the engineer
  // records the figure + its evidence, that attestation is frozen onto the
  // scenario, and only then is the envelope applied.
  const [reconcilingCatalogId, setReconcilingCatalogId] = useState<string | null>(null);
  const openReconcileDialog = useCallback((catalogId: string): void => {
    if (readOnly || busy) return;
    setReconcilingCatalogId(catalogId);
  }, [busy, readOnly]);

  const recordOemConfirmation = useCallback((
    confirmation: import('../../../types/generationImpact').GenerationEquipmentRatingConfirmation,
  ): void => {
    try {
      const confirmed = recordEquipmentRatingConfirmation(input, confirmation);
      const applied = applyGenerationEquipmentEnvelope(
        confirmed,
        confirmation.catalogId,
        { confirmation },
      );
      applied.equipmentWorkspace = withShortlisted(applied.equipmentWorkspace, confirmation.catalogId);
      setInput(applied);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setReconcilingCatalogId(null);
      setEquipmentApplyStatus(
        `Recorded ${confirmation.confirmedOutputMW} MW (${confirmation.confirmedRatingScope}) for `
        + `${applied.equipmentBasis?.manufacturer} ${applied.equipmentBasis?.model}, confirmed by ${confirmation.confirmedBy}.`,
      );
    } catch (error) {
      toast.error((error as Error).message || 'The OEM confirmation could not be recorded');
    }
  }, [input]);

  const toggleEquipmentShortlist = useCallback((catalogId: string, shortlisted: boolean): void => {
    if (readOnly || busy) return;
    setInput(current => ({
      ...current,
      equipmentWorkspace: shortlisted
        ? withShortlisted(current.equipmentWorkspace, catalogId)
        : {
            shortlistedCatalogIds: (current.equipmentWorkspace?.shortlistedCatalogIds ?? [])
              .filter(id => id !== catalogId),
            userEquipment: current.equipmentWorkspace?.userEquipment ?? [],
          },
    }));
  }, [busy, readOnly]);

  const addUserEquipment = useCallback((entry: GenerationUserEquipmentEntry): void => {
    if (readOnly || busy) return;
    setInput(current => ({
      ...current,
      equipmentWorkspace: {
        shortlistedCatalogIds: current.equipmentWorkspace?.shortlistedCatalogIds ?? [],
        userEquipment: [...(current.equipmentWorkspace?.userEquipment ?? []), entry],
      },
    }));
    setEquipmentApplyStatus(
      `Added ${entry.manufacturer} ${entry.model} as an unverified user-supplied equipment entry.`,
    );
  }, [busy, readOnly]);

  const removeUserEquipment = useCallback((entryId: string): void => {
    if (readOnly || busy) return;
    setInput(current => ({
      ...current,
      equipmentWorkspace: {
        shortlistedCatalogIds: current.equipmentWorkspace?.shortlistedCatalogIds ?? [],
        userEquipment: (current.equipmentWorkspace?.userEquipment ?? [])
          .filter(entry => entry.id !== entryId),
      },
    }));
  }, [busy, readOnly]);

  const applyUserEquipment = useCallback(async (entryId: string): Promise<void> => {
    if (readOnly || busy) return;
    const replacesAuthority = Boolean(input.projectDutyBasis || input.equipmentBasis);
    if (replacesAuthority
      && !(await confirmDecision({
        title: 'Apply unverified user equipment?',
        body: 'Applying user-supplied equipment will replace the sourced equipment basis and clear the project duty dossier. The scenario duty will rest on unverified user inputs until OEM data arrives.',
        confirmLabel: 'Apply user equipment',
        tone: 'danger',
      }))) return;
    try {
      const applied = applyGenerationUserEquipmentEnvelope(input, entryId);
      const entry = input.equipmentWorkspace?.userEquipment.find(candidate => candidate.id === entryId);
      setInput(applied);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setAlternativeApplyStatus(null);
      setProjectDutyApplyStatus(null);
      setEquipmentApplyStatus(
        `Applied ${entry?.manufacturer ?? ''} ${entry?.model ?? ''} as an unverified user-supplied screening duty.`.replace(/\s+/g, ' '),
      );
    } catch (error) {
      toast.error((error as Error).message || 'User equipment could not be applied');
    }
  }, [busy, confirmDecision, input, readOnly]);

  const bomCoverage = useMemo(
    () => summarizeBomEquipmentCoverage(
      site.supplyChainPlan,
      input.equipmentWorkspace,
      input.equipmentBasis?.catalogId,
    ),
    [input.equipmentBasis?.catalogId, input.equipmentWorkspace, site.supplyChainPlan],
  );
  // One-line summary for the collapsed BOM evidence row. Reference only —
  // mirrors the board's own alignment evaluation so the row and the expanded
  // board never disagree.
  const bomRowSummary = useMemo((): { label: string; detail: string; tone: string } => {
    const mw = (value: number | null): string => value == null ? 'no MW entered' : `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value)} MW net nameplate`;
    if (input.bomBasis) {
      const alignment = evaluateGenerationBomBasisAlignment(input.bomBasis, site.supplyChainPlan);
      const lines = input.bomBasis.items.length;
      const detail = `Frozen plan v${input.bomBasis.sourcePlanVersion} · ${lines} generation line${lines === 1 ? '' : 's'} · ${mw(input.bomBasis.totalNetNameplateMW)} · reference only, never a solver input.`;
      return alignment.alignmentStatus === 'aligned'
        ? { label: 'Linked · aligned', detail, tone: 'border-success/40 bg-success/10 text-success' }
        : { label: `Linked · ${alignment.alignmentStatus}`, detail: `${detail} The active BOM has changed since — refresh to re-freeze.`, tone: 'border-warning/40 bg-warning/10 text-warning' };
    }
    const liveLines = bomCoverage.generationLineCount;
    return liveLines > 0
      ? { label: 'Not linked', detail: `${liveLines} active generation line${liveLines === 1 ? '' : 's'} in the Equipment BOM. Link to freeze quantities, quotes, and lead times into this scenario as procurement provenance.`, tone: 'border-border text-text-muted' }
      : { label: 'No BOM lines', detail: 'No active generation procurement lines to reference. Calculations are unaffected either way.', tone: 'border-border text-text-muted' };
  }, [bomCoverage.generationLineCount, input.bomBasis, site.supplyChainPlan]);
  const openEquipmentImport = useCallback((tab: FinderSource | 'all'): void => {
    setImportTab(tab);
    setImportOpen(true);
  }, []);
  const closeAddEquipment = useCallback((): void => {
    setAddEquipmentOpen(false);
    setImportPrefill(null);
    setImportNonce(nonce => nonce + 1);
  }, []);
  const handleImportPrefill = useCallback((prefill: GenerationUserEquipmentPrefill): void => {
    setImportPrefill(prefill);
    setImportNonce(nonce => nonce + 1);
    setImportOpen(false);
    setAddEquipmentOpen(true);
  }, []);
  const handleImportShortlist = useCallback((catalogId: string): void => {
    toggleEquipmentShortlist(catalogId, true);
    const entry = GENERATION_EQUIPMENT_CATALOG.find(candidate => candidate.catalogId === catalogId);
    setEquipmentApplyStatus(entry
      ? `Added the reviewed ${entry.manufacturer} ${entry.model} envelope to your equipment set.`
      : 'Added the reviewed envelope to your equipment set.');
  }, [toggleEquipmentShortlist]);
  /** Shared per-row router: real envelope → shortlist; data → prefilled form;
   *  insufficient → explain + blank form (identity kept when known). */
  const routeEquipmentImport = useCallback((resolution: GenerationEquipmentImportResolution): void => {
    if (resolution.tier === 'catalog-envelope') {
      handleImportShortlist(resolution.catalogId);
      return;
    }
    if (resolution.tier === 'prefill-full' || resolution.tier === 'prefill-partial') {
      handleImportPrefill(resolution.prefill);
      return;
    }
    toast.warning(resolution.reasons.join(' '));
    if (resolution.identityPrefill) {
      handleImportPrefill({ ...resolution.identityPrefill });
    } else {
      setImportPrefill(null);
      setImportNonce(nonce => nonce + 1);
      setAddEquipmentOpen(true);
    }
  }, [handleImportPrefill, handleImportShortlist]);

  const resizeEquipmentFleet = useCallback(async (blockCount: number): Promise<void> => {
    if (readOnly || busy) return;
    if (blockCount === input.units.length) return;
    // Out-of-range used to `return` in silence: typing 65 blocks produced no
    // error, no state change and no explanation — the field simply snapped
    // back. Say what the bound is instead. The UI input, this guard and
    // `equipmentSelectionState` now share ONE bound.
    if (!Number.isInteger(blockCount)
      || blockCount < MIN_GENERATION_BLOCK_COUNT
      || blockCount > MAX_GENERATION_BLOCK_COUNT) {
      toast.warning(
        `Enter between ${MIN_GENERATION_BLOCK_COUNT} and ${MAX_GENERATION_BLOCK_COUNT} contingency blocks.`,
      );
      return;
    }
    // A CATALOG fleet carries its dossier across the resize (only the derived
    // block quantity and installed plant acreage move), so no confirmation is
    // owed. A user-supplied fleet still cannot bind one, so that path is still
    // destructive and still asks.
    const dossierSurvivesResize = Boolean(input.projectDutyBasis)
      && activeEquipmentSelection?.kind === 'catalog';
    if (input.projectDutyBasis
      && !dossierSurvivesResize
      && !(await confirmDecision({
        title: 'Resize the equipment fleet?',
        body: 'Changing the block count re-applies the equipment envelope and clears the project duty dossier, which is bound to a catalog envelope this fleet no longer carries.',
        confirmLabel: 'Resize fleet',
        tone: 'danger',
      }))) return;
    try {
      const selection = activeEquipmentSelection;
      const resized = resizeGenerationEquipmentFleet(input, blockCount);
      setInput(resized);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setAlternativeApplyStatus(null);
      setProjectDutyApplyStatus(resized.projectDutyBasis
        ? `Carried the ${resized.projectDutyBasis.basisName} dossier across to ${blockCount} block${blockCount === 1 ? '' : 's'}.`
        : null);
      setEquipmentApplyStatus(selection
        ? `Re-applied the ${selection.manufacturer} ${selection.model} envelope at ${blockCount} block${blockCount === 1 ? '' : 's'}.`
        : `Resized the equipment fleet to ${blockCount} block${blockCount === 1 ? '' : 's'}.`);
    } catch (error) {
      toast.error((error as Error).message || 'Equipment fleet could not be resized');
    }
  }, [activeEquipmentSelection, busy, confirmDecision, input, readOnly]);

  /**
   * Contingency-block count is a COMMITTED draft, not a live-bound input.
   *
   * It used to call `resizeEquipmentFleet` on every keystroke. With a project
   * dossier applied that opens a destructive confirm dialog per intermediate
   * value — clearing the field to retype "12" fired it at least twice, and each
   * dismissal left the fleet in whichever half-typed state the user had reached.
   * Committing on blur/Enter asks once, for the value the user meant.
   */
  const [blockCountDraft, setBlockCountDraft] = useState<string | null>(null);
  const commitBlockCount = useCallback((): void => {
    if (blockCountDraft == null) return;
    const raw = blockCountDraft.trim();
    setBlockCountDraft(null);
    if (raw === '') return;
    const count = numberValue(raw, input.units.length);
    if (count === input.units.length) return;
    if (activeEquipmentSelection) void resizeEquipmentFleet(count);
    else if (Number.isInteger(count)
      && count >= MIN_GENERATION_BLOCK_COUNT
      && count <= MAX_GENERATION_BLOCK_COUNT) rebuildUnits({ count });
    else toast.warning(`Enter between ${MIN_GENERATION_BLOCK_COUNT} and ${MAX_GENERATION_BLOCK_COUNT} contingency blocks.`);
  }, [activeEquipmentSelection, blockCountDraft, input.units.length, rebuildUnits, resizeEquipmentFleet]);

  // Assist › Add a generation block: grow the physical fleet by one through the
  // SAME sanctioned transaction as the Contingency-blocks input, so placements
  // and bindings are preserved atomically. The new block appears on the next
  // re-solve as `generation-block-{N+1}`, ready to accept an imported source.
  const addGenerationBlock = useCallback((): void => {
    if (readOnly || busy) return;
    const next = input.units.length + 1;
    if (activeEquipmentSelection) void resizeEquipmentFleet(next);
    else rebuildUnits({ count: next });
  }, [readOnly, busy, input.units.length, activeEquipmentSelection, resizeEquipmentFleet, rebuildUnits]);

  const applyProjectDuty = useCallback((draft: GenerationProjectDutyBasisDraft): void => {
    if (readOnly || busy) return;
    try {
      const applied = applyGenerationProjectDutyBasis(input, draft);
      setInput(applied);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setAlternativeApplyStatus(null);
      setProjectDutyApplyStatus(
        `Applied ${applied.projectDutyBasis?.basisName} as the catalog-bound project duty dossier.`,
      );
    } catch (error) {
      toast.error((error as Error).message || 'Project duty dossier could not be applied');
      throw error;
    }
  }, [busy, input, readOnly]);

  const clearProjectDuty = useCallback((): void => {
    if (readOnly || busy) return;
    const equipment = input.equipmentBasis;
    if (!equipment) throw new RangeError('No bound equipment catalog duty is available to restore.');
    try {
      const restored = applyGenerationEquipmentEnvelope(
        input,
        equipment.catalogId,
        { allowUnconfirmed: true },
      );
      setInput(restored);
      setSelectedId(null);
      setSelectedBase(null); setMainProjectDraft(false);
      setProjectDutyApplyStatus('Restored the immutable equipment catalog screening duty.');
    } catch (error) {
      toast.error((error as Error).message || 'Equipment catalog duty could not be restored');
      throw error;
    }
  }, [busy, input, readOnly]);

  const selectLayoutParcel = useCallback((targetParcelId: string): void => {
    if (readOnly || busy) return;
    const parcel = parcels.find(candidate => candidate.id === targetParcelId);
    setInput((current) => ({
      ...current,
      layout: {
        ...(current.layout ?? defaultLayout(site)),
        targetParcelId,
        schemaVersion: 2,
      },
      ...(parcel ? { layoutAnchor: parcel.centroid } : {}),
    }));
  }, [busy, parcels, readOnly, site]);

  // Unapplied project-duty dossier edits live in the editor's local form state,
  // so a scenario can look "saved" (input matches the snapshot) while a 40+ field
  // dossier draft sits unapplied. Every authority-advancing action — save, promote,
  // publish, and SED rebuild for Test Fit — must refuse to run until the draft is
  // applied or discarded, otherwise those edits are silently lost downstream.
  const guardProjectDutyDraft = useCallback((intent: string): boolean => {
    if (!projectDutyDraftDirty) return false;
    toast.warning(`Apply or discard the project duty dossier edits before ${intent}.`);
    // The dossier editor lives in the equipment panel — switch to it so the
    // guard points at a visible editor, then scroll it into view.
    setSelectedStage('equipment');
    const editor = document.getElementById('generation-project-duty-editor');
    if (typeof editor?.scrollIntoView === 'function') {
      editor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return true;
  }, [projectDutyDraftDirty]);

  /** One failure toast per input shape for autosave (see save()). */
  const autosaveFailedFingerprintRef = useRef<string | null>(null);
  /**
   * May a background save run right now? ONE predicate, read twice: the effect
   * below arms the debounce on it, and save() re-reads it through the ref at
   * fire time, because the timer runs after the render that armed it. Each
   * clause is justified in the effect's comment.
   */
  const autosaveEligible = !readOnly
    && !selectedIntegrityFailed
    && !projectDutyDraftDirty
    && operation === null
    && openConfirmDecisions === 0
    && selectedBase != null
    && selectedBase.id !== promotedScenarioId
    && selectedBase.id !== activeId
    && !selectedRemoteChanged
    && !inputIsSaved
    && !inputDiffersOnlyByName;
  const autosaveEligibleRef = useRef(autosaveEligible);
  autosaveEligibleRef.current = autosaveEligible;

  // A missing vendor sound level is fixed on the acoustic source schedule,
  // which lives with the equipment authority chain in Step 3 — never in the
  // Step 2 site envelope, which keeps only the receptor facts.
  const openAcousticSources = useCallback((): void => {
    setSelectedStage('equipment');
    const card = document.getElementById('generation-acoustic-source-schedule');
    if (typeof card?.scrollIntoView === 'function') {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const save = useCallback(async (opts?: { auto?: boolean }) => {
    if (readOnly || selectedIntegrityFailed) return;
    // Autosave stays SILENT on the dossier-draft guard — the manual path
    // warns and navigates; a background timer must not steal focus.
    if (opts?.auto) {
      if (!autosaveEligibleRef.current) return;
    } else if (guardProjectDutyDraft('saving the scenario')) return;
    setScenarioSaveFailed(false);
    // Saving by hand is the user declaring this evidence state theirs, so it
    // freezes automatic prefill — at the DECLARATION, not when the response
    // lands. Between the two, a SED revision or a document extraction would
    // otherwise rewrite `input` under the request, and the completion notice
    // below would report that machine edit back as the user's own. A background
    // write is not that declaration, so autosave leaves prefill running.
    // A write that FAILS declared nothing, so the freeze is lifted again in the
    // catch below — but only back to where it stood here: a save dispatched on
    // an already-paused record must not turn prefill back on.
    const evidenceRefreshWasEnabled = automaticEvidenceRefreshEnabledRef.current;
    if (!opts?.auto) setAutomaticEvidenceRefreshEnabled(false);
    setOperation('saving');
    const requestId = ++scenarioSaveRequestRef.current;
    const requestScope = {
      siteId: site.id,
      scenarioId: selectedBase?.id ?? 'draft',
      revision: saveScopeRef.current.revision,
    };
    /**
     * May the landed write's IDENTITY still be adopted? Only a superseding
     * request or a site change can retire it — never a selection change, which
     * moves where the identity lands, not whether it is real.
     */
    const identityApplies = (): boolean => (
      scenarioSaveRequestRef.current === requestId
      && saveScopeRef.current.siteId === requestScope.siteId
    );
    /** …and the workbench is still pointed at the row that was written. */
    const isCurrentScope = (): boolean => (
      identityApplies()
      && saveScopeRef.current.scenarioId === requestScope.scenarioId
      && saveScopeRef.current.revision === requestScope.revision
    );
    try {
      const current = selectedBase;
      const savesNewRevision = current?.id === promotedScenarioId;
      const data = await callUserFunction<{ scenario: GenerationImpactScenarioSnapshot }>('saveGenerationImpactScenario', {
        siteId: site.id,
        ownerUid: site._ownerUid,
        input,
        requestId,
        inputFingerprint,
        draftId: requestScope.scenarioId,
        ...(!savesNewRevision && current
          ? { scenarioId: current.id, expectedUpdatedAt: current.updatedAt }
          : {}),
      });
      // The write LANDED. If the user typed on, or moved to another scenario,
      // while it was in flight, that changes only where the response applies —
      // the newer input is still unsaved, and the other scenario keeps its own
      // base — but the server's identity, id and updatedAt, is adopted either
      // way, for the row it was written FOR. Dropping it strands the created
      // scenario (the next save mints a duplicate) or pins `expectedUpdatedAt`
      // behind the server's, which wedges that row: every later save of it,
      // manual included, fails as a conflict with nothing on screen to say why.
      if (!identityApplies()) return;
      if (!data.scenario
        || typeof data.scenario.id !== 'string'
        || !data.scenario.input
        || !data.scenario.result
        || typeof data.scenario.inputHash !== 'string'
        || typeof data.scenario.resultHash !== 'string') {
        throw new Error('Scenario save returned an incomplete immutable snapshot');
      }
      // Capture placement before adopting the returned identity. A first save
      // moves the selected scope from `draft` to the persisted ID (and saving a
      // promoted row similarly lands on a new draft revision). That transition
      // is this response's own work, not the user navigating away. After an
      // awaited rail refresh, accept either side of only that transition; a
      // genuinely different selected ID still suppresses row-scoped feedback.
      const responseWasForCurrentScope = isCurrentScope();
      const adoptedScopeRevision = requestScope.revision
        + (data.scenario.id === requestScope.scenarioId ? 0 : 1);
      const landedRowRemainsCurrent = (): boolean => (
        identityApplies()
        && responseWasForCurrentScope
        && (
          (saveScopeRef.current.scenarioId === requestScope.scenarioId
            && saveScopeRef.current.revision === requestScope.revision)
          || (saveScopeRef.current.scenarioId === data.scenario.id
            && saveScopeRef.current.revision === adoptedScopeRevision)
        )
      );
      // The response is authoritative for the row it just wrote, so the rail
      // adopts it here rather than waiting on the refresh below — and it adopts
      // it whatever is selected now, because that row is where a later save of
      // this scenario reads its `expectedUpdatedAt` from. A refresh that fails
      // would otherwise leave the PRE-save row in `saved`, and the updatedAt gap
      // against the new base reads as a remote change: a false publish blocker
      // that also stands autosave down for the rest of the session. Server order
      // is updatedAt desc, so this row leads.
      setSaved((rows) => [data.scenario, ...rows.filter((row) => row.id !== data.scenario.id)]);
      if (responseWasForCurrentScope) {
        setSelectedId(data.scenario.id);
        setSelectedBase(data.scenario);
        // Clearing the layout conflict stays on both the auto and the manual
        // path: it reports a scenario that moved under an open layout review,
        // which this save just resolved. It is scoped to the selection because
        // that review is about the selection, not about the row written.
        setLayoutConflict(null);
      }
      await loadScenarios();
      // The failing-shape memo belongs to the row the user is LOOKING at, not
      // to the row this response was written for. A write landing for another
      // scenario says nothing about the shape failing here, and clearing the
      // memo on it releases one more doomed request and its toast.
      if (landedRowRemainsCurrent()) autosaveFailedFingerprintRef.current = null;
      if (!opts?.auto && landedRowRemainsCurrent()) {
        // The write landed for the shape that was SENT. If the user typed on
        // while it was in flight those edits are still unsaved, so a bare
        // "saved" would sit next to the Unsaved-changes pill contradicting it.
        // Report the write, and say what it does not cover.
        const announcement = savesNewRevision
          ? 'Saved as a new draft revision; the promoted scenario remains frozen'
          : 'Generation scenario saved';
        toast.success(generationScenarioInputMatches(liveInputRef.current, data.scenario)
          ? announcement
          : `${announcement}. Edits made while it was saving are still unsaved.`);
      }
    } catch (error) {
      if (isCurrentScope()) {
        setScenarioSaveFailed(true);
        // One failure toast per input shape for autosave — a background retry
        // loop hammering the same error would be noise, not information.
        if (opts?.auto) autosaveFailedFingerprintRef.current = inputFingerprint;
        // Nothing was written, so nothing was declared: give the user back the
        // automatic prefill this save's dispatch froze rather than leaving a
        // failed write holding a setting they never changed. Scoped with the
        // toast above — a failure that is not this scope's to report is not
        // this scope's to undo either.
        if (!opts?.auto && evidenceRefreshWasEnabled) setAutomaticEvidenceRefreshEnabled(true);
        toast.error((error as Error).message || 'Scenario save failed');
      }
    } finally {
      if (scenarioSaveRequestRef.current === requestId) setOperation(null);
    }
  }, [guardProjectDutyDraft, input, inputFingerprint, loadScenarios, promotedScenarioId, readOnly, selectedBase, selectedIntegrityFailed, site._ownerUid, site.id]);

  /*
   * AUTOSAVE (2026-08-25, William: "does it auto save, it should"). The local
   * draft is crash protection, not persistence — this is the real save,
   * debounced off the input. It UPDATES the selected scenario, and only that:
   *   - never without a selection. The first save mints the record under the
   *     name the user chose, so it stays manual — and because Delete, Clone,
   *     apply-alternative and a restored crash draft all leave the selection
   *     null, none of them can mint a scenario behind the user's back either;
   *   - NEVER the promoted scenario: saving it forks a new draft revision,
   *     which must stay a deliberate manual act. That takes both clauses — a
   *     restored draft of promoted edits comes back with no selection at all,
   *     so an id comparison on its own is vacuous;
   *   - never while read-only, integrity-failed, another operation runs, a
   *     confirm dialog is awaiting an answer, or the row changed elsewhere
   *     (that save is server-rejected, and the toast would be unprompted);
   *   - never over unapplied dossier edits (silent — no focus stealing);
   *   - never for a rename alone (see `inputDiffersOnlyByName`);
   *   - one attempt per failing input shape (see save()).
   * Manual Save stays — it is the create path, the promoted-fork path, and the
   * immediate path.
   */
  useEffect(() => {
    if (!autosaveEligible) return;
    if (autosaveFailedFingerprintRef.current === inputFingerprint) return;
    const timer = setTimeout(() => { void save({ auto: true }); }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [autosaveEligible, inputFingerprint, save]);

  const requestSedRebuild = useCallback(async (
    afterPublish = false,
    publicationId = layoutWorkspace?.active?.publicationId,
  ): Promise<void> => {
    if (readOnly || (!afterPublish && operation !== null)) return;
    if (!afterPublish && guardProjectDutyDraft('rebuilding the SED for Test Fit')) return;
    if (!publicationId) {
      toast.warning('Publish a layout before rebuilding the SED for Test Fit.');
      return;
    }
    if (sedRebuildPublicationId === publicationId) return;
    const request = onSedRebuildRequested ?? onLayoutPublished;
    if (!request) {
      toast.warning('The layout is published, but this view cannot start the SED rebuild. Refresh the site and rebuild from Site Evidence.');
      return;
    }
    setSedRebuildPublicationId(publicationId);
    try {
      await request();
      toast.info('SED rebuild requested. Test Fit will unlock after the live authority snapshot is complete.');
    } catch (error) {
      setSedRebuildPublicationId(null);
      toast.warning(`The layout remains published, but the SED rebuild could not start: ${(error as Error).message || 'unknown error'}`);
    }
  }, [guardProjectDutyDraft, layoutWorkspace?.active?.publicationId, onLayoutPublished, onSedRebuildRequested, operation, readOnly, sedRebuildPublicationId]);

  // MAX-POWER STAMP — writes the envelope figure into Project Intent as the
  // project's power requirement. The server RE-RUNS the study from the promoted
  // scenario; nothing computed in this component is persisted, so a client-side
  // study can never become the site's capacity of record.
  const [maxPowerStudy, setMaxPowerStudy] = useState<
    import('../../../types/generationImpact').GenerationMaxPowerStudySnapshot | null
  >(null);
  const [maxPowerBusy, setMaxPowerBusy] = useState(false);
  /**
   * Run the sweep for the Studio rail. Same inputs as the capacity-stage panel —
   * one code path, so the two surfaces can never quote different max powers for
   * the same layout. Deferred a frame so the busy state paints first.
   */
  const runMaxPowerForStudio = useCallback((): void => {
    setMaxPowerBusy(true);
    window.setTimeout(() => {
      try {
        setMaxPowerStudy(buildGenerationMaxPowerStudySnapshot(
          {
            input,
            parcels: layoutParcels(site),
            layout: input.layout,
            fallbackAnchor: input.layoutAnchor ?? site.siteCentroid ?? site.location,
            // Read inline rather than via `parcelAreaAcres`, which is declared
            // further down the component.
            fallbackEnvelopeAcres: site.siteBoundaryAggregate?.totalArea ?? site.siteArea ?? undefined,
          },
          intentPowerContext,
        ));
      } finally {
        setMaxPowerBusy(false);
      }
    }, 30);
  }, [input, intentPowerContext, site]);

  const stampMaxPower = useCallback(async (): Promise<void> => {
    const current = selectedBase;
    if (!current || readOnly || !maxPowerStudy?.maximum) return;
    if (promotedScenarioId !== current.id) {
      toast.error('Promote this scenario to Project Intent before stamping a max-power figure.');
      return;
    }
    const max = maxPowerStudy.maximum;
    const qualifier = maxPowerStudy.boundQuality === 'search-ceiling-reached'
      ? 'at least '
      : maxPowerStudy.boundQuality === 'upper-bound-undocumented-constraints'
        ? 'no more than '
        : '';
    // PHASED sites: the stamp also lands on the ULTIMATE phase's cumulative
    // capacity (see buildMaxPowerIntentStamp) — say so before the user
    // confirms, naming the phase it will rewrite.
    const phasedView = effectivePhases(site.projectIntent);
    const ultimatePhase = phasedView.isPhased
      ? phasedView.phases[phasedView.phases.length - 1]
      : null;
    const confirmed = await confirmDecision({
      title: 'Stamp the max-power figure onto Project Intent?',
      body: (
        <span className="block space-y-1">
          <span className="block">Power requirement → {qualifier}{max.facilityMW.toFixed(1)} MW facility</span>
          {ultimatePhase && (
            <span className="block">
              Final phase &ldquo;{ultimatePhase.label || `Phase ${ultimatePhase.sequence}`}&rdquo; capacity → {max.facilityMW.toFixed(1)} MW (cumulative ultimate)
            </span>
          )}
          <span className="block">From {max.firmNetMW.toFixed(1)} MW firm generation ({max.blockCount} × {max.isoGrossMWPerBlock.toFixed(0)} MW)</span>
          <span className="block text-text-muted">{maxPowerStudy.bindingStatement}</span>
          <span className="block text-text-muted">
            The figure is recorded as DERIVED, not declared — the server recomputes the study before saving.
          </span>
        </span>
      ),
      confirmLabel: 'Stamp to intent',
    });
    if (!confirmed) return;
    setOperation('promoting');
    try {
      await callUserFunction<import('../../../types/generationImpact').StampMaxPowerStudyOutput>(
        'stampMaxPowerStudy',
        { siteId: site.id, ownerUid: site._ownerUid, scenarioId: current.id, expectedUpdatedAt: current.updatedAt },
      );
      toast.success('Max-power figure stamped onto Project Intent.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not stamp the max-power figure.');
    } finally {
      setOperation(null);
    }
  }, [confirmDecision, maxPowerStudy, promotedScenarioId, readOnly, selectedBase, site._ownerUid, site.id, site.projectIntent]);

  const actOnScenario = useCallback(async (
    action: 'promoteGenerationImpactScenario' | 'deleteGenerationImpactScenario',
  ): Promise<void> => {
    const current = selectedBase;
    if (!current || readOnly) return;
    if (current.integrity?.valid === false && action !== 'deleteGenerationImpactScenario') {
      toast.error('This scenario failed integrity validation and can only be deleted.');
      return;
    }
    // Deletion is always permitted; promotion freezes the scenario into Project
    // Intent, so unapplied dossier edits must be resolved first.
    if (action === 'promoteGenerationImpactScenario' && guardProjectDutyDraft('promoting the scenario')) return;
    if (action === 'promoteGenerationImpactScenario') {
      const grossMW = current.input.units.reduce((sum, unit) => sum + unit.count * unit.isoGrossMWPerUnit, 0);
      const gridFeedMW = current.input.units.filter(unit => unit.technology === 'grid-import')
        .reduce((sum, unit) => sum + unit.count * unit.isoGrossMWPerUnit, 0);
      const nonGridMW = grossMW - gridFeedMW;
      const confirmed = await confirmDecision({
        title: `Promote “${current.name}” to Project Intent?`,
        body: (
          <span className="block space-y-1">
            {(nonGridMW > 0 || gridFeedMW === 0) && (
              <span className="block">Selected phase non-grid equipment capacity → {nonGridMW.toFixed(1)} MW</span>
            )}
            {gridFeedMW > 0 && (
              <>
                <span className="block">Aggregate grid-feed hardware → {gridFeedMW.toFixed(1)} MW</span>
                <span className="block text-text-muted">Your declared grid-import allocation is preserved. Feeder ratings do not establish customer service capacity; an unknown allocation stays unknown.</span>
              </>
            )}
            <span className="block">Grid mode → {current.input.gridConnection}</span>
            <span className="block">Promoted scenario ID → {current.id}</span>
            <span className="block text-text-muted">The selected phase power-source mix will update. Other declared Project Intent inputs are preserved.</span>
          </span>
        ),
        confirmLabel: 'Promote',
      });
      if (!confirmed) return;
    }
    setOperation(action === 'promoteGenerationImpactScenario' ? 'promoting' : 'deleting');
    try {
      const response = await callUserFunction<import('../../../types/generationImpact').PromoteGenerationImpactScenarioOutput | {
        promotedScenarioId?: string;
        promotedScenario?: GenerationImpactScenarioSnapshot;
      }>(action, {
        siteId: site.id,
        ownerUid: site._ownerUid,
        scenarioId: current.id,
        expectedUpdatedAt: current.updatedAt,
        ...(action === 'promoteGenerationImpactScenario' ? { expectedDecisionHash: intentDecisionStateHash(site) } : {}),
        // Carry the candidate set this decision was made from. Promotion is a
        // funnel to one authority; without this the fact that a comparison
        // happened is destroyed at the boundary and the report can only ever
        // record the winner.
        ...(action === 'promoteGenerationImpactScenario' && compareIds.length > 0
          ? { comparedScenarioIds: compareIds }
          : {}),
      });
      if (action === 'promoteGenerationImpactScenario') {
        const frozen = response.promotedScenario ?? current;
        setPromotedScenarioId(response.promotedScenarioId ?? frozen.id);
        setPromotedScenario(frozen);
        setSelectedId(frozen.id);
        setSelectedBase(frozen);
        setInput(structuredClone(frozen.input));
        setAutomaticEvidenceRefreshEnabled(false);
      }
      if (action === 'deleteGenerationImpactScenario') {
        const resetResolution = initialScreeningResolution(site, sed, documents, dams);
        setSelectedId(null);
        setSelectedBase(null); setMainProjectDraft(false);
        setInput(resetResolution.input);
        setScreeningEvidenceResolution(resetResolution);
        setAutomaticEvidenceRefreshEnabled(true);
        setPublishAttempt(null);
        setLayoutReviewOpen(false);
      }
      await loadScenarios();
      if (action === 'promoteGenerationImpactScenario') {
        toast.success('Promoted to Project Intent. Review and publish this exact scenario layout next.');
        try {
          await onWorkflowAuthorityChanged?.();
        } catch (error) {
          toast.warning(`Promotion is saved, but the site status could not refresh: ${(error as Error).message || 'unknown error'}`);
        }
      } else {
        toast.success('Scenario deleted');
      }
    } catch (error) {
      toast.error((error as Error).message || 'Scenario operation failed');
    } finally {
      setOperation(null);
    }
  }, [compareIds, confirmDecision, dams, documents, guardProjectDutyDraft, loadScenarios, onWorkflowAuthorityChanged, readOnly, sed, selectedBase, site]);

  const publishLayout = useCallback(async (): Promise<void> => {
    const current = selectedBase;
    if (!current || publishBlockReason || readOnly) return;
    if (guardProjectDutyDraft('publishing the layout')) return;
    const expectedLayoutVersion = layoutWorkspace?.active?.version ?? 0;
    const matchesAttempt = publishAttempt
      && publishAttempt.scenarioId === current.id
      && publishAttempt.scenarioUpdatedAt === current.updatedAt
      && publishAttempt.inputHash === current.inputHash
      && publishAttempt.resultHash === current.resultHash
      && publishAttempt.expectedLayoutVersion === expectedLayoutVersion;
    const attempt: PendingPublishAttempt = matchesAttempt
      ? publishAttempt
      : {
          requestId: newLayoutPublishRequestId(),
          scenarioId: current.id,
          scenarioUpdatedAt: current.updatedAt,
          inputHash: current.inputHash,
          resultHash: current.resultHash,
          expectedLayoutVersion,
        };
    setPublishAttempt(attempt);
    setLayoutPublishError(null);
    setOperation('publishing');
    try {
      const response = await callUserFunction<PublishGenerationImpactLayoutOutput>('publishGenerationImpactLayout', {
        siteId: site.id,
        ownerUid: site._ownerUid,
        scenarioId: current.id,
        requestId: attempt.requestId,
        expectedLayoutVersion: attempt.expectedLayoutVersion,
        expectedScenario: {
          updatedAt: current.updatedAt,
          inputHash: current.inputHash,
          resultHash: current.resultHash,
        },
      });
      setLayoutWorkspace(response.layout);
      setLayoutWorkspaceLoaded(true);
      setPublishAttempt(null);
      setLayoutConflict(null);
      setLayoutPublishError(null);
      setLayoutReviewOpen(false);
      await loadScenarios();
      toast.success(response.outcome === 'already-published'
        ? `Layout v${response.head.version} is already published to Site Map`
        : `Layout v${response.head.version} published to Site Map`);
      try {
        await onWorkflowAuthorityChanged?.();
      } catch (error) {
        toast.warning(`Layout v${response.head.version} is published, but the site status could not refresh: ${(error as Error).message || 'unknown error'}`);
      }
      if (response.outcome === 'published' && response.sedRebuildRequired) {
        await requestSedRebuild(true, response.head.publicationId);
      }
    } catch (error) {
      if (error instanceof UserFunctionError && error.status === 409) {
        setPublishAttempt(null);
        setLayoutPublishError(null);
        setLayoutReviewOpen(false);
        await loadScenarios();
        setLayoutConflict('The saved scenario or Site Map layout changed while this review was open. Your working draft was kept; review the latest published version before trying again.');
        toast.warning('Layout changed during review. Refresh complete; review the latest version before publishing.');
      } else {
        const message = (error as Error).message || 'Layout publish failed';
        setLayoutPublishError(`${message} The same publish request can be retried safely.`);
        toast.error(message);
      }
    } finally {
      setOperation(null);
    }
  }, [guardProjectDutyDraft, layoutWorkspace?.active?.version, loadScenarios, onWorkflowAuthorityChanged, publishAttempt, publishBlockReason, readOnly, requestSedRebuild, selectedBase, site.id, site._ownerUid]);

  const capacity = result.capacity as typeof result.capacity & {
    contingencyOutages?: number;
    contingencyNetMW?: number;
    contingencyMarginMW?: number;
    contingencyReserveMarginMW?: number;
    contingencyVerdict?: typeof result.capacity.nMinusOneVerdict;
    contingencyReserveVerdict?: typeof result.capacity.nMinusOneReserveVerdict;
  };
  const contingencyOutages = capacity.contingencyOutages ?? input.contingencyOutages ?? 1;
  const criterionLabel = contingencyOutages === 0 ? 'N' : `N-${contingencyOutages}`;
  const contingencyNetMW = capacity.contingencyNetMW ?? capacity.nMinusOneNetMW;
  const contingencyMarginMW = capacity.contingencyMarginMW ?? capacity.nMinusOneMarginMW;
  const contingencyReserveMarginMW = capacity.contingencyReserveMarginMW ?? capacity.nMinusOneReserveMarginMW;
  const contingencyPass = (capacity.contingencyVerdict ?? capacity.nMinusOneVerdict) === 'pass';
  const reservePass = (capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict) === 'pass';
  // Joint screen verdict: the headline contingency verdict alone reads 'pass'
  // while the reserve-padded requirement fails; the statement names the
  // binding test so load-pass/reserve-fail surfaces as conditional.
  const capacityScreen = resolveCapacityScreenVerdict({
    criterion: criterionLabel,
    loadVerdict: capacity.contingencyVerdict ?? capacity.nMinusOneVerdict,
    reserveVerdict: capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict,
    loadMarginMW: contingencyMarginMW,
    reserveMarginMW: contingencyReserveMarginMW,
  });
  /**
   * SITE DESIGN DRY BULB — the ambient the project duty is corrected to.
   *
   * `climate.peakTemp` is the intended basis, but it is frequently absent while
   * the SAME climate section carries a full monthly summary. Falling straight
   * to NaN discarded that and pushed the NaN into the ambient interpolation.
   *
   * The fallback is the hottest monthly maximum — a design dry bulb is a peak,
   * so the peak of the monthly maxima is the honest reading of that record, not
   * a new estimate. It is LABELLED as a fallback rather than presented as the
   * recorded peak, because the two are not the same measurement.
   */
  const sedPeakDryBulb = sed?.climate?.data?.peakTemp;
  const monthlyPeakDryBulb = useMemo(() => {
    const maxima = (sed?.climate?.data?.monthlySummary ?? [])
      .map(month => month.maxTemp)
      .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    return maxima.length > 0 ? Math.max(...maxima) : null;
  }, [sed?.climate?.data?.monthlySummary]);
  const designDryBulbC = typeof sedPeakDryBulb === 'number'
    ? sedPeakDryBulb
    : monthlyPeakDryBulb ?? Number.NaN;
  const designDryBulbBasis: 'sed-peak' | 'monthly-maximum' | 'missing' =
    typeof sedPeakDryBulb === 'number'
      ? 'sed-peak'
      : monthlyPeakDryBulb != null ? 'monthly-maximum' : 'missing';
  const projectAbsoluteWater = input.projectDutyBasis?.water?.kind === 'absolute-design-day';
  const projectAbsolutePlantArea = input.footprint.installedPlantAcres != null;
  const projectAcousticSources = input.projectDutyBasis?.acoustics != null;
  const projectInstalledReservations = input.projectDutyBasis?.installedReservations;
  const projectCoolingEnvelope = projectInstalledReservations?.coolingEnvelope != null;
  const projectSwitchyardEnvelope = projectInstalledReservations?.switchyardEnvelope != null;
  const projectCcsCaptureEnvelope = projectInstalledReservations?.ccsCaptureEnvelope != null;
  const projectCcsCompressionEnvelope = projectInstalledReservations?.ccsCompressionEnvelope != null;
  const projectDutyLockLabels = [
    projectAbsoluteWater ? 'absolute design-day generation water' : null,
    projectAbsolutePlantArea ? 'generation-block installed area' : null,
    projectCoolingEnvelope ? 'cooling L × W envelope' : null,
    projectSwitchyardEnvelope ? 'switchyard L × W envelope' : null,
  ].filter((label): label is string => label != null);
  // Who currently owns the per-block performance / water / land figures. The
  // chain is planning defaults → vendor envelope → project dossier; the
  // Step 3 "Planning technology defaults" group discloses which link is live so
  // a user never tunes a default that an applied envelope has already replaced.
  const planningDefaultsAuthority: { kind: 'planning' | 'vendor' | 'dossier'; label: string; note: string } = input.projectDutyBasis
    ? {
        kind: 'dossier',
        label: `Superseded · ${input.projectDutyBasis.basisName}`,
        note: `The applied project dossier owns the performance, water and installed-area figures it documents; fields it supplies are locked below. Remaining fields still come from the ${activeEquipmentSelection ? `${activeEquipmentSelection.manufacturer} ${activeEquipmentSelection.model} envelope` : 'planning defaults'}.`,
      }
    : activeEquipmentSelection
      ? {
          kind: 'vendor',
          label: `Superseded · ${activeEquipmentSelection.model}`,
          note: `The applied ${activeEquipmentSelection.manufacturer} ${activeEquipmentSelection.model} envelope set these values from ${activeEquipmentSelection.kind === 'user' ? 'the user-supplied entry' : 'the OEM catalog'}. Editing one here marks the envelope "Modified after apply" in Equipment selection; re-apply the envelope to restore it.`,
        }
      : {
          kind: 'planning',
          label: 'In effect · no vendor applied',
          note: 'Generic planning figures in effect until a vendor envelope is applied above. Step 4 sizes the fleet with these numbers; a suggestion or applied envelope replaces them.',
        };
  /**
   * Parcel acreage for the design-basis strip and the layout fallback envelope.
   *
   * Drawn site geometry stays authoritative — it is what the test fit actually
   * packs against. The SED parcel section is a last-resort fallback so a site
   * whose acreage arrived through evidence rather than a drawn boundary reads
   * its real area instead of "Missing"; `parcelAreaSource` keeps the provenance
   * label honest about which one is in play.
   */
  const sedParcelAcres = typeof sed?.parcel?.data?.acres === 'number'
    && Number.isFinite(sed.parcel.data.acres)
    && sed.parcel.data.acres > 0
    ? sed.parcel.data.acres
    : undefined;
  // A project dossier binds to whichever equipment authority is applied — an
  // immutable catalog envelope OR a user-supplied entry. The user path exists so
  // the machine actually being procured (a BOM line with a quote against it) can
  // carry its own vendor data; it inherits that entry's unverified standing
  // rather than laundering it into catalog provenance.
  const dossierBindable = Boolean(activeEquipmentSelection);
  const parcelAreaAcres = site.siteBoundaryAggregate?.totalArea ?? site.siteArea ?? sedParcelAcres;
  const parcelAreaSource = (site.siteBoundaryAggregate?.totalArea ?? site.siteArea) != null
    ? 'Site map'
    : sedParcelAcres != null ? 'SED parcel' : 'Site map';
  // Site grid frequency from the geocoded address country; null = unknown
  // (mismatch warnings suppressed rather than guessed).
  const siteGridFrequencyHz = useMemo(
    () => expectedGridFrequencyHzFromAddress(site.address),
    [site.address],
  );
  const requirementsComplete = Boolean(
    input.name.trim()
    && input.facilityLoadMW > 0
    && (input.criticalLoadMW ?? input.facilityLoadMW) > 0
    && input.annualOperatingHours >= 0
    && input.annualOperatingHours <= 8760,
  );
  const capacityComplete = contingencyPass && reservePass;
  // Impact findings split by consequence: only red-flag risks hold the guided
  // workflow at the impacts stage. Amber risks, planning warnings, and data gaps
  // are advisory — they are surfaced but never permanently block Parcel/Handoff.
  const impactBlockingRiskCount = result.risks.filter(risk => risk.severity === 'red').length;
  const impactAdvisoryCount = result.gaps.length
    + result.warnings.length
    + result.risks.filter(risk => risk.severity === 'amber').length;
  const unresolvedImpactCount = impactBlockingRiskCount + impactAdvisoryCount;
  const impactsBlocking = impactBlockingRiskCount > 0;
  const impactsComplete = unresolvedImpactCount === 0;
  const parcelComplete = testFit.status === 'fits';
  // Guided-workflow derivations (pure projections of already-solved results).
  const equipmentSuggestion = useMemo(
    () => (isGridOnly || input.equipmentBasis || readOnly
      ? null
      // The whole scenario input satisfies the constraint-context Pick, so the
      // suggestion is ranked against the documented site envelope.
      : suggestGenerationEquipmentBasis(equipmentRows, criterionLabel, input)),
    [criterionLabel, equipmentRows, input, isGridOnly, readOnly],
  );
  // Site-envelope documentation gate: the constraints that make equipment
  // suggestions testable. Gas only matters for gas technologies. `requested`
  // (a utility data request is pending) never satisfies the gate — only a
  // documented value does.
  const siteLimitDocumentation = deriveSiteLimitDocumentation(input, isGasTechnology);
  const constraintsDocumented = (isGasTechnology ? input.availableGasCapacityDthPerDay != null : true)
    && input.availableWaterConsumptionGalPerDay != null
    && input.receptors.length > 0;
  // Every gating limit that is still missing carries a pending utility request.
  const allMissingLimitsRequested = !constraintsDocumented
    && (['gas', 'water', 'receptor'] as const).every((key) =>
      siteLimitDocumentation[key] !== 'undocumented');
  const workflowStages: GenerationWorkflowStages = [
    {
      id: 'requirements',
      label: 'Requirements',
      status: requirementsComplete ? 'Duty defined' : 'Inputs required',
      tone: requirementsComplete ? 'complete' : 'current',
    },
    {
      id: 'impacts',
      label: 'Site envelope',
      status: impactsComplete
        ? 'Checks clear'
        : impactsBlocking
          ? `${impactBlockingRiskCount} critical risk${impactBlockingRiskCount === 1 ? '' : 's'}`
          : constraintsDocumented
            ? `${impactAdvisoryCount} advisory item${impactAdvisoryCount === 1 ? '' : 's'}`
            : allMissingLimitsRequested
              ? 'Utility responses pending'
              : 'Constraints undocumented',
      tone: impactsComplete ? 'complete' : impactsBlocking ? 'blocked' : 'attention',
    },
    {
      id: 'equipment',
      label: 'Equipment evidence',
      status: isGridOnly
        ? 'Grid feed basis declared'
        : input.projectDutyBasis
        ? 'Project duty applied'
        : input.equipmentBasis
          ? 'Vendor basis selected'
          : 'Vendor basis required',
      tone: isGridOnly
        ? 'attention'
        : input.projectDutyBasis
        ? 'complete'
        : input.equipmentBasis
          ? 'attention'
          : requirementsComplete
            ? 'current'
            : 'blocked',
    },
    {
      id: 'capacity',
      label: 'Capacity & resilience',
      status: capacityComplete ? `${criterionLabel} duty met` : `${criterionLabel} margin open`,
      tone: capacityComplete ? 'complete' : requirementsComplete ? 'current' : 'blocked',
    },
    {
      id: 'parcel',
      label: 'Parcel fit',
      status: parcelComplete ? 'Contained' : testFit.status === 'partial' ? 'Fit issue' : 'Needs geometry',
      tone: parcelComplete ? 'complete' : testFit.status === 'partial' ? 'attention' : 'neutral',
    },
    {
      id: 'handoff',
      label: 'Publish & handoff',
      status: testFitReadiness.ready ? 'Ready for Test Fit' : testFitReadiness.title,
      tone: testFitReadiness.ready
        ? 'complete'
        : testFitReadiness.code === 'scenario-conflict' || testFitReadiness.code === 'sed-failed'
          ? 'blocked'
          : 'neutral',
    },
  ];
  // Guided chain, equipment-first: document the site envelope so suggestions
  // are constraint-tested, pick equipment, then prove capacity with it. The
  // envelope stage returns at the end only for red (blocking) resource risks.
  const recommendedStage: GenerationWorkflowStageId = !requirementsComplete
    ? 'requirements'
    : !constraintsDocumented && !input.equipmentBasis && !isGridOnly
      ? 'impacts'
      : !input.equipmentBasis && !isGridOnly
        ? 'equipment'
        : !capacityComplete
          ? 'capacity'
          : impactsBlocking
            ? 'impacts'
            : !parcelComplete
              ? 'parcel'
              : 'handoff';
  // Pin the initial panel to the first recommendation ONCE (the Path-to-Go
  // "first open stage" anchor). Afterwards the user owns navigation — a
  // completion flip mid-typing must never yank the view to another panel.
  useEffect(() => {
    setSelectedStage((current) => current ?? initialStage ?? recommendedStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only pin
  }, []);
  const activeStage = selectedStage ?? recommendedStage;
  useEffect(() => {
    if (!initialStage) return;
    setSelectedStage(initialStage);
    const frame = requestAnimationFrame(() => {
      const panel = document.getElementById(`generation-stage-${initialStage}`);
      if (!panel) return;
      panel.tabIndex = -1;
      panel.focus({ preventScroll: true });
      const reduceMotion = typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      panel.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialStage]);
  // Exact per-count screens are ~15 solves; compute them only while the
  // capacity panel is the visible one and an equipment selection is active.
  const equipmentBlockSizing = useMemo(
    () => (activeStage === 'capacity' && activeEquipmentSelection
      ? sizeGenerationEquipmentBlockCount(input)
      : null),
    [activeEquipmentSelection, activeStage, input],
  );
  const stageIndex = GENERATION_WORKFLOW_STAGE_IDS.indexOf(activeStage);
  const previousStage = stageIndex > 0 ? GENERATION_WORKFLOW_STAGE_IDS[stageIndex - 1]! : null;
  const nextStage = stageIndex >= 0 && stageIndex < GENERATION_WORKFLOW_STAGE_IDS.length - 1
    ? GENERATION_WORKFLOW_STAGE_IDS[stageIndex + 1]!
    : null;
  const stageLabel = (id: GenerationWorkflowStageId): string =>
    workflowStages.find((stage) => stage.id === id)?.label ?? id;
  // Panels stay MOUNTED and toggle via CSS only: unmounting would destroy the
  // project-duty editor's local draft (and other in-section state) on every
  // stage switch, defeating the draft-preservation guards.
  const stagePanelClass = (id: GenerationWorkflowStageId): string =>
    `min-w-0 space-y-4 ${activeStage === id ? '' : 'hidden'}`;

  const topBlockingRisk = result.risks.find((risk) => risk.severity === 'red');
  const nextActionByStage: Record<GenerationWorkflowStageId, GenerationWorkflowNextAction> = {
    requirements: {
      target: 'requirements',
      title: requirementsComplete ? 'Review facility duty' : 'Set facility demand',
      description: 'Set the facility load, operating hours and outage criterion. Unknown inputs remain open; zero demand is not a completed design duty.',
    },
    capacity: {
      target: 'capacity',
      title: isGridOnly ? 'Review declared grid-feed capacity' : capacityComplete ? `Review the ${criterionLabel} capacity screen` : `Resolve the ${criterionLabel} capacity margin`,
      // Data-specific: the exact solver margins, not generic advice.
      description: !(input.facilityLoadMW > 0)
        ? 'Set facility demand in Requirements before comparing supply and reserve.'
        : `${criterionLabel} modeled supply ${contingencyNetMW.toFixed(1)} MW against ${input.facilityLoadMW.toFixed(1)} MW facility demand; load margin ${contingencyMarginMW.toFixed(1)} MW and reserve margin ${contingencyReserveMarginMW.toFixed(1)} MW. ${isGridOnly ? 'Review feed independence and the source-stated allocation; modeled feed ratings do not confirm utility service.' : 'Adjust block count or the selected equipment basis.'}`,
    },
    equipment: {
      target: 'equipment',
      title: isGridOnly ? 'Review grid feeds and linked equipment' : 'Select an evidence-backed equipment basis',
      description: isGridOnly
        ? 'Grid feeds use their declared capacity and outage basis. Review the equipment BOM separately; standby equipment is not included in the grid operating model.'
        : equipmentSuggestion
        ? `${equipmentSuggestion.manufacturer} ${equipmentSuggestion.model} clears the ${criterionLabel} duty and reserve in the exact catalog screen — apply it in one click or compare the alternatives.`
        : 'Choose the normalized vendor envelope, then refine it with project and procurement evidence.',
      ...(equipmentSuggestion
        ? {
            impact: `Clears ${criterionLabel} + reserve`,
            apply: {
              label: `Apply ${equipmentSuggestion.manufacturer} ${equipmentSuggestion.model}`,
              title: equipmentSuggestion.rationale,
              onApply: () => applyEquipmentEnvelope(equipmentSuggestion.catalogId),
            },
          }
        : {}),
    },
    impacts: {
      target: 'impacts',
      title: impactsBlocking
        ? 'Resolve the critical resource risks'
        : constraintsDocumented
          ? 'Review the site envelope'
          : allMissingLimitsRequested
            ? 'Awaiting utility responses'
            : 'Document the site envelope',
      description: impactsBlocking && topBlockingRisk
        ? topBlockingRisk.message
        : constraintsDocumented
          ? 'Review resource limits, modeled demand, noise and lighting notes. Resolve critical risks before handoff.'
          : allMissingLimitsRequested
            ? 'Utility data requests are out for the undocumented limits. Enter each figure as it is committed — until then equipment suggestions stay constraint-untested.'
            : isGridOnly
              ? 'Review cooling demand, water supply and nearby receptors. Gas and CCS supply do not apply to a grid-only scenario; missing water evidence remains unknown.'
              : 'Add source-backed gas and water supply limits and nearby receptors. Request missing utility information using the modeled demand.',
    },
    parcel: {
      target: 'parcel',
      title: parcelComplete ? 'Review parcel fit' : 'Resolve the parcel fit',
      description: testFit.status === 'fits'
        ? 'Boundary containment is confirmed — review setbacks before the handoff.'
        : testFit.containmentPct != null
          ? `Reservations are ${testFit.containmentPct.toFixed(1)}% contained — place the overflow or adjust setbacks and the target parcel.`
          : 'Place the calculated reservations, check setbacks, and confirm boundary containment.',
    },
    handoff: {
      target: 'handoff',
      title: testFitReadiness.ready ? 'Review the Test Fit handoff' : testFitReadiness.title,
      description: testFitReadiness.message,
    },
  };

  // The Step-5 studio, extracted so the windowed section and the full-page
  // overlay share ONE prop block and ONE component instance. It is rendered in
  // exactly one place at a time (inline when windowed, overlay when full-page)
  // — never both — so the `test-fit-studio` testid never duplicates.
  const parcelStudioV2 = isParcelStudioV2Enabled();
  const testFitStudioElement = (
    <TestFitStudio
      scenarioKey={`${site.id}:${selectedId ?? 'draft'}`}
      scenarioId={selectedId ?? 'draft'}
      scenarioRevision={selectedBase
        ? inputIsSaved
          ? `${selectedBase.updatedAt} · ${selectedBase.inputHash.slice(0, 12)}`
          : `Unsaved changes based on ${selectedBase.updatedAt} · ${inputFingerprint.slice(0, 12)}`
        : `Unsaved draft · ${inputFingerprint.slice(0, 12)}`}
      site={site}
      maxPower={maxPowerStudy}
      maxPowerBusy={maxPowerBusy}
      onRunMaxPower={runMaxPowerForStudio}
      testFit={testFit}
      parcels={parcels}
      activeLayout={activeLayout}
      readOnly={readOnly || Boolean(selectedIntegrityFailed)}
      busy={busy}
      displayMode={parcelStudioV2 && onOpenFullStudio
        ? fullStudio ? 'editor' : 'preview'
        : 'legacy'}
      onCommitLayout={commitLayout}
      onReplaceLayout={replaceLayout}
      onAddGenerationBlock={addGenerationBlock}
      {...(fullStudio ? {
        saveState: scenarioSaveFailed
          ? 'error' as const
          : operation === 'saving'
            ? 'saving' as const
            : inputIsSaved ? 'saved' as const : 'unsaved' as const,
        onSave: () => void save(),
      } : {})}
      {...(onOpenFullStudio ? { onOpenStudio: onOpenFullStudio } : {})}
      onSelectParcel={selectLayoutParcel}
      layoutSearchBase={layoutSearchBase}
      installedReservations={input.projectDutyBasis?.installedReservations}
      noiseField={liveNoiseField}
      noiseVisible={noiseVisible}
      onToggleNoise={() => setNoiseVisible(current => !current)}
      acousticBarriers={input.layout?.acousticBarriers ?? []}
    />
  );

  const fullStudioDialogRef = useRef<HTMLDivElement | null>(null);

  // The full-page workstation is a modal surface: contain keyboard focus,
  // let nested dialogs own their keys, and return focus to the launch point.
  useEffect(() => {
    if (!fullStudio || !parcelStudioV2) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      const studioRoot = fullStudioDialogRef.current;
      if (!studioRoot) return;
      const modalDialogs = [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')];
      const topDialog = modalDialogs.at(-1);
      if (topDialog && topDialog !== studioRoot) return;
      if (event.key === 'Escape') {
        onExitFullStudio?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...studioRoot.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )];
      if (focusable.length === 0) {
        event.preventDefault();
        studioRoot.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const focusFrame = window.requestAnimationFrame(() => {
      const studioRoot = fullStudioDialogRef.current;
      const firstControl = studioRoot?.querySelector<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      );
      (firstControl ?? studioRoot)?.focus({ preventScroll: true });
    });
    window.addEventListener('keydown', onKey);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus({ preventScroll: true });
    };
  }, [fullStudio, onExitFullStudio, parcelStudioV2]);

  return (
    <div className="mx-auto w-full min-w-0 max-w-[1500px] p-3 sm:p-4 md:p-6" data-testid="generation-impact-workbench">
      {fullStudio && parcelStudioV2 && (
        <div
          ref={fullStudioDialogRef}
          className="fixed inset-0 z-50 grid h-dvh min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-background"
          data-testid="generation-studio-fullscreen"
          role="dialog"
          aria-modal="true"
          aria-label="Full-page parcel test-fit studio"
          tabIndex={-1}
        >
          <div className="flex min-h-12 items-center gap-3 border-b border-border bg-card px-3 py-2">
            <button
              type="button"
              onClick={onExitFullStudio}
              className="inline-flex items-center gap-1.5 border border-border bg-background px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-text-secondary transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary max-[599px]:min-h-11 rounded-md"
              data-testid="generation-studio-fullscreen-exit"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Back to workbench
            </button>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Parcel Test-Fit Studio</p>
              <p className="truncate text-xs text-text-muted">{site.name} · {input.name}</p>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <span
                className={`hidden items-center gap-1.5 text-[10px] font-semibold sm:inline-flex ${
                  scenarioSaveFailed
                    ? 'text-danger'
                    : operation === 'saving'
                    ? 'text-warning'
                    : inputIsSaved ? 'text-success' : 'text-primary'
                }`}
                role="status"
                aria-live="polite"
                data-testid="generation-studio-save-state"
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    scenarioSaveFailed
                      ? 'bg-danger'
                      : operation === 'saving'
                      ? 'animate-pulse bg-warning'
                      : inputIsSaved ? 'bg-success' : 'bg-primary'
                  }`}
                  aria-hidden
                />
                {scenarioSaveFailed
                  ? 'Save failed'
                  : operation === 'saving' ? 'Saving…' : inputIsSaved ? 'Saved' : 'Unsaved changes'}
              </span>
              <button
                type="button"
                onClick={() => void save()}
                disabled={readOnly || busy || Boolean(selectedIntegrityFailed) || projectDutyDraftDirty}
                className="inline-flex min-h-9 items-center gap-1.5 border border-primary/60 bg-primary/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:opacity-40 max-[599px]:min-h-11 rounded-md"
                title={projectDutyDraftDirty ? 'Apply or discard the project duty edits before saving.' : 'Save the current scenario'}
                data-testid="generation-studio-save"
              >
                <Save className="h-3.5 w-3.5" aria-hidden />
                Save
              </button>
            </div>
          </div>
          <div className="min-h-0 min-w-0 overflow-hidden">
            {testFitStudioElement}
          </div>
        </div>
      )}
      {/* Cockpit contract rule 5 — the ONE canonical tab header primitive. */}
      <SiteTabSectionHeader
        icon={Factory}
        title="Power supply & site fit"
        eyebrow="Generation & Impact"
        context="Analyze"
        testId="generation-impact-header"
        className="mb-5"
        actions={
          <>
            {workspaceControl}
            {/* Hero scenario picker — the saved-scenarios rail folded into a
                dropdown so the full page width belongs to the workflow. The
                panel keeps every rail behavior: load (confirm-guarded),
                compare toggles (max 4), integrity quarantine, clone/delete. */}
            <Popover open={scenarioPickerOpen} onOpenChange={setScenarioPickerOpen}>
              <PopoverTrigger
                title="Saved scenarios"
                className="inline-flex min-w-0 max-w-[18rem] items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid="generation-scenario-picker-trigger"
              >
                <FolderOpen className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0">
                  <span className="block truncate text-xs font-bold text-text-primary">
                    {selectedScenario?.name ?? input.name}
                  </span>
                  <span className="block text-[9px] uppercase tracking-wider text-text-muted">
                    {inputIsSaved ? 'Saved inputs' : 'Working draft'} · {saved.length} saved
                  </span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-text-muted" aria-hidden />
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="w-96 max-w-[calc(100vw-2rem)] gap-0 p-3"
                data-testid="generation-impact-scenario-rail"
                aria-label="Saved scenarios"
              >
                <div className="mb-1 flex items-center justify-between"><h2 className="text-xs font-bold uppercase tracking-wider">Saved scenarios</h2><span className="text-[10px] text-text-muted">{saved.length}</span></div>
                {/* The row carries TWO independent selections and they are not
                    interchangeable: the checkbox adds to the comparison set,
                    while Clone/Delete act on `selectedId`, which only a click
                    on the scenario name sets. Ticking every box and pressing
                    Delete (reported 2026-08-03) leaves Delete disabled with no
                    stated reason, so both affordances are labelled here. */}
                <p className="mb-3 text-[10px] leading-snug text-text-muted">Tick to <span className="font-bold">compare</span>. Click a name to <span className="font-bold">select</span> it for clone or delete.</p>
                <div className="grid max-h-[60vh] min-w-0 gap-2 overflow-y-auto overscroll-contain">
                  {saved.map((row) => {
                    const integrityFailed = row.integrity?.valid === false;
                    const integrityTitle = integrityFailed
                      ? row.integrity?.errors.join(' ') || 'Scenario payload failed integrity validation.'
                      : 'Add to comparison';
                    const rowCapacity = row.result.capacity as typeof row.result.capacity & {
                      contingencyOutages?: number;
                      contingencyNetMW?: number;
                      contingencyVerdict?: typeof row.result.capacity.nMinusOneVerdict;
                    };
                    const rowOutages = rowCapacity.contingencyOutages ?? 1;
                    const rowLabel = rowOutages === 0 ? 'N' : `N-${rowOutages}`;
                    const rowVerdict = rowCapacity.contingencyVerdict ?? rowCapacity.nMinusOneVerdict;
                    const rowNetMW = rowCapacity.contingencyNetMW ?? rowCapacity.nMinusOneNetMW;
                    // While the delete request is in flight the row it targets
                    // fades and collapses away, so the destructive action reads
                    // as "this one, now" rather than the list simply blinking
                    // when loadScenarios() returns. `operation`/`selectedId`
                    // already identify the row — deleting clears selectedId only
                    // after the call resolves — so no extra state is needed.
                    const isDeleting = deletingScenarioId === row.id;
                    return <div key={row.id} aria-busy={isDeleting || undefined} data-deleting={isDeleting || undefined} className={`flex items-start gap-2 border p-2 ${selectedId === row.id ? integrityFailed ? 'border-danger bg-danger/10' : 'border-primary bg-primary/5' : integrityFailed ? 'border-danger/50 bg-danger/5' : 'border-border'} rounded-md ${isDeleting ? 'pointer-events-none animate-out fade-out-0 slide-out-to-left-2 zoom-out-95 fill-mode-forwards duration-300 border-danger bg-danger/10 motion-reduce:animate-none motion-reduce:opacity-50' : ''}`}><input aria-label={`Compare ${row.name}`} title={integrityTitle} type="checkbox" disabled={integrityFailed} checked={!integrityFailed && compareIds.includes(row.id)} onChange={(event) => setCompareIds((ids) => event.target.checked ? [...ids, row.id].slice(-4) : ids.filter((id) => id !== row.id))} /><button type="button" title={integrityTitle} onClick={() => { void loadSavedScenario(row, { selectOnly: integrityFailed }); }} className="min-w-0 flex-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary"><span className="block truncate text-xs font-bold">{row.name}</span><span className={`mt-1 block text-[9px] uppercase ${integrityFailed ? 'text-danger' : 'text-text-muted'}`}>{integrityFailed ? 'Integrity failed · select to delete' : `${rowLabel} ${rowVerdict} · ${rowNetMW.toFixed(0)} MW`}</span><GenerationScenarioStatusBadges scenario={row} workspace={layoutWorkspace} promotedScenarioId={promotedScenarioId} /></button></div>;
                  })}
                  {saved.length === 0 && <p className="border border-dashed border-border p-3 text-xs text-text-muted rounded-lg">No saved scenarios. The working calculation is private until saved.</p>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button type="button" disabled={!selectedId || readOnly || selectedIntegrityFailed} title={!selectedId ? 'Click a scenario name above to select it first — ticking the checkbox only adds it to the comparison.' : selectedIntegrityFailed ? 'This scenario failed integrity validation and cannot be cloned.' : readOnly ? 'You do not have edit access to this site.' : 'Clone the selected scenario'} onClick={() => { setInput({ ...input, name: `${input.name} — copy` }); setSelectedId(null); setSelectedBase(null); setMainProjectDraft(false); setPublishAttempt(null); setLayoutConflict(null); setLayoutReviewOpen(false); setScenarioPickerOpen(false); }} className="inline-flex items-center justify-center gap-1 border border-border p-2 text-[9px] font-bold uppercase disabled:opacity-40 rounded-md"><Copy className="h-3 w-3" /> Clone</button>
                  <button type="button" disabled={!selectedId || readOnly || busy} title={!selectedId ? 'Click a scenario name above to select it first — ticking the checkbox only adds it to the comparison.' : readOnly ? 'You do not have edit access to this site.' : busy ? 'Another scenario action is still running.' : 'Delete the selected scenario'} onClick={() => void actOnScenario('deleteGenerationImpactScenario')} className="inline-flex items-center justify-center gap-1 border border-border p-2 text-[9px] font-bold uppercase text-danger disabled:opacity-40 rounded-md">{deletingScenarioId ? <><Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" /> Deleting…</> : <><Trash2 className="h-3 w-3" /> Delete</>}</button>
                </div>
                {!selectedId && saved.length > 0 && (
                  <p className="mt-2 text-[10px] leading-snug text-text-muted">No scenario selected — click a name above to enable clone and delete.</p>
                )}
              </PopoverContent>
            </Popover>
            <Button
              variant="mission-primary"
              size="sm"
              onClick={() => void save()}
              disabled={busy || readOnly || selectedIntegrityFailed}
              title={projectDutyDraftDirty ? 'Apply or discard the project duty dossier edits before saving.' : undefined}
              data-testid="generation-save-scenario"
            >
              <Save className="h-4 w-4" /> {operation === 'saving' ? 'Saving…' : projectDutyDraftDirty ? 'Apply dossier first' : inputIsSaved ? 'Save scenario' : 'Save changes'}
            </Button>
          </>
        }
      />
      {site.studyAreaScope && studyScopeChanged && <aside role="alert" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm" data-testid="generation-study-area">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-text-primary">Study area changed · {site.studyAreaScope.name}</p>
          <p className="mt-1 text-xs text-text-secondary">This scenario uses an earlier study area. Create a draft for the selected area before saving or publishing.</p>
        </div>
        <Button variant="secondary" size="sm" disabled={readOnly} onClick={() => {
          const next = hydrateLayout({ ...input, layout: defaultLayout(site), layoutAnchor: undefined, name: `${input.name} — ${site.studyAreaScope!.name}` }, site);
          setInput(next); setSelectedId(null); setSelectedBase(null); setMainProjectDraft(false); setPublishAttempt(null);
        }}>Use selected study area in a new draft</Button>
      </aside>}

      <main className="min-w-0 space-y-4" data-testid="generation-impact-primary">
          <GenerationScenarioBasis input={input} inputIsSaved={inputIsSaved} selectedScenarioId={selectedId}
            projectScenario={currentProjectScenario} projectRevisionMatches={generationImpactWorkflowInternals.sameScenarioRevision(selectedBase, currentProjectScenario)} projectScenarioExpected={activeId != null}
            publishedVersion={currentPublishedVersion} busy={busy || openConfirmDecisions > 0}
            onOpenProjectScenario={() => { if (currentProjectScenario) void loadSavedScenario(currentProjectScenario); }} />
          <GenerationWorkflowNavigator
            stages={workflowStages}
            recommendedNextAction={nextActionByStage[recommendedStage]}
            activeStage={activeStage}
            onSelectStage={selectStage}
            nextActionPlacement="external"
          />
          <div className="min-w-0 space-y-4" data-testid="generation-stage-column">
          <GenerationNextActionCard action={{ ...nextActionByStage[activeStage], impact: undefined, apply: undefined }} onActivate={selectStage} label="Next in this step"
            className="border-l-2 border-primary pl-4 py-1" />
          <details className="rounded-lg border border-border bg-card text-xs" aria-label="Site-sourced design basis">
            <summary className="cursor-pointer px-4 py-3 font-medium text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">Site source details</summary>
            <div className="flex flex-wrap gap-x-8 gap-y-2 border-t border-border px-4 py-3">
            <span data-testid="generation-design-basis-facility"><b className="inline-flex items-center gap-1 text-text-muted">Facility basis <GenerationFieldHelp field="facilityBasis" /></b> {designBasisFacilityLabel} <em className="ml-1 text-[9px] not-italic uppercase text-primary">Project Intent{projectLoadUsesBand ? ' · bucket-derived' : ''}</em></span>
            <span><b className="inline-flex items-center gap-1 text-text-muted">Peak dry bulb <GenerationFieldHelp field="peakDryBulb" /></b> {sed?.climate?.data?.peakTemp ?? 'Missing'} °C <em className="ml-1 text-[9px] not-italic uppercase text-primary">SED</em></span>
            <span><b className="inline-flex items-center gap-1 text-text-muted">Peak wet bulb <GenerationFieldHelp field="peakWetBulb" /></b> {sed?.climate?.data?.peakWetBulb ?? 'Missing'} °C <em className="ml-1 text-[9px] not-italic uppercase text-primary">SED</em></span>
            <span><b className="inline-flex items-center gap-1 text-text-muted">Parcel <GenerationFieldHelp field="parcel" /></b> {parcelAreaAcres == null ? 'Missing' : parcelAreaAcres.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} acres <em className="ml-1 text-[9px] not-italic uppercase text-primary" data-testid="generation-design-basis-parcel-source">{parcelAreaSource}</em></span>
          </div>
          </details>

          <div className={stagePanelClass('requirements')} data-testid="generation-stage-panel-requirements" data-stage-active={activeStage === 'requirements' ? 'true' : 'false'}>
          <GenerationProjectInputsPanel site={site} editingAssessment={editingAssessment} isMainProjectScope={isMainProjectScope}
            readOnly={readOnly || busy} onSaved={onWorkflowAuthorityChanged} />
          {projectInputSync.message && <p role="status">{projectInputSync.message}</p>}
          <section id="generation-stage-requirements" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card" aria-labelledby="generation-stage-requirements-heading" data-testid="generation-stage-requirements">
            <div className="border-b border-border px-4 py-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 1 · Requirements</p>
              <h2 id="generation-stage-requirements-heading" className="mt-1 text-base font-bold text-text-primary">Define facility duty</h2>
              <p className="mt-1 max-w-4xl text-xs text-text-muted">Enter the demand the supply must serve. Keep facility load, operating hours and outage assumptions together.</p>
            </div>
            <div className="grid gap-3 p-4 sm:grid-cols-2 2xl:grid-cols-4">
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="scenarioName" htmlFor="generation-scenario-name">Scenario name</GenerationFieldLabel><input id="generation-scenario-name" value={input.name} onChange={(e) => setInput({ ...input, name: e.target.value })} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /></div>
              <div className="text-[10px] uppercase text-text-muted">
                <GenerationFieldLabel field="loadBasis" htmlFor="generation-load-basis">Facility load source</GenerationFieldLabel>
                <select id="generation-load-basis" value={selectedLoadBasis} onChange={(e) => applyLoadBasis(e.target.value)} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md">
                  <option value="project-total" disabled={projectLoadMW == null}>Project Intent total · {projectLoadMW == null ? 'facility demand unknown' : `${Math.round(projectLoadMW)} MW facility`}{projectLoadUsesBand ? ' (bucket-derived)' : ''}</option>
                  {projectPhases.map(phase => {
                    const facilityMW = planningFacilityLoadMW(site.projectIntent, phase);
                    return <option key={phase.id} value={phase.id} disabled={facilityMW == null}>{phase.label} · {facilityMW == null ? 'facility demand unknown' : `${Math.round(facilityMW)} MW facility`}</option>;
                  })}
                  {rangeOnlyPhases.map(phase => <option key={phase.id} value={phase.id} disabled>{phase.label} · {effectivePhaseCapacity(phase).displayLabel} (range — set an exact MW to target)</option>)}
                  <option value="manual">Manual scenario input</option>
                </select>
                {projectLoadMW == null && (site.projectIntent?.powerRequirementMW != null || projectIntentBand != null || projectPhases.length > 0) && (
                  <p className="mt-1 text-[11px] normal-case leading-relaxed text-text-muted">Confirm the power basis and a valid PUE before using an IT load as facility demand. The entered Intent quantity stays unchanged.</p>
                )}
              </div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="facilityLoad" htmlFor="generation-facility-load">Facility load (MW)</GenerationFieldLabel><input id="generation-facility-load" type="number" min="0" value={facilityLoadText ?? String(input.facilityLoadMW)} onFocus={event => {
                  projectInputSync.captureEdit(); sharedFieldFocus.current.load = event.target.value;
                  facilityLoadEditing.current = true; setFacilityLoadText(event.target.value);
                }}
                onChange={event => {
                  setFacilityLoadText(event.target.value);
                  setInput({ ...input, facilityLoadMW: numberValue(event.target.value), ...(isMainProjectScope ? {} : { targetPhaseId: undefined }) });
                }}
                onBlur={event => {
                  const focused = sharedFieldFocus.current.load; sharedFieldFocus.current.load = null;
                  facilityLoadEditing.current = false;
                  setFacilityLoadText(event.target.value.trim() === '' ? '' : null);
                  if (!isMainProjectScope || focused == null || focused === event.target.value) return;
                  try {
                    void projectInputSync.save(canonicalFacilityLoadEdit(site.projectIntent,
                      event.target.value.trim() === '' ? null : Number(event.target.value),
                      input.targetPhaseId ?? latestIntentPhase(site)?.id));
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : 'Facility demand could not be converted to the recorded power basis.');
                  }
                }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /></div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="criticalLoad" htmlFor="generation-critical-load">Critical load (MW)</GenerationFieldLabel><input id="generation-critical-load" type="number" min="0" value={input.criticalLoadMW ?? ''} placeholder="Defaults to facility load" onChange={(e) => setInput({ ...input, criticalLoadMW: e.target.value === '' ? undefined : numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /></div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="reserveMargin" htmlFor="generation-reserve-margin">Reserve margin (%)</GenerationFieldLabel><input id="generation-reserve-margin" type="number" min="0" max="100" step="0.1" value={input.reserveMarginPct ?? ''} onChange={(e) => setInput({ ...input, reserveMarginPct: e.target.value === '' ? undefined : numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /></div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="resilienceCriterion" htmlFor="generation-resilience-criterion">Resilience criterion</GenerationFieldLabel><select id="generation-resilience-criterion" value={input.contingencyOutages ?? 1} onChange={(e) => setInput({ ...input, contingencyOutages: numberValue(e.target.value, 1) })} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md"><option value="0">N — normal operation</option><option value="1">N+1 / N-1 — survive 1 group</option><option value="2">N+2 / N-2 — survive 2 groups</option><option value="3">N+3 / N-3 — survive 3 groups</option></select>{site.projectIntent?.redundancyClass && <p className="mt-1 text-[9px] normal-case text-text-muted" data-testid="generation-redundancy-note">Project Intent declares <strong className="text-text-secondary">{site.projectIntent.redundancyClass}</strong>{contingencyOutagesFromRedundancy(site.projectIntent.redundancyClass) == null ? ' — a distribution-path spec that does not by itself fix how many generation groups the plant is split into. Set the criterion to match your intended split.' : ' — carried across as this criterion.'}</p>}</div>
              <label className="text-[10px] uppercase text-text-muted">Grid relationship<select value={input.gridConnection} onFocus={event => { projectInputSync.captureEdit(); sharedFieldFocus.current.grid = event.target.value; }} onChange={(e) => setInput({ ...input, gridConnection: e.target.value as GenerationImpactScenarioInput['gridConnection'] })}
                onBlur={event => { const focused = sharedFieldFocus.current.grid; sharedFieldFocus.current.grid = null; const phaseId = input.targetPhaseId ?? latestIntentPhase(site)?.id; const value = event.target.value; if (isMainProjectScope && focused != null && focused !== value && phaseId && (value === 'islanded' || value === 'grid-connected' || value === 'undecided')) void projectInputSync.save({ phaseId, gridConnection: value }); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md"><option value="islanded">Islanded / on-site only</option><option value="grid-connected">Grid-connected</option><option value="undecided">Undecided</option></select></label>
              <label className="text-[10px] uppercase text-text-muted">Full-load hours / year<input type="number" min="0" max="8760" value={input.annualOperatingHours} onChange={(e) => setInput({ ...input, annualOperatingHours: numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /></label>
            </div>
            {/* Size the load from what is being BUILT. Optional — a developer
                who already knows their facility MW just types it above. */}
            <details className="mx-4 mb-4 border border-border rounded-md" data-testid="generation-data-center-sizing">
              <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-primary">Size the load from racks</span>
                  <span className="mt-0.5 block text-[9px] normal-case text-text-muted">Derive facility MW from a workload preset and rack count instead of entering it directly.</span>
                </span>
                <span className="border border-border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-muted rounded-sm">Optional</span>
              </summary>
              <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-3">
                <div className="text-[10px] uppercase text-text-muted">
                  <label htmlFor="generation-dc-preset">Workload</label>
                  <select
                    id="generation-dc-preset"
                    value={dataCenterDraft.presetId}
                    disabled={readOnly}
                    onChange={(e) => setDataCenterDraft(current => ({ ...current, presetId: e.target.value as WorkloadProfile }))}
                    className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md"
                  >
                    {dataCenterPresets.map(preset => (
                      <option key={preset.id} value={preset.id}>
                        {preset.label}{preset.recommended ? ' · Project Intent' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="text-[10px] uppercase text-text-muted">
                  <label htmlFor="generation-dc-racks">Rack count</label>
                  <input
                    id="generation-dc-racks"
                    type="number"
                    min="1"
                    value={dataCenterDraft.rackCount}
                    disabled={readOnly}
                    placeholder="e.g. 2500"
                    onChange={(e) => setDataCenterDraft(current => ({ ...current, rackCount: e.target.value }))}
                    className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    disabled={readOnly || !dataCenterSizing}
                    onClick={applyDataCenterSizing}
                    data-testid="generation-dc-apply"
                    className="border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted rounded-md"
                  >
                    {dataCenterSizing ? `Use ${dataCenterSizing.facilityMW.toFixed(1)} MW facility load` : 'Enter a rack count'}
                  </button>
                </div>
              </div>
              {dataCenterSizing && (
                <p className="border-t border-border px-3 py-2 text-[9px] text-text-muted" data-testid="generation-dc-preview">
                  {dataCenterSizing.rackCount.toLocaleString('en-US')} racks × {dataCenterSizing.kwPerRack.toFixed(1)} kW/rack
                  {' = '}{dataCenterSizing.itLoadMW.toFixed(1)} MW IT
                  {' → '}{dataCenterSizing.facilityMW.toFixed(1)} MW facility at PUE {dataCenterSizing.pue.toFixed(2)}
                  {dataCenterSizing.gridMW == null ? '' : ` → ${dataCenterSizing.gridMW.toFixed(1)} MW grid draw`}.
                  {' '}Rack density is the workload&rsquo;s published anchor; override it in Project Intent.
                </p>
              )}
            </details>
            {/* Facility cooling water basis — a DEMAND parameter of the data
                centre, so it lives with the load definition, not among the
                Step 2 supply limits. The cooling-type default lets the campus
                water total compute at screening; provenance stays visible and
                user/evidence values supersede it. */}
            <details className="mx-4 mb-4 border border-border rounded-md" data-testid="generation-cooling-water-basis">
              <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-3 py-2 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-text-primary">Facility cooling water basis</span>
                  <span className="mt-0.5 block text-[9px] normal-case text-text-muted">The data-center cooling draw completes the campus water total. Pick a cooling type for a cited planning default, or enter an engineered basis.</span>
                </span>
                <span className="border border-border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-muted rounded-sm">{input.water.dataCenterConsumptionGalPerMWhFacility == null ? 'Needed for water total' : 'Set'}</span>
              </summary>
              <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-3">
                <div className="text-[10px] uppercase text-text-muted">
                  <GenerationFieldLabel field="coolingType" htmlFor="generation-cooling-type">Cooling type</GenerationFieldLabel>
                  <select
                    id="generation-cooling-type"
                    value={coolingTypeDraft}
                    disabled={readOnly}
                    onChange={(e) => setCoolingTypeDraft(e.target.value as DataCenterCoolingType | '')}
                    className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md"
                  >
                    <option value="">Select cooling type…</option>
                    {DATA_CENTER_COOLING_WATER_DEFAULTS.map((entry) => (
                      <option key={entry.coolingType} value={entry.coolingType}>{entry.label}</option>
                    ))}
                  </select>
                  {coolingRecommendation && (
                    <p className="mt-1 text-[10px] normal-case text-text-secondary" data-testid="generation-cooling-recommendation">
                      Recommended: <span className="font-semibold text-text-primary">{coolingRecommendation.label}</span> — {coolingRecommendation.reason}
                      {!readOnly && coolingTypeDraft !== coolingRecommendation.coolingType && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="underline"
                            onClick={() => setCoolingTypeDraft(coolingRecommendation.coolingType)}
                            data-testid="generation-cooling-use-recommended"
                          >
                            Use recommended
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex flex-col justify-end">
                  <button
                    type="button"
                    disabled={readOnly || !coolingTypeDraft}
                    onClick={applyCoolingWaterDefault}
                    data-testid="generation-cooling-apply"
                    className="border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted rounded-md"
                  >
                    Apply planning default
                  </button>
                </div>
              </div>
              <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2">
                <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="dataCenterWithdrawal" htmlFor="generation-dc-withdrawal">Data-center withdrawal (gal / MWh facility)</GenerationFieldLabel><input id="generation-dc-withdrawal" type="number" min="0" value={input.water.dataCenterWithdrawalGalPerMWhFacility ?? ''} placeholder="Add cooling basis" onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('water.dataCenterWithdrawalGalPerMWhFacility', value, current => ({ ...current, water: { ...current.water, dataCenterWithdrawalGalPerMWhFacility: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary rounded-md" />{input.water.dataCenterWithdrawalGalPerMWhFacility != null && <GenerationScreeningSourceBadge field="water.dataCenterWithdrawalGalPerMWhFacility" assumption={screeningAssumptions.get('water.dataCenterWithdrawalGalPerMWhFacility')} onOpenDocument={onOpenDocuments} />}</div>
                <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="dataCenterConsumption" htmlFor="generation-dc-consumption">Data-center consumption (gal / MWh facility)</GenerationFieldLabel><input id="generation-dc-consumption" type="number" min="0" value={input.water.dataCenterConsumptionGalPerMWhFacility ?? ''} placeholder="Add cooling basis" onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('water.dataCenterConsumptionGalPerMWhFacility', value, current => ({ ...current, water: { ...current.water, dataCenterConsumptionGalPerMWhFacility: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary rounded-md" />{(input.water.dataCenterConsumptionGalPerMWhFacility != null || latestEvidenceActionFor('water.dataCenterConsumptionGalPerMWhFacility')) && <GenerationScreeningSourceBadge field="water.dataCenterConsumptionGalPerMWhFacility" assumption={screeningAssumptions.get('water.dataCenterConsumptionGalPerMWhFacility')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('water.dataCenterConsumptionGalPerMWhFacility')} />}</div>
              </div>
            </details>
          </section>
          </div>

          <div className={stagePanelClass('impacts')} data-testid="generation-stage-panel-impacts" data-stage-active={activeStage === 'impacts' ? 'true' : 'false'}>
          <section id="generation-stage-impacts" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card px-4 py-3" aria-labelledby="generation-stage-impacts-heading" data-testid="generation-stage-impacts">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 2 · Site envelope</p>
            <h2 id="generation-stage-impacts-heading" className="mt-1 text-base font-bold text-text-primary">Document what the site can supply and tolerate</h2>
            <p className="mt-1 max-w-4xl text-xs text-text-muted">This stage records the site's <strong>supply</strong> — the firm gas, water, CO₂ export, and nearest-receptor evidence every Step 3 candidate is screened against. The Community &amp; resource screen alongside shows the computed <strong>demand</strong>; when a supply figure is not yet committed, that demand is the number to request from the utility. Technology assumptions (output factors, heat rate, water and land intensities) live with the equipment authority chain in Step 3, where a vendor envelope replaces them.</p>
            <LandScreeningBasis site={site} compact className="mt-3 rounded-lg border border-border p-3" />
          </section>

          <section className="grid min-w-0 grid-cols-1 gap-4 2xl:grid-cols-2">
            <div className="min-w-0 border border-border bg-card p-4 rounded-lg">
              <h2 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider"><Factory className="h-4 w-4 text-primary" /> Site limits &amp; community constraints <span className="font-normal normal-case tracking-normal text-text-muted">— supply (evidence)</span></h2>
              <p className="mb-3 text-[10px] text-text-muted">What the site can <strong>get</strong>, evidenced from utility commitments — not the computed demand shown alongside. Each documented limit turns a grey “undocumented” chip on the Step 3 equipment cards into a pass/exceed screen. Missing a figure? Request it from the counterparty with the composer below.</p>
              {/* Auto-refresh is switched off whenever a scenario is loaded,
                  saved or promoted so a frozen record cannot mutate under the
                  user. Without this control there was no way back on, and new
                  SED revisions / freshly extracted documents silently stopped
                  reaching the scenario. */}
              {!readOnly && !automaticEvidenceRefreshEnabled && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border border-dashed border-border px-3 py-2 rounded-md" data-testid="generation-evidence-refresh-paused">
                  <p className="text-[10px] text-text-muted">
                    <strong className="text-text-secondary">Saved evidence revision</strong> — this scenario keeps the inputs it was saved with. Use current evidence to prepare an updated draft, then save when ready.
                  </p>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={refreshFromEvidence}
                    className="border border-primary px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted rounded-md"
                    data-testid="generation-evidence-refresh"
                  >
                    Use current evidence in draft
                  </button>
                </div>
              )}
              <GenerationSiteWaterEvidence sed={sed} onOpenDocument={onOpenDocuments} />
              {/* Documentation checklist — the SAME predicate as the Site-envelope
                  stage gate (`constraintsDocumented`) and the per-card equipment
                  chips, so the three surfaces can never disagree. `requested`
                  (utility data request pending) is a distinct state that never
                  satisfies the gate. */}
              <div className="flex flex-wrap gap-1.5" data-testid="generation-site-limits-checklist" role="list" aria-label="Site-limit documentation">
                {([
                  ['gas', 'Firm gas', siteLimitDocumentation.gas, 'gas'],
                  ['water', 'Water', siteLimitDocumentation.water, 'water'],
                  // The receptor is not a requestable utility limit, so it
                  // carries no request record — hence the null request key
                  // rather than a cast that would silently mis-look-up.
                  ['receptor', 'Nearest receptor', siteLimitDocumentation.receptor, null],
                ] as const).map(([key, label, state, requestKey]: readonly [string, string, SiteLimitDocumentationState, UtilityResourceKind | null]) => (
                  <span
                    key={key}
                    role="listitem"
                    data-testid={`generation-site-limit-${key}`}
                    data-documented={state === 'not-applicable' ? 'not-applicable' : state === 'documented' ? 'true' : state === 'requested' ? 'requested' : 'false'}
                    title={state === 'requested' && requestKey
                      ? `Requested ${input.resourceLimitRequests?.[requestKey]?.requestedAt ?? ''}`.trim()
                      : undefined}
                    className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${state === 'not-applicable' ? 'border-border text-text-muted opacity-60' : state === 'documented' ? 'border-success/40 bg-success/10 text-success' : state === 'requested' ? 'border-primary/40 bg-primary/10 text-primary' : 'border-warning/40 bg-warning/10 text-warning'}`}
                  >
                    {label} · {state === 'not-applicable' ? 'n/a' : state === 'documented' ? 'documented' : state === 'requested' ? 'requested · pending' : 'undocumented'}
                  </span>
                ))}
              </div>
              <div className={`mt-3 grid gap-3 ${isGasTechnology ? 'sm:grid-cols-3' : 'sm:grid-cols-1'}`}>
                <div className={`text-[10px] uppercase text-text-muted ${isGasTechnology ? '' : 'hidden'}`}><GenerationFieldLabel field="firmGasCapacity" htmlFor="generation-gas-capacity">Firm gas capacity (Dth/day)</GenerationFieldLabel><input id="generation-gas-capacity" type="number" min="0" disabled={!isGasTechnology} value={input.availableGasCapacityDthPerDay ?? ''} placeholder={isGasTechnology ? 'Evidence input' : 'Not applicable'} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('availableGasCapacityDthPerDay', value, current => withRequestCleared({ ...current, availableGasCapacityDthPerDay: value }, 'gas', value)); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:cursor-not-allowed rounded-md" />{(input.availableGasCapacityDthPerDay != null || latestEvidenceActionFor('availableGasCapacityDthPerDay')) && <GenerationScreeningSourceBadge field="availableGasCapacityDthPerDay" assumption={screeningAssumptions.get('availableGasCapacityDthPerDay')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('availableGasCapacityDthPerDay')} />}{renderResourceLimitHints('gas', input.availableGasCapacityDthPerDay != null)}</div>
                <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="waterCapacity" htmlFor="generation-water-capacity">Water capacity (consumptive use, gal/day)</GenerationFieldLabel><input id="generation-water-capacity" type="number" min="0" value={input.availableWaterConsumptionGalPerDay ?? ''} placeholder="Evidence input" onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('availableWaterConsumptionGalPerDay', value, current => withRequestCleared({ ...current, availableWaterConsumptionGalPerDay: value }, 'water', value)); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary rounded-md" />{(input.availableWaterConsumptionGalPerDay != null || latestEvidenceActionFor('availableWaterConsumptionGalPerDay')) && <GenerationScreeningSourceBadge field="availableWaterConsumptionGalPerDay" assumption={screeningAssumptions.get('availableWaterConsumptionGalPerDay')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('availableWaterConsumptionGalPerDay')} />}<p className="mt-1 text-[10px] normal-case leading-relaxed text-text-muted">Committed consumption limit for the modeled equipment and cooling. Service allocation and whole-site demand above are separate quantities.</p>{renderResourceLimitHints('water', input.availableWaterConsumptionGalPerDay != null)}</div>
                <div className={`text-[10px] uppercase text-text-muted ${isGasTechnology ? '' : 'hidden'}`}><GenerationFieldLabel field="co2ExportCapacity" htmlFor="generation-co2-capacity">CO₂ export capacity (t/year)</GenerationFieldLabel><input id="generation-co2-capacity" type="number" min="0" disabled={!isGasTechnology} value={input.ccsExportCapacityTonnesPerYear ?? ''} placeholder={isGasTechnology ? 'Evidence input' : 'Not applicable'} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('ccsExportCapacityTonnesPerYear', value, current => withRequestCleared({ ...current, ccsExportCapacityTonnesPerYear: value }, 'co2', value)); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:cursor-not-allowed rounded-md" />{(input.ccsExportCapacityTonnesPerYear != null || latestEvidenceActionFor('ccsExportCapacityTonnesPerYear')) && <GenerationScreeningSourceBadge field="ccsExportCapacityTonnesPerYear" assumption={screeningAssumptions.get('ccsExportCapacityTonnesPerYear')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('ccsExportCapacityTonnesPerYear')} />}{renderResourceLimitHints('co2', input.ccsExportCapacityTonnesPerYear != null)}</div>
              </div>
              {/* "I don't have the figure yet" path: the computed demand IS the
                  number to request from the counterparty. Drafts only — the
                  user reviews and sends from their own mail client. */}
              {!readOnly && (['gas', 'water', 'co2'] as const).some((kind) =>
                (siteLimitDocumentation[kind] === 'undocumented' || siteLimitDocumentation[kind] === 'requested')
                // A resource with no computed demand yields no ask, so an
                // otherwise-qualifying kind must not open an empty box.
                && deriveUtilityAsk({ kind, input, result }) != null) && (
                <div id="generation-utility-requests" className="mt-3 border border-dashed border-primary/40 bg-primary/[0.03] p-3 rounded-lg" data-testid="generation-utility-requests">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-primary">Ask the counterparty</p>
                  <p className="mt-1 text-[10px] text-text-muted">The computed demand is the number to request. Each draft states the screening basis and the evidence the response must contain to become screenable here. Nothing is sent automatically.</p>
                  {(['gas', 'water', 'co2'] as const).map((kind) => {
                    const state = siteLimitDocumentation[kind];
                    if (state !== 'undocumented' && state !== 'requested') return null;
                    const ask = deriveUtilityAsk({ kind, input, result, nominationMarginPct: utilityAskMarginPct[kind] });
                    if (!ask) return null;
                    const email = buildUtilityDataRequestEmail({ ask, siteName: site.name });
                    const requestRecord = input.resourceLimitRequests?.[kind];
                    return (
                      <div key={kind} className="mt-2 border-t border-primary/20 pt-2" data-testid={`generation-utility-request-${kind}`}>
                        <p className="text-xs">
                          Request <strong>{ask.askValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} {ask.demandUnit} {ask.commitmentTerm}</strong> from the {ask.counterpartyLabel} — computed {ask.demandValue.toLocaleString(undefined, { maximumFractionDigits: 0 })} {ask.demandUnit} + {ask.nominationMarginPct}% nomination planning margin.
                        </p>
                        {ask.incomplete && <p className="mt-1 text-[10px] text-warning">{ask.incomplete}</p>}
                        <div className="mt-1.5 flex flex-wrap items-center gap-3">
                          <label className="inline-flex items-center gap-1.5 text-[10px] uppercase text-text-muted">Margin %
                            <input
                              type="number"
                              min="0"
                              max="100"
                              aria-label={`Nomination planning margin (%) for the ${ask.counterpartyLabel} request`}
                              value={utilityAskMarginPct[kind]}
                              onChange={(e) => { const value = Math.max(0, Math.min(100, numberValue(e.target.value))); setUtilityAskMarginPct((current) => ({ ...current, [kind]: value })); }}
                              className="w-14 border border-border bg-background p-1 text-xs text-text-primary rounded-md"
                            />
                          </label>
                          <a
                            aria-label={`Email the ${ask.counterpartyLabel} a capacity request`}
                            className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-primary underline-offset-2 hover:underline"
                            href={email.mailtoHref}
                          >
                            <Mail className="h-3 w-3" /> Draft request
                          </a>
                          <button
                            type="button"
                            aria-label={`Copy the ${ask.counterpartyLabel} capacity request to the clipboard`}
                            className="text-[9px] uppercase tracking-wider text-text-muted underline-offset-2 hover:underline"
                            onClick={() => { void copyUtilityAsk(kind, email.to, email.subject, email.body); }}
                          >
                            {copiedUtilityAskKind === kind ? 'Copied' : 'Copy'}
                          </button>
                          {requestRecord
                            ? (
                              <button
                                type="button"
                                className="text-[9px] uppercase tracking-wider text-text-muted underline-offset-2 hover:underline"
                                data-testid={`generation-utility-request-clear-${kind}`}
                                onClick={() => setResourceLimitRequest(kind, false)}
                              >
                                Requested {requestRecord.requestedAt} · clear
                              </button>
                            )
                            : (
                              <button
                                type="button"
                                className="border border-primary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 rounded-md"
                                data-testid={`generation-utility-request-mark-${kind}`}
                                onClick={() => setResourceLimitRequest(kind, true)}
                              >
                                Mark as requested
                              </button>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {(screeningEvidenceResolution.decisions.length > 0
                || screeningEvidenceResolution.conflicts.length > 0
                || screeningEvidenceNotices.length > 0) && (
                <div
                  className="mt-3 space-y-1.5 border-t border-border pt-3 text-[10px] text-text-muted"
                  data-testid="generation-screening-evidence-summary"
                >
                  {screeningEvidenceResolution.decisions.some(decision => decision.applied) && (
                    <p>
                      <span className="font-bold">{automaticEvidenceRefreshEnabled ? 'Applied sources' : 'Available evidence updates'}</span>
                      {' — '}
                      {screeningEvidenceResolution.decisions
                        .filter(decision => decision.applied)
                        .map(decision => `${decision.field} (${decision.candidate.source})`)
                        .join(' · ')}
                    </p>
                  )}
                  {screeningEvidenceResolution.conflicts.map(conflict => (
                    <p
                      key={`${conflict.source}:${conflict.field}`}
                      className="text-warning"
                      role="alert"
                    >
                      <strong>Conflict:</strong> {conflict.message} Review the cited documents and enter a selected design value manually.
                    </p>
                  ))}
                  {/* One line per DOCUMENT (not per fact): what stopped it, and
                      what would make it count. Informational exclusions last. */}
                  {screeningEvidenceNotices.length > 0 && <details className="pt-1"><summary className="cursor-pointer text-primary">Evidence checks · {screeningEvidenceNotices.length} documents</summary><div className="mt-2 space-y-2">
                  {screeningEvidenceNotices.slice(0, 4).map(notice => (
                    <p
                      key={`${notice.source}:${notice.sourceId}`}
                      className={notice.severity === 'action' ? 'text-text-secondary' : 'text-text-muted'}
                      data-testid="generation-screening-evidence-notice"
                      data-severity={notice.severity}
                      title={notice.codes.join(', ')}
                    >
                      {notice.source === 'document' && onOpenDocuments
                        ? <button type="button" className="font-bold text-text-primary underline-offset-2 hover:underline" onClick={() => onOpenDocuments(notice.sourceId)}>{notice.label}</button>
                        : <strong className="text-text-primary">{notice.label}</strong>}
                      {notice.fields.length > 0 ? <span className="ml-1 uppercase tracking-wider">· {notice.fields.join(', ')}</span> : null}
                      {' — '}{notice.reason}
                      {notice.nextStep ? <> <em className="not-italic text-primary">{notice.nextStep}</em></> : null}
                    </p>
                  ))}
                  {screeningEvidenceNotices.length > 4 && (
                    <p>{screeningEvidenceNotices.length - 4} more document{screeningEvidenceNotices.length - 4 === 1 ? '' : 's'} set aside — open Documents for the full resolver log.</p>
                  )}
                  </div></details>}
                </div>
              )}
              <div className={`mt-4 flex items-center gap-2 border border-border p-3 text-xs ${isGasTechnology ? '' : 'hidden'} rounded-lg`}><input id="generation-ccs-enabled" type="checkbox" checked={input.ccs.enabled} disabled={!isGasTechnology} onChange={(e) => toggleCcs(e.target.checked)} /><label htmlFor="generation-ccs-enabled">Include capture, conditioning and compression load (pipeline export boundary){isGasTechnology ? '' : ' — gas technologies only'}</label><GenerationFieldHelp field="ccsEnabled" /></div>
              {input.ccs.enabled && <div className="mt-3 border border-primary/30 bg-primary/[0.03] p-3 rounded-lg" data-testid="ccs-design-basis">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><p className="text-[10px] font-bold uppercase tracking-wider text-primary">CCS planning basis</p><span className="text-[9px] uppercase text-text-muted">Low-confidence defaults · replace with FEED</span></div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <label className="text-[10px] uppercase text-text-muted">Capture rate (%)<input aria-label="CCS capture rate (%)" type="number" min="0.1" max="100" step="0.1" value={input.ccs.captureRate * 100} onChange={(e) => { const value = numberValue(e.target.value) / 100; setScreeningField('ccs.captureRate', value, current => ({ ...current, ccs: { ...current.ccs, captureRate: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label>
                  <label className="text-[10px] uppercase text-text-muted">Parasitic power (kWh / t CO₂)<input aria-label="CCS parasitic power (kWh per tonne)" type="number" min="0.1" value={input.ccs.specificPowerKWhPerTonneCaptured} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('ccs.specificPowerKWhPerTonneCaptured', value, current => ({ ...current, ccs: { ...current.ccs, specificPowerKWhPerTonneCaptured: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label>
                  <label className="text-[10px] uppercase text-text-muted">Capture island (acres)<input type="number" min="0" step="0.1" disabled={projectCcsCaptureEnvelope} value={input.ccs.footprintAcres} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('ccs.footprintAcres', value, current => ({ ...current, ccs: { ...current.ccs, footprintAcres: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label>
                  <label className="text-[10px] uppercase text-text-muted">CCS withdrawal (gal / t CO₂)<input type="number" min="0" value={input.ccs.waterWithdrawalGalPerTonneCaptured} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('ccs.waterWithdrawalGalPerTonneCaptured', value, current => ({ ...current, ccs: { ...current.ccs, waterWithdrawalGalPerTonneCaptured: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label>
                  <label className="text-[10px] uppercase text-text-muted">CCS consumption (gal / t CO₂)<input type="number" min="0" value={input.ccs.waterConsumptionGalPerTonneCaptured} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('ccs.waterConsumptionGalPerTonneCaptured', value, current => ({ ...current, ccs: { ...current.ccs, waterConsumptionGalPerTonneCaptured: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label>
                  <label className="text-[10px] uppercase text-text-muted">Compression / export (acres)<input type="number" min="0" step="0.1" disabled={projectCcsCompressionEnvelope} value={input.ccs.compressionFootprintAcres} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('ccs.compressionFootprintAcres', value, current => ({ ...current, ccs: { ...current.ccs, compressionFootprintAcres: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label>
                </div>
                {(projectCcsCaptureEnvelope || projectCcsCompressionEnvelope) && <p className="mt-3 border-t border-primary/20 pt-2 text-[9px] text-text-muted">The project dossier L × W envelopes govern the locked CCS acreage fields.</p>}
                <label className="mt-3 flex items-center gap-2 text-xs"><input type="checkbox" checked={input.ccs.pipelineExportConfirmed} onChange={(e) => { const value = e.target.checked; setScreeningField('ccs.pipelineExportConfirmed', value, current => ({ ...current, ccs: { ...current.ccs, pipelineExportConfirmed: value } })); }} /> CO₂ pipeline / storage export route confirmed</label>
              </div>}
              <details className="mt-4 border border-border rounded-md">
                <summary className="cursor-pointer px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-text-muted">Noise receptor screen</summary>
                {/* SITE FACTS only — receptor location, barrier, and the
                    ordinance threshold. Vendor source levels are Step 3
                    equipment data and live with the acoustic source schedule
                    on the equipment stage; the screen result stays a computed
                    output on the Community & resource card. */}
                <p className="border-t border-border px-3 py-2 text-[9px] text-text-muted">Receptor location and threshold are site facts. Vendor source sound levels live with the equipment evidence in Step 3 — the screen turns pass/exceed once both sides exist.</p>
                <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="nearestReceptor" htmlFor="generation-nearest-receptor">Nearest receptor (ft)</GenerationFieldLabel><input id="generation-nearest-receptor" aria-label="Nearest receptor distance (ft)" type="number" min="1" value={input.receptors[0] ? Math.round(input.receptors[0].distanceM / METRES_PER_FOOT) : ''} placeholder="e.g. 1000" onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value) * METRES_PER_FOOT; setScreeningField('receptors.nearest.distanceM', value, current => ({ ...current, receptors: value == null ? [] : [{ id: 'nearest-residence', label: 'Nearest residence', distanceM: value, barrierLossDb: current.receptors[0]?.barrierLossDb ?? 0, thresholdDbA: current.receptors[0]?.thresholdDbA ?? 45 }] })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary rounded-md" />{(input.receptors[0] || latestEvidenceActionFor('receptors.nearest.distanceM')) && <GenerationScreeningSourceBadge field="receptors.nearest.distanceM" assumption={screeningAssumptions.get('receptors.nearest.distanceM')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('receptors.nearest.distanceM')} />}</div>
                  <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="barrierLoss" htmlFor="generation-barrier-loss">Barrier loss (dB)</GenerationFieldLabel><input id="generation-barrier-loss" type="number" min="0" value={input.receptors[0]?.barrierLossDb ?? 0} onChange={(e) => { if (!input.receptors[0]) return; const value = numberValue(e.target.value); setScreeningField('receptors.nearest.barrierLossDb', value, current => current.receptors[0] ? ({ ...current, receptors: [{ ...current.receptors[0], barrierLossDb: value }] }) : current); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></div>
                  <label className="text-[10px] uppercase text-text-muted">Screening threshold (dBA)<input type="number" min="0" value={input.receptors[0]?.thresholdDbA ?? 45} onChange={(e) => { if (!input.receptors[0]) return; const value = numberValue(e.target.value); setScreeningField('receptors.nearest.thresholdDbA', value, current => current.receptors[0] ? ({ ...current, receptors: [{ ...current.receptors[0], thresholdDbA: value }] }) : current); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label>
                </div>
              </details>
              <details className="mt-4 border border-border rounded-md" data-testid="generation-lighting-commitments">
                <summary className="cursor-pointer px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-text-muted">Lighting commitments</summary>
                {/* COMMITMENTS, not measurements: at screening nobody has a
                    fixture inventory, and a permanently-amber section trains
                    users to ignore the chips that matter. The four booleans
                    are exactly what drives result.lighting.planningFlags and
                    the environmental report; the numeric inventory is demoted
                    to optional permitting detail below (solver arithmetic and
                    report consumers unchanged). */}
                <p className="border-t border-border px-3 py-2 text-[9px] text-text-muted">Screening-stage commitments the reports carry as stated mitigation. The fixture inventory is permitting detail — optional here.</p>
                <div className="flex flex-wrap gap-4 border-t border-border p-3 text-xs">
                  <span>
                    <span className="inline-flex items-center gap-1.5"><input id="generation-fully-shielded" type="checkbox" checked={input.lighting.fullyShielded ?? false} onChange={(e) => { const value = e.target.checked; setScreeningField('lighting.fullyShielded', value, current => ({ ...current, lighting: { ...current.lighting, fullyShielded: value } })); }} /><label htmlFor="generation-fully-shielded">Fully shielded</label><GenerationFieldHelp field="fullyShielded" /></span>
                    {input.lighting.fullyShielded != null && <GenerationScreeningSourceBadge field="lighting.fullyShielded" assumption={screeningAssumptions.get('lighting.fullyShielded')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.fullyShielded')} />}
                  </span>
                  <span>
                    <span className="inline-flex items-center gap-1.5"><input id="generation-bug-rated" type="checkbox" checked={input.lighting.bugRated ?? false} onChange={(e) => { const value = e.target.checked; setScreeningField('lighting.bugRated', value, current => ({ ...current, lighting: { ...current.lighting, bugRated: value } })); }} /><label htmlFor="generation-bug-rated">BUG rated</label><GenerationFieldHelp field="bugRated" /></span>
                    {input.lighting.bugRated != null && <GenerationScreeningSourceBadge field="lighting.bugRated" assumption={screeningAssumptions.get('lighting.bugRated')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.bugRated')} />}
                  </span>
                  <span>
                    <span className="inline-flex items-center gap-1.5"><input id="generation-curfew-controls" type="checkbox" checked={input.lighting.motionOrCurfewControls ?? false} onChange={(e) => { const value = e.target.checked; setScreeningField('lighting.motionOrCurfewControls', value, current => ({ ...current, lighting: { ...current.lighting, motionOrCurfewControls: value } })); }} /><label htmlFor="generation-curfew-controls">Curfew / controls</label><GenerationFieldHelp field="curfewControls" /></span>
                    {input.lighting.motionOrCurfewControls != null && <GenerationScreeningSourceBadge field="lighting.motionOrCurfewControls" assumption={screeningAssumptions.get('lighting.motionOrCurfewControls')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.motionOrCurfewControls')} />}
                  </span>
                  <span>
                    <span className="inline-flex items-center gap-1.5"><input id="generation-ies-available" type="checkbox" checked={input.lighting.iesPhotometricAvailable ?? false} onChange={(e) => { const value = e.target.checked; setScreeningField('lighting.iesPhotometricAvailable', value, current => ({ ...current, lighting: { ...current.lighting, iesPhotometricAvailable: value } })); }} /><label htmlFor="generation-ies-available">IES file available</label><GenerationFieldHelp field="iesFileAvailable" /></span>
                    {input.lighting.iesPhotometricAvailable != null && <GenerationScreeningSourceBadge field="lighting.iesPhotometricAvailable" assumption={screeningAssumptions.get('lighting.iesPhotometricAvailable')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.iesPhotometricAvailable')} />}
                  </span>
                </div>
                <details className="border-t border-border" data-testid="generation-lighting-inventory-detail">
                  <summary className="cursor-pointer px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-muted">Permitting detail (optional) — fixture inventory</summary>
                  <div className="grid gap-3 border-t border-border p-3 sm:grid-cols-3">
                    <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="fixtureCount" htmlFor="generation-fixture-count">Fixture count</GenerationFieldLabel><input id="generation-fixture-count" type="number" min="0" value={input.lighting.fixtureCount ?? ''} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('lighting.fixtureCount', value, current => ({ ...current, lighting: { ...current.lighting, fixtureCount: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" />{(input.lighting.fixtureCount != null || latestEvidenceActionFor('lighting.fixtureCount')) && <GenerationScreeningSourceBadge field="lighting.fixtureCount" assumption={screeningAssumptions.get('lighting.fixtureCount')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.fixtureCount')} />}</div>
                    <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="wattsPerFixture" htmlFor="generation-watts-per-fixture">Watts / fixture</GenerationFieldLabel><input id="generation-watts-per-fixture" type="number" min="0" value={input.lighting.wattsPerFixture ?? ''} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('lighting.wattsPerFixture', value, current => ({ ...current, lighting: { ...current.lighting, wattsPerFixture: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" />{(input.lighting.wattsPerFixture != null || latestEvidenceActionFor('lighting.wattsPerFixture')) && <GenerationScreeningSourceBadge field="lighting.wattsPerFixture" assumption={screeningAssumptions.get('lighting.wattsPerFixture')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.wattsPerFixture')} />}</div>
                    <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="lumensPerFixture" htmlFor="generation-lumens-per-fixture">Lumens / fixture</GenerationFieldLabel><input id="generation-lumens-per-fixture" type="number" min="0" value={input.lighting.lumensPerFixture ?? ''} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('lighting.lumensPerFixture', value, current => ({ ...current, lighting: { ...current.lighting, lumensPerFixture: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" />{(input.lighting.lumensPerFixture != null || latestEvidenceActionFor('lighting.lumensPerFixture')) && <GenerationScreeningSourceBadge field="lighting.lumensPerFixture" assumption={screeningAssumptions.get('lighting.lumensPerFixture')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('lighting.lumensPerFixture')} />}</div>
                  </div>
                </details>
              </details>
            </div>

            <div className="min-w-0 border border-border bg-card p-4 rounded-lg">
              <h2 className="mb-4 text-xs font-bold uppercase tracking-wider">Community &amp; resource screen <span className="font-normal normal-case tracking-normal text-text-muted">— demand (computed)</span></h2>
              {!(input.facilityLoadMW > 0) && <div className="mb-3 rounded-md border border-warning/40 p-3 text-xs leading-relaxed text-text-secondary" data-testid="generation-demand-not-set">
                <p className="font-medium text-text-primary">Set facility demand before using this screen.</p>
                <p className="mt-1">These are working-draft values, not the project basis. Open the project scenario above or enter the duty in Requirements.</p>
                <button type="button" className="mt-2 font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" onClick={() => selectStage('requirements')}>Set facility demand</button>
              </div>}
              {isGridOnly && <p className="mb-3 rounded-md bg-background p-3 text-xs leading-relaxed text-text-secondary" data-testid="generation-grid-demand-scope">Grid supply has no modeled on-site fuel use or combustion emissions. Purchased-grid emissions and standby generators in the BOM are excluded. Water covers modeled equipment and declared cooling only; a dry-cooling zero is not zero whole-site water demand.</p>}
              <dl className="grid grid-cols-1 gap-x-4 gap-y-3 text-xs sm:grid-cols-2">
                <div className={isGasTechnology ? '' : 'hidden'}><dt className="inline-flex items-center gap-1.5 text-text-muted">Peak gas <GenerationFieldHelp field="peakGas" /></dt><dd className="font-bold">{isGridOnly && result.gas.peakMmbtuPerHour === 0 ? 'Not applicable · grid supply' : `${result.gas.peakMmbtuPerHour.toLocaleString(undefined, { maximumFractionDigits: 0 })} MMBtu/h`}</dd></div>
                <div className={isGasTechnology ? '' : 'hidden'}><dt className="inline-flex items-center gap-1.5 text-text-muted">Pipeline nomination <GenerationFieldHelp field="pipelineNomination" /></dt><dd className="font-bold">{isGridOnly && result.gas.peakDthPerDay === 0 ? 'Not applicable · grid supply' : `${result.gas.peakDthPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} Dth/day`}</dd></div>
                <div className={isGasTechnology ? '' : 'hidden'}><dt className="inline-flex items-center gap-1.5 text-text-muted">Modeled on-site residual CO₂ at full-load hours <GenerationFieldHelp field="residualCo2" /></dt><dd className="font-bold">{isGridOnly && result.emissions.residualCo2TonnesPerYear === 0 ? 'Not applicable · grid supply' : `${result.emissions.residualCo2TonnesPerYear.toLocaleString(undefined, { maximumFractionDigits: 0 })} t/y`}</dd></div>
                <div className={isGasTechnology && input.ccs.enabled ? '' : 'hidden'}><dt className="inline-flex items-center gap-1.5 text-text-muted">Modeled on-site captured CO₂ at full-load hours <GenerationFieldHelp field="capturedCo2" /></dt><dd className="font-bold">{input.ccs.enabled ? `${result.emissions.capturedCo2TonnesPerYear.toLocaleString(undefined, { maximumFractionDigits: 0 })} t/y` : 'Not applicable · capture off'}</dd></div>
                <div className={isGasTechnology && input.ccs.enabled ? '' : 'hidden'}><dt className="text-text-muted">CCS parasitic load</dt><dd className="font-bold">{input.ccs.enabled ? `${totalCcsLoadMW.toLocaleString(undefined, { maximumFractionDigits: 1 })} MW` : 'Not applicable · capture off'}</dd></div>
                <div className={isGridOnly ? 'hidden' : ''}><dt className="text-text-muted">Peak generation water consumption</dt><dd className="font-bold">{isGridOnly && result.water.generationConsumptionGalPerDay === 0 ? 'Not applicable · grid supply' : `${result.water.generationConsumptionGalPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} gal/day`}</dd></div>
                <div className={isGasTechnology && input.ccs.enabled ? '' : 'hidden'}><dt className="text-text-muted">Peak CCS water consumption</dt><dd className="font-bold">{input.ccs.enabled ? `${result.water.ccsConsumptionGalPerDay.toLocaleString(undefined, { maximumFractionDigits: 0 })} gal/day` : 'Not applicable · capture off'}</dd></div>
                <div><dt className="inline-flex items-center gap-1.5 text-text-muted">Peak modeled component water consumption <GenerationFieldHelp field="waterConsumption" /></dt><dd className="font-bold">{result.water.totalConsumptionGalPerDay?.toLocaleString(undefined, { maximumFractionDigits: 0 }) ?? 'Unknown · cooling basis needed'}{result.water.totalConsumptionGalPerDay == null ? '' : ' gal/day'}</dd></div>
                <div><dt className="inline-flex items-center gap-1.5 text-text-muted">Planning footprint <GenerationFieldHelp field="planningFootprint" /></dt><dd className="font-bold">{result.footprint.totalAcres.toFixed(1)} acres</dd></div>
                <div><dt className="text-text-muted">Independent availability screen</dt><dd className="font-bold">{!(input.facilityLoadMW > 0) ? 'Not screened · demand not set' : result.reliability.probabilityLoadServed == null ? 'Unknown · outage rates needed' : `${(result.reliability.probabilityLoadServed * 100).toFixed(3)}%`}</dd></div>
              </dl>
              {/* Demand vs documented supply — statuses read straight off the
                  solver risk register via the shared constraint-fit projection,
                  so this ledger can never disagree with the Step 3 chips. */}
              <div className="mt-4 border-t border-border pt-3" data-testid="generation-envelope-ledger">
                <p className="text-[10px] font-bold uppercase text-text-muted">Demand vs documented supply</p>
                {deriveSiteEnvelopeLedger(result, input).filter((row) => row.status !== 'not-applicable').map((row) => {
                  // Only promise a draft the composer will actually render:
                  // a resource with no demand (zero-water technologies) has no
                  // ask, so the "draft the request" link would land on nothing.
                  const ledgerAskKind: UtilityResourceKind | null = row.id === 'gas-capacity'
                    ? 'gas'
                    : row.id === 'water-capacity' ? 'water' : 'co2';
                  const hasDraftableAsk = ledgerAskKind != null
                    && deriveUtilityAsk({ kind: ledgerAskKind, input, result }) != null;
                  return (
                  <div key={row.id} data-testid={`generation-envelope-ledger-${row.id}`} data-fit-status={row.status} className="mt-2 border-l-2 border-border pl-3 text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>{row.label}</span>
                      <span className={`border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${
                        row.status === 'fits'
                          ? 'border-success/40 bg-success/10 text-success'
                          : row.status === 'exceeds'
                            ? 'border-danger/40 bg-danger/10 text-danger'
                            : 'border-border text-text-muted'
                      }`}>
                        {row.status === 'fits' ? 'fits' : row.status === 'exceeds' ? 'exceeded' : 'undocumented'}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] text-text-muted">{row.headroomDetail}</p>
                    {row.demandCaveat && <p className="mt-1 text-[10px] text-warning">{row.demandCaveat}</p>}
                    {row.status === 'undocumented' && !readOnly && hasDraftableAsk && (
                      <p className="mt-1 text-[10px] text-text-muted">
                        This computed demand is the number to request from the counterparty — <a href="#generation-utility-requests" className="text-primary underline-offset-2 hover:underline">draft the utility data request</a>.
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-[10px] font-bold uppercase text-text-muted">Physical test-fit allowance</p>
                <div className={`mt-2 grid grid-cols-2 gap-px bg-border text-[10px] ${input.ccs.enabled ? 'sm:grid-cols-5' : isGridOnly && result.footprint.plantAcres === 0 ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                  {[
                    ['Plant', result.footprint.plantAcres, isGridOnly && result.footprint.plantAcres === 0],
                    ['Cooling', result.footprint.coolingAcres, false],
                    ['Switchyard', result.footprint.switchyardAcres, false],
                    ['Capture', result.footprint.ccsAcres, !input.ccs.enabled],
                    ['Compression', result.footprint.compressionAcres, !input.ccs.enabled],
                  ].map(([label, acres, hidden]) => <div key={String(label)} className={hidden ? 'hidden' : 'bg-background p-2'}><span className="block text-text-muted">{label}</span><strong>{Number(acres).toFixed(1)} ac</strong></div>)}
                </div>
                {testFit.plan && <div className="mt-2 grid grid-cols-1 gap-px bg-border text-[10px] sm:grid-cols-2"><div className="bg-background p-2"><span className="block text-text-muted">Gas + CO₂ route reservation</span><strong>{testFit.plan.reservationSummary.routeReservationAcres.toFixed(1)} ac</strong></div><div className="bg-background p-2"><span className="block text-text-muted">Construction laydown</span><strong>{testFit.plan.reservationSummary.constructionLaydownAcres.toFixed(1)} ac</strong></div></div>}
              </div>
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-[10px] font-bold uppercase text-text-muted">Receptor acoustics</p>
                {result.noise.map((row) => <div key={row.receptorId} className="mt-2 border-l-2 border-border pl-3 text-xs"><div className="flex justify-between gap-3"><span>{row.label}</span><strong>{row.predictedDbA == null ? 'Missing source data' : `${row.predictedDbA.toFixed(1)} dBA`}</strong></div><p className="mt-1 text-[10px] text-text-muted">Threshold {row.thresholdDbA?.toFixed(1) ?? 'missing'} dBA · margin {row.marginDb == null ? 'indeterminate' : `${row.marginDb.toFixed(1)} dB`} · {row.verdict}</p></div>)}
                {result.noise.length === 0 && <p className="mt-2 text-xs text-text-muted">Add a receptor distance and vendor source level to screen the nearest residence.</p>}
              </div>
              <div className="mt-4 border-t border-border pt-3">
                <p className="text-[10px] font-bold uppercase text-text-muted">Exterior lighting</p>
                <p className="mt-2 text-xs"><strong>{result.lighting.installedPowerKW?.toLocaleString(undefined, { maximumFractionDigits: 1 }) ?? 'Inventory incomplete'}</strong>{result.lighting.installedPowerKW == null ? '' : ' kW installed'} · {result.lighting.installedLumens?.toLocaleString(undefined, { maximumFractionDigits: 0 }) ?? 'lumens not entered'}{result.lighting.installedLumens == null ? '' : ' lm'}</p>
                {result.lighting.planningFlags.length > 0 ? <ul className="mt-2 space-y-1 text-[10px] text-text-muted">{result.lighting.planningFlags.map((flag) => <li key={flag}>— {flag}</li>)}</ul> : <p className="mt-2 text-[10px] text-success">Fixture controls are documented; verify property-line trespass with the IES model.</p>}
              </div>
            </div>
          </section>

          </div>

          <div className={stagePanelClass('equipment')} data-testid="generation-stage-panel-equipment" data-stage-active={activeStage === 'equipment' ? 'true' : 'false'}>
          <section id="generation-stage-equipment" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card px-4 py-3" aria-labelledby="generation-stage-equipment-heading" data-testid="generation-stage-equipment">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 3 · Equipment evidence</p>
            <h2 id="generation-stage-equipment-heading" className="mt-1 text-base font-bold text-text-primary">{isGridOnly ? 'Review grid feeds and linked equipment' : 'Replace planning defaults with traceable authority'}</h2>
            <p className="mt-1 max-w-4xl text-xs text-text-muted">{isGridOnly ? 'The capacity screen uses your declared grid feeds. Review their delivered capacity and independent contingency groups against utility evidence. Linked BOM equipment is shown alongside; standby generation needs its own operating basis to enter the model.' : 'One authority chain for the block: planning technology defaults → vendor envelope (OEM catalog or user-supplied) → optional project OEM/EPC duty dossier. Each link replaces the one before it in the solver; the BOM snapshot is procurement provenance and never a solver input. Candidates are screened against the Step 2 site limits.'}</p>
          </section>

          {reconcilingCatalogId && (() => {
            const entry = GENERATION_EQUIPMENT_CATALOG.find(
              candidate => candidate.catalogId === reconcilingCatalogId,
            );
            if (!entry) return null;
            return (
              <GenerationOemConfirmationDialog
                catalogId={entry.catalogId}
                manufacturer={entry.manufacturer}
                model={entry.model}
                conflictNote={entry.publishedRating.referenceConditions}
                publishedOutputMW={entry.publishedRating.outputMWPerBlock}
                publishedRatingScope={entry.publishedRating.ratingScope}
                onCancel={() => setReconcilingCatalogId(null)}
                onConfirm={recordOemConfirmation}
              />
            );
          })()}
          <GenerationEquipmentEnvelopeBoard
            gridOnly={isGridOnly}
            linkedBomItems={input.bomBasis?.items}
            rows={equipmentRows}
            userRows={userEquipmentRows}
            shortlistedIds={input.equipmentWorkspace?.shortlistedCatalogIds ?? []}
            currentResult={result}
            selected={equipmentSelection}
            readOnly={readOnly}
            actionsDisabled={busy}
            appliedStatus={equipmentApplyStatus}
            siteName={site.name}
            constraintContext={input}
            onOpenSiteEnvelope={() => selectStage('impacts')}
            onOpenAcousticSources={openAcousticSources}
            onOpenLibrary={() => openEquipmentImport('all')}
            {...(!isGridOnly && (bomBridgeDismissed || bomCoverage.unrepresented.length === 0)
              ? {}
              : {
                  bomBridge: {
                    unrepresentedCount: bomCoverage.unrepresented.length,
                    generationLineCount: bomCoverage.generationLineCount,
                    onReview: () => openEquipmentImport('bom'),
                    onDismiss: () => setBomBridgeDismissed(true),
                  },
                })}
            onApply={applyEquipmentEnvelope}
            onReconcile={openReconcileDialog}
            onToggleShortlist={toggleEquipmentShortlist}
            onAddEquipment={() => setAddEquipmentOpen(true)}
            onApplyUserEquipment={applyUserEquipment}
            onRemoveUserEquipment={removeUserEquipment}
          />
          {/* ACOUSTIC SOURCE SCHEDULE — vendor source levels are equipment
              evidence, so they live here with the authority chain (moved from
              the Step 2 site envelope, which keeps only the receptor facts).
              grid-screening-v2 — multi-source acoustic inventory. The legacy
              single-field screen below stays only while noiseSources is
              undefined (v1 arithmetic preserved). */}
          <div id="generation-acoustic-source-schedule" className="min-w-0 scroll-mt-24 border border-border bg-card p-4 rounded-lg" data-testid="generation-acoustic-source-schedule">
            <h3 className="text-xs font-bold uppercase tracking-wider">Acoustic source schedule</h3>
            <p className="mt-1 text-[10px] text-text-muted">Vendor sound-power levels for the selected equipment. The receptor screen on the Step 2 Community &amp; resource card turns pass/exceed once these and a documented receptor both exist.</p>
            <div className="mt-3">
              <GenerationNoiseSourcesEditor
                noiseSources={input.noiseSources}
                readOnly={readOnly || projectAcousticSources}
                equipmentAcousticsBasis={input.equipmentBasis?.publishedAcoustics?.basis ?? null}
                onChange={(sources) => setScreeningField(
                  'noiseSources',
                  'manual acoustic source schedule',
                  current => ({ ...current, noiseSources: sources }),
                )}
              />
            </div>
            {input.noiseSources === undefined && (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="sourceSoundPower" htmlFor="generation-sound-power">Source sound power (dBA)</GenerationFieldLabel><input id="generation-sound-power" type="number" disabled={projectAcousticSources} value={input.units[0]?.soundPowerDbA ?? ''} placeholder={projectAcousticSources ? 'Project source schedule active' : 'Vendor input'} onChange={(e) => { const value = e.target.value === '' ? undefined : numberValue(e.target.value); setScreeningField('units.soundPowerDbA', value, current => ({ ...current, units: current.units.map(unit => ({ ...unit, soundPowerDbA: value })) })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" />{input.units[0]?.soundPowerDbA != null && <GenerationScreeningSourceBadge field="units.soundPowerDbA" assumption={screeningAssumptions.get('units.soundPowerDbA')} onOpenDocument={onOpenDocuments} />}</div>
              </div>
            )}
          </div>
          {/* Envelope availability provenance — who governs the offered set,
              plus every rejected row's reason (nothing degrades silently). */}
          <div
            className="text-[10px] uppercase tracking-wider text-text-muted"
            data-testid="generation-envelope-availability"
            data-authority={screeningAdmissions.authority}
          >
            Envelope availability: {screeningAdmissions.authority === 'firestore'
              ? `equipment catalog (${screeningAdmissions.admitted.length} offered${screeningAdmissions.hiddenCount > 0 ? ` · ${screeningAdmissions.hiddenCount} hidden by admin` : ''})`
              : 'shipped catalog (equipment database not seeded)'}
            {' — values are version-locked to the shipped screening catalog.'}
          </div>
          {screeningAdmissions.rejected.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-xl border border-warning/50 bg-warning/10 px-4 py-3 text-xs text-warning"
              role="status"
              data-testid="generation-envelope-rejections"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p><strong>{screeningAdmissions.rejected.length} catalog row{screeningAdmissions.rejected.length === 1 ? '' : 's'} not admitted:</strong></p>
                <ul className="mt-1 list-disc pl-4">
                  {screeningAdmissions.rejected.map((r) => (
                    <li key={r.docId}>{r.reason}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* PLANNING TECHNOLOGY DEFAULTS — first link of the authority chain.
              These are the per-block performance, water and land figures the
              solver uses UNTIL a vendor envelope (catalog/user) or a project
              dossier supplies them. They were previously shown under the Site
              envelope, where a user could tune them only to have Step 3
              silently overwrite them; here the supersession is disclosed. */}
          <details className="rounded-xl border border-border bg-card" data-testid="generation-planning-defaults" data-authority={planningDefaultsAuthority.kind}>
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
              <span>
                <strong className="block text-xs font-bold uppercase tracking-wider text-text-primary">Planning technology defaults</strong>
                <span className="mt-1 block text-[10px] text-text-muted">Output factors, heat rate, forced outage, and water / land intensities for generic {technologyLabels[input.units[0]?.technology ?? 'combined-cycle-block']?.toLowerCase()}.</span>
              </span>
              <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md ${planningDefaultsAuthority.kind === 'planning' ? 'border-primary/40 bg-primary/5 text-primary' : 'border-border text-text-muted'}`} data-testid="generation-planning-defaults-status">
                {planningDefaultsAuthority.label}
              </span>
            </summary>
            <div className="border-t border-border">
              <p className="bg-background px-4 py-2 text-[9px] text-text-muted" data-testid="generation-planning-defaults-note">{planningDefaultsAuthority.note}</p>
              {projectDutyLockLabels.length > 0 && <p className="border-t border-border bg-primary/[0.03] px-4 py-2 text-[9px] text-text-muted">Dormant screening fields are locked while the project dossier supplies {projectDutyLockLabels.join(', ')}.</p>}
              <div className="border-t border-border p-4">
                <p className="mb-2 text-[9px] font-bold uppercase tracking-wider text-text-muted">Power-block performance</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="ambientFactor" htmlFor="generation-ambient-factor">Site / firm output factor (%)</GenerationFieldLabel><label htmlFor="generation-ambient-factor" className="sr-only">Ambient output factor (%)</label><input id="generation-ambient-factor" type="number" min="0.1" max="100" step="0.1" value={Math.round((input.units[0]?.ambientFactor ?? 0) * 1_000) / 10} onChange={(e) => patchUniformUnits('units.ambientFactor', numberValue(e.target.value) / 100, { ambientFactor: numberValue(e.target.value) / 100 })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /><GenerationScreeningSourceBadge field="units.ambientFactor" assumption={screeningAssumptions.get('units.ambientFactor')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('units.ambientFactor')} /></div>
                  <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="degradationFactor" htmlFor="generation-degradation-factor">Degradation factor (%)</GenerationFieldLabel><input id="generation-degradation-factor" type="number" min="0.1" max="100" step="0.1" value={Math.round((input.units[0]?.degradationFactor ?? 0) * 1_000) / 10} onChange={(e) => patchUniformUnits('units.degradationFactor', numberValue(e.target.value) / 100, { degradationFactor: numberValue(e.target.value) / 100 })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /><GenerationScreeningSourceBadge field="units.degradationFactor" assumption={screeningAssumptions.get('units.degradationFactor')} onOpenDocument={onOpenDocuments} /></div>
                  <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="auxiliaryLoad" htmlFor="generation-auxiliary-load">Auxiliary load / block (MW)</GenerationFieldLabel><input id="generation-auxiliary-load" type="number" min="0" step="0.1" value={input.units[0]?.auxiliaryLoadMWPerUnit ?? 0} onChange={(e) => patchUniformUnits('units.auxiliaryLoadMWPerUnit', numberValue(e.target.value), { auxiliaryLoadMWPerUnit: numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /><GenerationScreeningSourceBadge field="units.auxiliaryLoadMWPerUnit" assumption={screeningAssumptions.get('units.auxiliaryLoadMWPerUnit')} onOpenDocument={onOpenDocuments} /></div>
                  <div className={isGasTechnology ? 'text-[10px] uppercase text-text-muted' : 'hidden'}><label className="contents">HHV heat rate (Btu / kWh)<input type="number" min="1" step="10" value={input.units[0]?.heatRateBtuPerKWhHhv ?? 0} onChange={(e) => patchUniformUnits('units.heatRateBtuPerKWhHhv', numberValue(e.target.value), { heatRateBtuPerKWhHhv: numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label><GenerationScreeningSourceBadge field="units.heatRateBtuPerKWhHhv" assumption={screeningAssumptions.get('units.heatRateBtuPerKWhHhv')} onOpenDocument={onOpenDocuments} /></div>
                  <div className="text-[10px] uppercase text-text-muted"><label className="contents">Forced outage rate (%)<input type="number" min="0" max="100" step="0.1" value={(input.units[0]?.forcedOutageRate ?? 0) * 100} onChange={(e) => patchUniformUnits('units.forcedOutageRate', numberValue(e.target.value) / 100, { forcedOutageRate: numberValue(e.target.value) / 100 })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label><GenerationScreeningSourceBadge field="units.forcedOutageRate" assumption={screeningAssumptions.get('units.forcedOutageRate')} onOpenDocument={onOpenDocuments} /></div>
                </div>
                <p className="mb-2 mt-4 text-[9px] font-bold uppercase tracking-wider text-text-muted">Water and land intensities</p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className={isGridOnly ? 'hidden' : 'text-[10px] uppercase text-text-muted'}><label className="contents">Generation withdrawal (gal / MWh gross)<input type="number" min="0" disabled={projectAbsoluteWater} value={input.water.generationWithdrawalGalPerMWhGross} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('water.generationWithdrawalGalPerMWhGross', value, current => ({ ...current, water: { ...current.water, generationWithdrawalGalPerMWhGross: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label><GenerationScreeningSourceBadge field="water.generationWithdrawalGalPerMWhGross" assumption={screeningAssumptions.get('water.generationWithdrawalGalPerMWhGross')} onOpenDocument={onOpenDocuments} /></div>
                  <div className={isGridOnly ? 'hidden' : 'text-[10px] uppercase text-text-muted'}><label className="contents">Generation consumption (gal / MWh gross)<input type="number" min="0" disabled={projectAbsoluteWater} value={input.water.generationConsumptionGalPerMWhGross} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('water.generationConsumptionGalPerMWhGross', value, current => ({ ...current, water: { ...current.water, generationConsumptionGalPerMWhGross: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label><GenerationScreeningSourceBadge field="water.generationConsumptionGalPerMWhGross" assumption={screeningAssumptions.get('water.generationConsumptionGalPerMWhGross')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('water.generationConsumptionGalPerMWhGross')} /></div>
                  <div className={isGridOnly ? 'hidden' : 'text-[10px] uppercase text-text-muted'}><label className="contents">Plant intensity (acres / gross MW)<input type="number" min="0" step="0.01" disabled={projectAbsolutePlantArea} value={input.footprint.plantAcresPerGrossMW} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('footprint.plantAcresPerGrossMW', value, current => ({ ...current, footprint: { ...current.footprint, plantAcresPerGrossMW: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label><GenerationScreeningSourceBadge field="footprint.plantAcresPerGrossMW" assumption={screeningAssumptions.get('footprint.plantAcresPerGrossMW')} onOpenDocument={onOpenDocuments} onUseLatest={latestEvidenceActionFor('footprint.plantAcresPerGrossMW')} /></div>
                  <div className="text-[10px] uppercase text-text-muted"><label className="contents">Cooling island (acres)<input type="number" min="0" step="0.1" disabled={projectCoolingEnvelope} value={input.footprint.coolingAcres} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('footprint.coolingAcres', value, current => ({ ...current, footprint: { ...current.footprint, coolingAcres: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label><GenerationScreeningSourceBadge field="footprint.coolingAcres" assumption={screeningAssumptions.get('footprint.coolingAcres')} onOpenDocument={onOpenDocuments} /></div>
                  <div className="text-[10px] uppercase text-text-muted"><label className="contents">Switchyard (acres)<input type="number" min="0" step="0.1" disabled={projectSwitchyardEnvelope} value={input.footprint.switchyardAcres} onChange={(e) => { const value = numberValue(e.target.value); setScreeningField('footprint.switchyardAcres', value, current => ({ ...current, footprint: { ...current.footprint, switchyardAcres: value } })); }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary disabled:cursor-not-allowed disabled:opacity-50 rounded-md" /></label><GenerationScreeningSourceBadge field="footprint.switchyardAcres" assumption={screeningAssumptions.get('footprint.switchyardAcres')} onOpenDocument={onOpenDocuments} /></div>
                  {/* OPTIONAL area INTENSITIES. Leave blank to keep the flat
                      figures above verbatim (which is what every existing
                      scenario does, byte-identically). Supply a documented
                      acres/MW and the area scales with site-rated gross MW —
                      this is what stops a max-power sweep from charging a 2 GW
                      fleet the same heat-rejection area as a 200 MW one. */}
                  <div className="text-[10px] uppercase text-text-muted"><label className="contents">Cooling intensity (acres / gross MW, optional)<input type="number" min="0" step="0.001" placeholder="flat figure applies" value={input.footprint.coolingAcresPerGrossMW ?? ''} onChange={(e) => { const raw = e.target.value.trim(); const value = raw === '' ? undefined : numberValue(raw); setScreeningField('footprint.coolingAcresPerGrossMW', value, current => ({ ...current, footprint: { ...current.footprint, ...(value === undefined ? { coolingAcresPerGrossMW: undefined } : { coolingAcresPerGrossMW: value }) } })); }} data-testid="generation-cooling-intensity" className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label></div>
                  <div className="text-[10px] uppercase text-text-muted"><label className="contents">Switchyard intensity (acres / gross MW, optional)<input type="number" min="0" step="0.001" placeholder="flat figure applies" value={input.footprint.switchyardAcresPerGrossMW ?? ''} onChange={(e) => { const raw = e.target.value.trim(); const value = raw === '' ? undefined : numberValue(raw); setScreeningField('footprint.switchyardAcresPerGrossMW', value, current => ({ ...current, footprint: { ...current.footprint, ...(value === undefined ? { switchyardAcresPerGrossMW: undefined } : { switchyardAcresPerGrossMW: value }) } })); }} data-testid="generation-switchyard-intensity" className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary rounded-md" /></label></div>
                </div>
              </div>
            </div>
          </details>

          <div id="generation-project-duty-editor" className="scroll-mt-24">
            {projectDutyDraftDirty && (
              <div className="mb-3 flex items-start gap-2 border border-warning/50 bg-warning/10 px-4 py-3 text-xs text-warning rounded-md" role="status">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p><strong>Project duty edits are not applied yet.</strong> Apply the dossier or restore its applied values before saving the scenario.</p>
              </div>
            )}
            {dossierBindable
              ? (
                <details className="rounded-xl border border-border bg-card" data-testid="generation-project-duty-row" data-bound="true">
                  <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
                    <span>
                      <strong className="block text-xs font-bold uppercase tracking-wider text-text-primary">Project OEM / EPC duty dossier</strong>
                      <span className="mt-1 block text-[10px] text-text-muted">Optional project-specific performance, installed envelopes, utilities, and acoustics bound to the {activeEquipmentSelection?.manufacturer} {activeEquipmentSelection?.model} {activeEquipmentSelection?.kind === 'user' ? 'user-supplied entry (the dossier inherits its unverified standing)' : 'catalog envelope'}.</span>
                    </span>
                    <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${projectDutyDraftDirty ? 'border-warning/50 bg-warning/10 text-warning' : input.projectDutyBasis ? 'border-success/40 bg-success/10 text-success' : 'border-primary/40 bg-primary/5 text-primary'} rounded-md`}>
                      {projectDutyDraftDirty ? 'Unapplied edits' : input.projectDutyBasis ? `Applied · ${input.projectDutyBasis.basisName}` : 'None attached · optional'}
                    </span>
                  </summary>
                  <div className="border-t border-border">
                    <GenerationProjectDutyBoard
                      embedded
                      input={input}
                      designDryBulbC={designDryBulbC}
                      designDryBulbBasis={designDryBulbBasis}
                      documents={documents}
                      documentPrefillEnabled={automaticEvidenceRefreshEnabled}
                      evidenceMode={site.evidenceMode}
                      readOnly={readOnly}
                      actionsDisabled={busy}
                      appliedStatus={projectDutyApplyStatus}
                      onApply={applyProjectDuty}
                      onClear={clearProjectDuty}
                      onOpenDocuments={onOpenDocuments}
                      onDraftDirtyChange={setProjectDutyDraftDirty}
                      scenarioResetKey={selectedId}
                    />
                  </div>
                </details>
              )
              : (
                /* Unbound: a single honest row instead of a locked full-size
                   editor. Says WHY it is locked — including the user-supplied
                   case, where no catalog binding exists to hash the dossier to. */
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-border bg-card px-4 py-3" data-testid="generation-project-duty-row" data-bound="false">
                  <span>
                    <strong className="block text-xs font-bold uppercase tracking-wider text-text-muted">Project OEM / EPC duty dossier</strong>
                    <span className="mt-1 block text-[10px] text-text-muted">
                      Optional project-specific performance, installed envelopes, utilities, and acoustics. Unlocks once an equipment envelope — catalog or user-supplied — is the working basis above.
                    </span>
                  </span>
                  <span className="border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-muted rounded-md">Select equipment first</span>
                </div>
              )}
          </div>

          {/* BOM evidence — procurement provenance, never a solver input
              (`solverBinding: 'none'`). Collapsed to one row so Step 3 reads as
              one primary decision plus two optional attachments. */}
          <details className="rounded-xl border border-border bg-card" data-testid="generation-bom-row">
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset">
              <span>
                <strong className="block text-xs font-bold uppercase tracking-wider text-text-primary">Equipment BOM evidence</strong>
                <span className="mt-1 block text-[10px] text-text-muted">{bomRowSummary.detail}</span>
              </span>
              <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-wider rounded-md ${bomRowSummary.tone}`} data-testid="generation-bom-row-status">{bomRowSummary.label}</span>
            </summary>
            <div className="border-t border-border">
              <GenerationBomBasisBoard
                plan={site.supplyChainPlan}
                basis={input.bomBasis}
                readOnly={readOnly}
                actionsDisabled={busy}
                onLink={bomBasis => setInput(current => ({ ...current, bomBasis }))}
                onClear={() => setInput(current => {
                  const next = { ...current };
                  delete next.bomBasis;
                  return next;
                })}
                onOpenBom={onOpenBom}
                onUseInEquipmentSelection={(bomItemId) => {
                  const item = site.supplyChainPlan?.bom.find(candidate => candidate.id === bomItemId);
                  if (item) routeEquipmentImport(resolveBomItemImport(item, { siteGridFrequencyHz }));
                }}
              />
            </div>
          </details>
          </div>

          <div className={stagePanelClass('capacity')} data-testid="generation-stage-panel-capacity" data-stage-active={activeStage === 'capacity' ? 'true' : 'false'}>
          <section id="generation-stage-capacity" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card" aria-labelledby="generation-stage-capacity-heading" data-testid="generation-stage-capacity">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 4 · Capacity</p>
                <h2 id="generation-stage-capacity-heading" className="mt-1 text-base font-bold text-text-primary">Check supply capacity and resilience</h2>
                <p className="mt-1 max-w-3xl text-xs text-text-muted">{isGridOnly ? 'Compare declared feed capacity with facility demand and reserve under the chosen outages. Feed independence is a modeling assumption until supported by utility evidence.' : 'Size the selected equipment in blocks, then compare modeled capacity with the facility duty and reserve.'}</p>
                <p className="mt-2 text-[10px] text-text-secondary" data-testid="generation-capacity-block-basis" data-authority={planningDefaultsAuthority.kind}>
                  <strong className="uppercase tracking-wider text-text-muted">Block basis</strong>
                  {' — '}
                  {planningDefaultsAuthority.kind === 'dossier'
                    ? `${input.projectDutyBasis?.basisName} project duty over the ${activeEquipmentSelection ? `${activeEquipmentSelection.manufacturer} ${activeEquipmentSelection.model}` : 'catalog'} envelope`
                    : planningDefaultsAuthority.kind === 'vendor'
                      ? `${activeEquipmentSelection?.manufacturer} ${activeEquipmentSelection?.model} ${activeEquipmentSelection?.kind === 'user' ? 'user-supplied' : 'OEM catalog'} envelope`
                      : 'planning technology defaults — no vendor envelope applied'}
                  {' · '}
                  <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => selectStage('equipment')}>Step 3 authority</button>
                </p>
              </div>
              <span className={`border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${contingencyPass && reservePass ? 'border-success/40 bg-success/10 text-success' : 'border-warning/40 bg-warning/10 text-warning'} rounded-md`}>{contingencyPass && reservePass ? `${criterionLabel} duty met` : `${criterionLabel} action required`}</span>
            </div>
            {activeEquipmentSelection && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-primary/[0.03] px-4 py-2" data-testid="generation-equipment-locked-fleet">
                <p className="text-[10px] text-text-secondary">
                  <strong className="uppercase tracking-wider text-primary">Fleet locked to equipment</strong>
                  {' — '}{activeEquipmentSelection.manufacturer} {activeEquipmentSelection.model}
                  {activeEquipmentSelection.kind === 'user' ? ' (user-supplied · unverified)' : ''} fixes the block rating; size by block count below.
                </p>
                <button
                  type="button"
                  aria-label="Clear the equipment basis to edit the fleet manually"
                  disabled={readOnly || busy}
                  onClick={() => rebuildUnits({ count: input.units.length })}
                  className="border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:bg-background disabled:text-text-muted rounded-md"
                >
                  Unlock manual editing
                </button>
              </div>
            )}
            <div className="grid gap-3 p-4 sm:grid-cols-3">
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="technology" htmlFor="generation-technology">Technology</GenerationFieldLabel><select id="generation-technology" value={input.units[0]?.technology} disabled={activeEquipmentSelection != null} onChange={(e) => rebuildUnits({ technology: e.target.value as GenerationTechnology })} className="mt-1 w-full border border-border bg-background p-2 text-xs normal-case text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60 rounded-md">{Object.entries(technologyLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><p className="mt-1 text-[9px] normal-case text-text-muted">{activeEquipmentSelection ? 'Fixed by the applied equipment envelope.' : <>{intentComponents.length > 1 ? <>Hybrid intent: {intentComponents.map((component) => intentComponentLabels[component] ?? component).join(' + ')} · </> : null}{intentTechnology === input.units[0]?.technology ? 'Project Intent match' : intentTechnology ? 'Scenario override' : 'No technology set in Project Intent'}</>}</p></div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="contingencyBlocks" htmlFor="generation-contingency-blocks">{isGridOnly ? 'Grid contingency groups' : 'Contingency blocks'}</GenerationFieldLabel><input id="generation-contingency-blocks" type="number" min={MIN_GENERATION_BLOCK_COUNT} max={MAX_GENERATION_BLOCK_COUNT} value={blockCountDraft ?? input.units.length} onChange={(e) => setBlockCountDraft(e.target.value)} onBlur={() => commitBlockCount()} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitBlockCount(); } }} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary rounded-md" /><p className="mt-1 text-[9px] normal-case text-text-muted">{activeEquipmentSelection ? `Blocks of the applied ${activeEquipmentSelection.model} envelope; each change re-applies the envelope.` : 'Independent physical groups available before the selected outage criterion is applied.'}</p></div>
              <div className="text-[10px] uppercase text-text-muted"><GenerationFieldLabel field="isoGrossPerBlock" htmlFor="generation-iso-gross">{isGridOnly ? 'Declared capacity per feed (MW)' : 'ISO gross per block (MW)'}</GenerationFieldLabel><input id="generation-iso-gross" type="number" min="0" value={input.units[0]?.isoGrossMWPerUnit ?? 0} disabled={activeEquipmentSelection != null} onChange={(e) => rebuildUnits({ gross: numberValue(e.target.value) })} className="mt-1 w-full border border-border bg-background p-2 text-xs text-text-primary outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60 rounded-md" /><p className="mt-1 text-[9px] normal-case text-text-muted">{activeEquipmentSelection ? `Locked to the ${activeEquipmentSelection.model} screening normalization.` : isGridOnly ? 'Declared grid feed rating before the firm-output factor; verify with utility evidence.' : 'Planning ISO-gross rating; an applied Step 3 vendor duty overwrites it.'}</p></div>
            </div>
          </section>

          <section className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 xl:grid-cols-4" aria-label="Capacity dashboard">
            <div className="bg-card p-4"><span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-text-muted">Normal net <GenerationFieldHelp field="normalNet" /></span><strong className="mt-1 block text-2xl">{result.capacity.normalNetMW.toFixed(1)} <small className="text-xs">MW</small></strong></div>
            <div className="bg-card p-4"><span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-text-muted">{criterionLabel} contingency output <GenerationFieldHelp field="largestGroupOut" /></span><strong className="mt-1 block text-2xl">{contingencyNetMW.toFixed(1)} <small className="text-xs">MW</small></strong></div>
            <div className={`p-4 ${contingencyPass ? 'bg-success/10' : 'bg-danger/10'}`}><span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-text-muted">{criterionLabel} load margin <GenerationFieldHelp field="nMinusOneLoadMargin" /></span><strong className="mt-1 flex items-center gap-2 text-2xl">{contingencyPass ? <CheckCircle2 className="h-5 w-5 text-success" /> : <ShieldAlert className="h-5 w-5 text-danger" />}{contingencyMarginMW.toFixed(1)} <small className="text-xs">MW</small></strong></div>
            <div className={`p-4 ${reservePass ? 'bg-success/10' : 'bg-warning/10'}`}><span className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-text-muted">{criterionLabel} + reserve margin <GenerationFieldHelp field="nMinusOneReserveMargin" /></span><strong className="mt-1 flex items-center gap-2 text-2xl">{reservePass ? <CheckCircle2 className="h-5 w-5 text-success" /> : <AlertTriangle className="h-5 w-5 text-warning" />}{contingencyReserveMarginMW.toFixed(1)} <small className="text-xs">MW</small></strong></div>
          </section>

          {activeEquipmentSelection && equipmentBlockSizing?.status === 'available' && (
            <section className="rounded-2xl border border-primary/40 bg-card shadow-card" aria-label="Equipment block-count sizing" data-testid="equipment-block-count-sizing">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
                <div>
                  <h2 className="text-xs font-bold uppercase tracking-wider">Block-count sizing line · {activeEquipmentSelection.model}</h2>
                  <p className="mt-1 text-[10px] text-text-muted">Each count is an exact solver screen of a real {activeEquipmentSelection.manufacturer} {activeEquipmentSelection.model} re-application under the active {criterionLabel} criterion — the block rating never changes.</p>
                </div>
                <button
                  type="button"
                  disabled={readOnly || busy || equipmentBlockSizing.minimumBlocksForReserve == null || equipmentBlockSizing.minimumBlocksForReserve === input.units.length}
                  onClick={() => { if (equipmentBlockSizing.minimumBlocksForReserve != null) void resizeEquipmentFleet(equipmentBlockSizing.minimumBlocksForReserve); }}
                  className="border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary disabled:border-border disabled:text-text-muted rounded-md"
                >
                  {equipmentBlockSizing.minimumBlocksForReserve == null
                    ? 'No screened count clears reserve'
                    : equipmentBlockSizing.minimumBlocksForReserve === input.units.length
                      ? 'Reserve duty met at this count'
                      : `Use ${equipmentBlockSizing.minimumBlocksForReserve} × ${activeEquipmentSelection.model}`}
                </button>
              </div>
              <div className="grid gap-px bg-border sm:grid-cols-3">
                <div className="bg-card px-4 py-3"><span className="text-[9px] uppercase text-text-muted">Entered</span><strong className="mt-1 block text-xl">{input.units.length} <small className="text-[10px]">block{input.units.length === 1 ? '' : 's'}</small></strong></div>
                <div className={equipmentBlockSizing.minimumBlocksForDuty != null && input.units.length >= equipmentBlockSizing.minimumBlocksForDuty ? 'bg-success/10 px-4 py-3' : 'bg-danger/10 px-4 py-3'}><span className="text-[9px] uppercase text-text-muted">Minimum for {criterionLabel} load</span><strong className="mt-1 block text-xl">{equipmentBlockSizing.minimumBlocksForDuty ?? '—'} <small className="text-[10px]">blocks</small></strong></div>
                <div className={equipmentBlockSizing.minimumBlocksForReserve != null && input.units.length >= equipmentBlockSizing.minimumBlocksForReserve ? 'bg-success/10 px-4 py-3' : 'bg-warning/10 px-4 py-3'}><span className="text-[9px] uppercase text-text-muted">Minimum for {criterionLabel} + reserve</span><strong className="mt-1 block text-xl">{equipmentBlockSizing.minimumBlocksForReserve ?? '—'} <small className="text-[10px]">blocks</small></strong></div>
              </div>
            </section>
          )}

          {!activeEquipmentSelection && contingencyOutages === 1 && sizing.status === 'available' && <section className="rounded-2xl border border-primary/40 bg-card shadow-card" aria-label="Uniform block sizing envelope" data-testid="uniform-block-sizing">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider">Largest-block-out sizing line</h2>
                <p className="mt-1 text-[10px] text-text-muted">{sizing.blockCount} identical blocks → {sizing.onlineBlocksAfterContingency} online after the design contingency · {Math.round(sizing.netDeliveryFactor * 1000) / 10}% site-net delivery per ISO MW</p>
              </div>
              <button type="button" disabled={readOnly || sizing.configuredMeetsReserve} onClick={() => rebuildUnits({ gross: sizing.suggestedReserveGrossMWPerBlock })} className="border border-primary px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-primary disabled:border-border disabled:text-text-muted rounded-md">
                {sizing.configuredMeetsReserve ? 'Reserve duty met' : `Use ${sizing.suggestedReserveGrossMWPerBlock.toFixed(0)} MW / block`}
              </button>
            </div>
            <div className="grid gap-px bg-border sm:grid-cols-3">
              <div className="bg-card px-4 py-3"><span className="text-[9px] uppercase text-text-muted">Entered</span><strong className="mt-1 block text-xl">{sizing.configuredGrossMWPerBlock.toFixed(0)} <small className="text-[10px]">ISO MW / block</small></strong></div>
              <div className={sizing.configuredMeetsLoad ? 'bg-success/10 px-4 py-3' : 'bg-danger/10 px-4 py-3'}><span className="text-[9px] uppercase text-text-muted">Minimum for N-1 load</span><strong className="mt-1 block text-xl">{sizing.suggestedLoadGrossMWPerBlock.toFixed(0)} <small className="text-[10px]">MW / block</small></strong><span className="text-[9px] text-text-muted">Exact screen {sizing.requiredLoadGrossMWPerBlock.toFixed(1)} MW</span></div>
              <div className={sizing.configuredMeetsReserve ? 'bg-success/10 px-4 py-3' : 'bg-warning/10 px-4 py-3'}><span className="text-[9px] uppercase text-text-muted">Minimum for N-1 + reserve</span><strong className="mt-1 block text-xl">{sizing.suggestedReserveGrossMWPerBlock.toFixed(0)} <small className="text-[10px]">MW / block</small></strong><span className="text-[9px] text-text-muted">Exact screen {sizing.requiredReserveGrossMWPerBlock.toFixed(1)} MW · rounded to {sizing.incrementMW} MW</span></div>
            </div>
          </section>}

          {!isGridOnly && !activeEquipmentSelection && contingencyOutages === 1 && sizing.status === 'unavailable' && <section className="border border-warning/50 bg-warning/5 px-4 py-3 rounded-md" aria-label="Uniform block sizing unavailable" data-testid="uniform-block-sizing-unavailable">
            <h2 className="text-xs font-bold uppercase tracking-wider">Largest-block-out sizing unavailable</h2>
            <p className="mt-1 text-[10px] text-text-secondary">{sizing.reason}</p>
          </section>}

          {!activeEquipmentSelection && contingencyOutages === 1 && granularity.status === 'available' && granularityRows.length > 0 && <section className="rounded-2xl border border-primary/40 bg-card shadow-card" aria-label="Contingency granularity sweep" data-testid="contingency-granularity">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-xs font-bold uppercase tracking-wider">Contingency granularity</h2>
              <p className="mt-1 text-[10px] text-text-muted">N-1 overbuild scales with the largest contingency, not the load. Holding the {granularity.basisRequirementMW.toFixed(0)} MW {granularity.basis === 'reserve' ? 'reserve-padded' : 'critical-load'} duty, a fleet of n blocks installs about n / (n − 1) times the delivered requirement.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] border-collapse text-[10px]">
                <thead>
                  <tr className="border-b border-border text-left text-[9px] uppercase text-text-muted">
                    <th scope="col" className="px-4 py-2 font-medium">Blocks</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">ISO MW / block</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">Installed nameplate</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">vs 2 blocks</th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">At {granularity.incrementMW} MW steps</th>
                  </tr>
                </thead>
                <tbody>
                  {granularityRows.map((row) => (
                    <tr key={row.blockCount} className={row.isConfiguredBlockCount ? 'border-b border-border bg-primary/10' : 'border-b border-border'} data-testid={`contingency-granularity-row-${row.blockCount}`}>
                      <th scope="row" className="px-4 py-2 text-left font-normal">
                        {row.blockCount}
                        {row.isConfiguredBlockCount && <span className="type-meta ml-2 border border-primary/40 bg-primary/5 px-1 py-0.5 font-bold uppercase tracking-wider text-primary rounded-md">Current</span>}
                      </th>
                      <td className="px-4 py-2 text-right tabular-nums">{row.requiredGrossMWPerBlock.toFixed(1)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.requiredInstalledGrossMW.toFixed(0)} MW</td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-muted">{(row.requiredInstalledGrossMW / (granularityRows[0]?.requiredInstalledGrossMW || row.requiredInstalledGrossMW) * 100).toFixed(0)}%</td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-muted">{row.installedGrossMW.toFixed(0)} MW{row.incrementRoundingPenaltyMW > 0 && <span className="ml-1 text-warning">+{row.incrementRoundingPenaltyMW.toFixed(0)}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="border-t border-border px-4 py-2 text-[9px] text-text-muted">Installed nameplate is the exact N-1 requirement. The final column rounds every block up to the {granularity.incrementMW} MW procurement increment, so the round-up scales with block count and can outrun the granularity saving once block size approaches the increment. Screening-level: this assumes identical blocks on independent contingency groups, and does not price the switchgear, transformers or collector system that buying finer granularity requires.</p>
          </section>}

          {!activeEquipmentSelection && contingencyOutages === 1 && granularity.status === 'unavailable' && sizing.status === 'available' && <section className="border border-warning/50 bg-warning/5 px-4 py-3 rounded-md" aria-label="Contingency granularity unavailable" data-testid="contingency-granularity-unavailable">
            <h2 className="text-xs font-bold uppercase tracking-wider">Contingency granularity unavailable</h2>
            <p className="mt-1 text-[10px] text-text-secondary">{granularity.reason}</p>
          </section>}

          {contingencyOutages !== 1 && !activeEquipmentSelection && (
            <section className="border border-warning/50 bg-warning/5 px-4 py-3 rounded-md" role="note">
              <h2 className="text-xs font-bold uppercase tracking-wider text-warning">N-1 planning tool paused</h2>
              <p className="mt-1 text-[10px] text-text-secondary">Uniform sizing, vendor comparison margins, and generated alternatives are N-1 reference screens. Your active {criterionLabel} result remains authoritative in the dashboard above; switch to N-1 to apply a generated alternative.</p>
            </section>
          )}

          {/* MAX POWER — the upward mirror of the sizing screens above. It honours
              the scenario's own N-k criterion (it reads contingencyNetMW), so it
              is deliberately NOT gated by the N-1 pause banner above it. */}
          {isGridOnly ? <section className="rounded-lg border border-border bg-card p-4" aria-label="Grid supply capacity basis"><h2 className="text-xs font-bold">Grid supply capacity</h2><p className="mt-2 text-xs leading-relaxed text-text-secondary">{GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE}</p><p className="mt-2 text-xs text-text-muted">The margins above screen the feed capacities and independence you declared. They do not confirm utility commitments or grid reliability.</p></section> : <GenerationMaxPowerPanel
            input={input}
            parcels={layoutParcels(site)}
            layout={input.layout}
            fallbackAnchor={input.layoutAnchor ?? site.siteCentroid ?? site.location}
            fallbackEnvelopeAcres={parcelAreaAcres ?? undefined}
            pue={intentPowerContext.pue}
            pueSource={intentPowerContext.pueSource}
            powerFactor={intentPowerContext.powerFactor}
            onStudyResolved={setMaxPowerStudy}
            onStampToIntent={
              !readOnly && selectedBase != null && promotedScenarioId === selectedBase.id
                ? stampMaxPower
                : undefined
            }
            stampDisabledReason={
              readOnly ? 'This site is read-only.'
                : selectedBase == null ? 'Save the scenario first.'
                  : promotedScenarioId !== selectedBase.id
                    ? 'Promote this scenario to Project Intent before stamping a max-power figure.'
                    : undefined
            }
            stampTargetLabel={(() => {
              const v = effectivePhases(site.projectIntent);
              if (!v.isPhased) return undefined;
              const last = v.phases[v.phases.length - 1];
              return `On this phased site the figure also becomes the final phase's ("${last.label || `Phase ${last.sequence}`}") cumulative capacity.`;
            })()}
          />}

          {!isGridOnly && <GenerationAlternativesMatrix
            matrix={alternatives}
            readOnly={readOnly}
            actionsDisabled={busy || (contingencyOutages !== 1 && !activeEquipmentSelection)}
            appliedStatus={alternativeApplyStatus}
            parcelFits={alternativeParcelFits}
            onApply={applyAlternative}
          />}

          {/* Post-fleet verdicts confront the user where the fleet is decided;
              red resource risks still deep-link back to the Site envelope. */}
          {capacityScreen.screenVerdict !== 'pass' && (
            <section
              data-testid="generation-capacity-screen-caution"
              className={`border p-3 rounded-lg ${capacityScreen.screenVerdict === 'fail' ? 'border-danger/50 bg-danger/5' : 'border-warning/50 bg-warning/5'}`}
            >
              <p className={`text-xs font-bold ${capacityScreen.screenVerdict === 'fail' ? 'text-danger' : 'text-warning'}`}>{capacityScreen.statement}</p>
              <p className="mt-1 text-[10px] text-text-muted">{deriveReliabilityPerspective({ probabilityLoadServed: result.reliability.probabilityLoadServed }).note}</p>
            </section>
          )}
          {(result.gaps.length > 0 || result.warnings.length > 0) && <section className="border border-warning/50 bg-warning/5 p-4 rounded-lg"><h2 className="text-xs font-bold uppercase tracking-wider">Assumptions &amp; gaps</h2><ul className="mt-2 space-y-1 text-xs text-text-secondary">{[...result.gaps, ...result.warnings].map((item) => <li key={item}>— {item}</li>)}</ul></section>}

          {result.risks.length > 0 && <section className="border border-border bg-card p-4 rounded-lg"><h2 className="text-xs font-bold uppercase tracking-wider">Deterministic risk register</h2><div className="mt-3 grid gap-2 md:grid-cols-2">{result.risks.map((risk) => <div key={risk.id} className={`border-l-2 p-3 text-xs ${risk.severity === 'red' ? 'border-danger bg-danger/5' : 'border-warning bg-warning/5'}`}><p className="font-bold uppercase">{riskTitle(risk.id, criterionLabel)}</p><p className="mt-1 text-text-secondary">{risk.message}</p></div>)}</div></section>}
          </div>

          <div className={stagePanelClass('parcel')} data-testid="generation-stage-panel-parcel" data-stage-active={activeStage === 'parcel' ? 'true' : 'false'}>
          <section id="generation-stage-parcel" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card" aria-label="Parcel test-fit" data-testid="parcel-test-fit">
            {/* Verdict is owned by the studio (canvas badge + status-bar strip);
                the header stays a plain title to avoid a fourth duplicate. */}
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 5 · Parcel fit</p>
                <h2 className="mt-1 flex items-center gap-2 text-base font-bold text-text-primary"><LayoutGrid className="h-4 w-4 text-primary" /> Place the solution and verify containment</h2>
                <p className="mt-1 max-w-4xl text-xs text-text-muted">Project L × W reservations are used where supplied; remaining equipment uses acreage-closing screening rectangles. Gas/CO₂ corridors and construction laydown remain separate lifecycle areas.</p>
                <p className="mt-2 text-[10px] text-text-secondary" data-testid="generation-parcel-footprint-basis" data-basis={projectInstalledReservations ? 'project-installed-dimensions' : 'planning-acreage'}>
                  <strong className="uppercase tracking-wider text-text-muted">Footprint basis</strong>
                  {' — '}
                  {projectInstalledReservations
                    ? `project installed L × W reservations from ${input.projectDutyBasis?.basisName} (${projectInstalledReservations.blockQuantity} generation block${projectInstalledReservations.blockQuantity === 1 ? '' : 's'})`
                    : `screening intensities (${result.footprint.plantAcres.toFixed(1)} ac plant · ${result.footprint.coolingAcres.toFixed(1)} ac cooling · ${result.footprint.switchyardAcres.toFixed(1)} ac switchyard) from the ${planningDefaultsAuthority.kind === 'vendor' ? `${activeEquipmentSelection?.model} envelope` : 'planning technology defaults'}`}
                  {' · '}
                  <button type="button" className="text-primary underline-offset-2 hover:underline" onClick={() => selectStage('equipment')}>{projectInstalledReservations ? 'Open dossier' : 'Edit intensities'}</button>
                </p>
              </div>
            </div>
            {fullStudio && parcelStudioV2 ? (
              <div
                className="flex flex-col items-center justify-center gap-3 p-10 text-center"
                data-testid="parcel-test-fit-fullscreen-active"
              >
                <Maximize2 className="h-6 w-6 text-primary" aria-hidden />
                <p className="text-xs text-text-muted">The Parcel Test-Fit Studio is open in full screen.</p>
                <button
                  type="button"
                  onClick={onExitFullStudio}
                  className="inline-flex items-center gap-1.5 border border-border bg-background px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary rounded-md"
                >
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden /> Return to workbench
                </button>
              </div>
            ) : (
              <div
                className="h-[clamp(32rem,70dvh,48rem)] min-h-0 overflow-hidden"
                data-testid="parcel-test-fit-embedded-viewport"
              >
                {testFitStudioElement}
              </div>
            )}
          </section>

          </div>

          <div className={stagePanelClass('handoff')} data-testid="generation-stage-panel-handoff" data-stage-active={activeStage === 'handoff' ? 'true' : 'false'}>
          <section id="generation-stage-handoff" className="scroll-mt-24 rounded-2xl border border-border bg-card shadow-card px-4 py-3" aria-labelledby="generation-stage-handoff-heading" data-testid="generation-stage-handoff">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">Step 6 · Review &amp; handoff</p>
            <h2 id="generation-stage-handoff-heading" className="mt-1 text-base font-bold text-text-primary">Freeze one decision and hand it downstream safely</h2>
            <p className="mt-1 max-w-4xl text-xs text-text-muted">Compare saved candidates, then follow the enforced authority chain: Save scenario → Promote to Project Intent → Publish layout → rebuild SED → Generate Test Fit.</p>
          </section>

          {compareIds.length >= 2 && (
            <section className="min-w-0 max-w-full border border-border bg-card p-4 rounded-lg" data-testid="generation-scenario-comparison">
              <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-xs font-bold uppercase tracking-wider">Saved scenario comparison ({compareIds.length}/4)</h2>
                <button
                  type="button"
                  onClick={() => setScenarioPickerOpen(true)}
                  className="text-[9px] font-bold uppercase tracking-wider text-primary underline-offset-2 hover:underline"
                >
                  Change selection
                </button>
              </div>
              <p className="mb-3 text-[10px] text-text-muted">
                Every row is that scenario&rsquo;s own saved solver result, re-fitted against the CURRENT parcel layout — so &ldquo;Parcel fit&rdquo; compares candidates on one geometry, not on whatever layout each was saved with.
              </p>
              <div aria-label="Saved generation scenario comparison; scroll horizontally for all columns" className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain" data-testid="generation-scenario-comparison-scroll" role="region" tabIndex={0}>
                <table className="w-full min-w-[900px] text-left text-xs">
                  <thead>
                    <tr className="border-b border-border text-[9px] uppercase text-text-muted">
                      <th className="p-2">Scenario</th>
                      <th className="p-2">Equipment basis</th>
                      <th className="p-2">Criterion net</th>
                      <th className="p-2">Load margin</th>
                      <th className="p-2">Reserve margin</th>
                      <th className="p-2">Parcel fit</th>
                      <th className="p-2">Gas</th>
                      <th className="p-2">Peak modeled component water consumption</th>
                      <th className="p-2">Footprint</th>
                      <th className="p-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {comparedScenarios.map(({ row, capacity, criterionLabel: rowCriterion, equipmentLabel, parcelFit }) => (
                      <tr key={row.id} className="border-b border-border/60" data-testid={`generation-scenario-comparison-row-${row.id}`}>
                        <td className="p-2 font-bold">
                          {row.name}
                          {promotedScenarioId === row.id && <span className="ml-2 border border-primary/40 bg-primary/5 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary rounded-sm">Promoted</span>}
                        </td>
                        <td className="p-2">{equipmentLabel}</td>
                        <td className="p-2">{rowCriterion} · {capacity.netMW.toFixed(1)} MW</td>
                        <td className="p-2">{capacity.marginMW.toFixed(1)} MW</td>
                        <td className="p-2">{capacity.reserveMarginMW.toFixed(1)} MW</td>
                        <td className={`p-2 font-bold ${parcelFit == null ? 'text-text-muted' : parcelFit.status === 'fits' ? 'text-success' : parcelFit.status === 'partial' ? 'text-warning' : 'text-text-muted'}`} data-fit-status={parcelFit?.status ?? 'unavailable'}>
                          {parcelFit == null
                            ? 'No parcel'
                            : parcelFit.status === 'fits'
                              ? 'Contained'
                              : parcelFit.status === 'partial'
                                ? `${parcelFit.blocksContained}/${parcelFit.blocksPlaced} blocks${parcelFit.containmentPct == null ? '' : ` · ${parcelFit.containmentPct.toFixed(0)}%`}`
                                : 'Indeterminate'}
                        </td>
                        <td className="p-2">{row.result.gas.peakMmbtuPerHour.toFixed(0)} MMBtu/h</td>
                        <td className="p-2">{(row.result.water.totalConsumptionGalPerDay ?? row.result.water.generationConsumptionGalPerDay + row.result.water.ccsConsumptionGalPerDay).toFixed(0)} gal/d{row.result.water.totalConsumptionGalPerDay == null ? ' (generation + CCS)' : ''}</td>
                        <td className="p-2">{row.result.footprint.totalAcres.toFixed(1)} ac</td>
                        <td className="p-2 text-right">
                          {selectedId === row.id
                            ? <span className="text-[9px] uppercase tracking-wider text-text-muted">Loaded</span>
                            : (
                              <button
                                type="button"
                                disabled={readOnly || busy}
                                onClick={() => void loadSavedScenario(row)}
                                className="border border-border px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary disabled:text-text-muted rounded-md"
                                data-testid={`generation-scenario-comparison-load-${row.id}`}
                              >
                                Load
                              </button>
                            )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <GenerationImpactTestFitWorkflow
            readiness={testFitReadiness}
            readOnly={readOnly || Boolean(selectedIntegrityFailed)}
            busy={busy}
            saving={operation === 'saving'}
            promoting={operation === 'promoting'}
            publishing={operation === 'publishing'}
            onSave={() => void save()}
            onPromote={() => void actOnScenario('promoteGenerationImpactScenario')}
            onReviewPublish={() => setLayoutReviewOpen(true)}
            publishBlockReason={publishBlockReason}
            publishBlockRemedy={publishBlockRemedy}
            onRebuildSed={() => void requestSedRebuild()}
            onGenerateTestFit={onGenerateTestFit}
          />

          {selectedIntegrityFailed && (
            <div className="flex flex-wrap justify-end gap-2" aria-label="Unavailable actions for corrupt scenario">
              <button type="button" disabled className="border border-border px-3 py-2 text-[10px] font-bold uppercase opacity-40 rounded-md">Publish layout</button>
              <button type="button" disabled className="border border-primary px-3 py-2 text-[10px] font-bold uppercase text-primary opacity-40 rounded-md">Promote to Project Intent</button>
            </div>
          )}

          <GenerationImpactLayoutPublishing
            workspace={layoutWorkspace}
            workspaceLoaded={layoutWorkspaceLoaded}
            scenario={selectedBase}
            parcels={parcels}
            publishBlockReason={publishBlockReason}
            remoteScenarioChanged={selectedRemoteChanged}
            readOnly={readOnly}
            reviewOpen={layoutReviewOpen}
            publishing={operation === 'publishing'}
            conflictMessage={layoutConflict}
            publishError={layoutPublishError}
            onReviewOpenChange={setLayoutReviewOpen}
            onPublish={() => void publishLayout()}
            onExport={() => downloadSiteLayersGeoJson(
              layoutView.activeLayers,
              `${(layoutView.active?.scenarioName ?? 'generation-impact').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-layout-v${layoutView.active?.version ?? 1}.geojson`,
            )}
            onExportKml={() => downloadSiteLayersKml(
              layoutView.activeLayers,
              `${(layoutView.active?.scenarioName ?? 'generation-impact').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-layout-v${layoutView.active?.version ?? 1}.kml`,
            )}
            onRefresh={() => void loadScenarios()}
            onViewOnSiteMap={onOpenSiteMap}
          />
          </div>

          {/* Stage pager — sequential Back/Next through the six panels. */}
          <div className="flex items-center justify-between gap-3 border-t-2 border-border pt-4" data-testid="generation-stage-pager">
            {previousStage ? (
              <button
                type="button"
                onClick={() => selectStage(previousStage)}
                className="inline-flex items-center gap-2 border border-border px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md"
                data-testid="generation-stage-back"
              >
                ← {stageLabel(previousStage)}
              </button>
            ) : <span aria-hidden />}
            {nextStage ? (
              <button
                type="button"
                onClick={() => selectStage(nextStage)}
                className="inline-flex items-center gap-2 border border-primary px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-md"
                data-testid="generation-stage-next"
              >
                {stageLabel(nextStage)} →
              </button>
            ) : <span aria-hidden />}
          </div>
          </div>
        </main>

        {/* Fixed-overlay dialogs live OUTSIDE the CSS-hidden stage panels so
            they open from any stage (bridge banner, boards, next actions). */}
        <GenerationAddEquipmentDialog
          key={importNonce}
          open={addEquipmentOpen}
          prefill={importPrefill ?? undefined}
          onClose={closeAddEquipment}
          onAdd={addUserEquipment}
        />
        <GenerationEquipmentImportDialog
          key={`import-${importTab}-${importOpen ? 'open' : 'closed'}`}
          open={importOpen}
          input={input}
          criterionLabel={criterionLabel}
          parcelAcres={parcelAreaAcres ?? null}
          equipmentRows={equipmentRows}
          initialSource={importTab}
          plan={site.supplyChainPlan}
          companyId={site.companyId}
          shortlistedCatalogIds={input.equipmentWorkspace?.shortlistedCatalogIds ?? []}
          appliedCatalogId={input.equipmentBasis?.catalogId}
          userEquipment={input.equipmentWorkspace?.userEquipment ?? []}
          siteGridFrequencyHz={siteGridFrequencyHz}
          onClose={() => setImportOpen(false)}
          onShortlistCatalog={handleImportShortlist}
          onPrefill={handleImportPrefill}
          onOpenBlank={() => {
            setImportOpen(false);
            setImportPrefill(null);
            setImportNonce(nonce => nonce + 1);
            setAddEquipmentOpen(true);
          }}
          {...(onOpenBom ? { onOpenBom } : {})}
        />
    </div>
  );
}
